/**
 * TaskViewModal Component
 * Modal/drawer for viewing task details with tabs
 * Handles comments, worklog, history, and subtasks
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Drawer,
  Dialog,
  Popover,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Button,
  Menu,
  MenuItem,
  Tooltip,
  TextField,
  FormControl,
  Select,
  Autocomplete,
  Avatar,
  Divider,
  CircularProgress,
  ListItemIcon,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Collapse,
  InputAdornment
} from '@mui/material';
import CommentBox, { trimEdgeHtml } from './CommentBox';
import WorkLog from './WorkLog';
import TaskHistory from './History';

import EditTaskModal from './EditTaskModal'; // eslint-disable-line no-unused-vars
import AddTaskModal from './AddTaskModal';
import CommonDeleteConfirmDialog from '../shared/CommonDeleteConfirmDialog';
import CustomColorPopover from '../shared/CustomColorPopover';
import NestedSubtaskDrawer from './NestedSubtaskDrawer';
import ChecklistManager from './ChecklistManager';
import { getStatusIcon, getTaskTypeIcon } from '../../shared/iconUtils';
import { ICONS } from '../../shared/icons';
import TaskCompletionCheckbox from './TaskCompletionCheckbox';
import SubtaskProgressBar from './SubtaskProgressBar';
import LinkTicketModal from './LinkTicketModal';
import AITaskPlanner from './AITaskPlanner';
import DropdownSelector from './DropdownSelector'; // eslint-disable-line no-unused-vars
import ProjectStatusModal from './ProjectStatusModal';
import { useDispatch, useSelector } from 'react-redux';
import { createTicketComment, fetchTicketComments, updateComment, deleteComment, createWorklog, updateWorklog, deleteWorklog, fetchWorklogSummary, fetchTicketHistory, fetchTicketDetails, createTicket, updateTicket, updateTicketPartial, updateTicketAttachments, deleteTicket, fetchTicketsByProject, updateTicketAssignee, updateTicketStatus, updateTicketTitle, fetchTicketDropdowns, fetchProjectDropdownsByUser, linkTickets, updateLinkTickets, deleteLinkTickets, searchTickets, markSubtaskUpdated, createTicketLabel, createTicketSprint, createTicketType, createTicketPriority, setProjectDropdownStatus, updateTicketDropdown, deleteTicketDropdown } from '../../store/ticketSlice';
import { isStorageQuotaExceededError, uploadFile } from '../../store/fileSlice';
import { updateCalendarTicket } from '../../store/calendarSlice';
import { updateProject } from '../../store/projectSlice';
import { api } from '../../services';
import { ENDPOINTS } from '../../services/endpoints'; // eslint-disable-line no-unused-vars
import { COLORS } from '../../shared/colors';
import SafeCommentRenderer from '../../shared/SafeCommentRenderer';
import { decodeHtmlContent, normalizeRichTextHtml, renderHtmlContentWithMentions, renderCommentContent } from '../../shared/htmlUtils';
import { handleCopyTaskLink, handleCopyCommentLink, formatTaskCode, getTaskTicketNumber } from '../../shared/taskUtils';
import { minutesToTimeString, timeStringToMinutes, isValidTimeFormat, validateTimeDigitLimits, buildDateTimeString, getLocalDatePart, getLocalTimePart } from '../../shared/timeUtils';
import { 
  findCommentById, 
  updateCommentInTree, 
  removeCommentFromTree, 
  insertReplyIntoTree, 
  getReplyAuthors as getReplyAuthorsUtil, 
  isCommentOwnedByUser as isCommentOwnedByUserUtil, 
  getCurrentUserDisplay as getCurrentUserDisplayUtil, 
  mapApiComment as mapApiCommentUtil,
  getReplyCountLabel as getReplyCountLabelUtil,
  isReplyThreadOpen as isReplyThreadOpenUtil,
  toggleReplyThread as toggleReplyThreadUtil
} from '../../shared/commentUtils';
import { taskTitleFieldStyles, taskSelectMenuProps, checklistStyles } from '../../shared/globalSX';
import { FILTER_VALUES, PROJECT_AI_SETTING_STATUS, CHARACTER_LIMITS, COMMENT_REPLY, PROJECT_STATUS, USER_ROLES, TOAST_MESSAGES, PROJECT_TYPES, RESTRICTED_NUMBER_KEYS, TIME_FORMATS } from '../../shared/appConstants';
import { REGEX } from '../../shared/regex';
import AssigneeSelector from '../../shared/AssigneeSelector';
import OverflowTooltip from '../../shared/OverflowTooltip';
import { clearDraftFields, clearDraft, clearOtherCommentDrafts, findCommentPathById, getActiveCommentDraft, getCommentEditDraftFieldName, getCommentReplyDraftFieldName, isCommentDraftField, loadDraft } from '../../shared/autoSaveUtils';
import { getEntityId, normalizeDateValue, stableStringify } from '../../shared/formNormalizationUtils';
import { buildLimitedPasteValue, clampTextToLimit, stringToColor, getPriorityColor } from '../../shared/commonFunctions';
import { mergeChecklistChange } from '../../shared/checklistMergeUtils';
import './TaskModal.scss';
import '../../shared/globalStyles.scss';
import DatePickerModal from '../../shared/DatePickerModal';

import AssigneeAvatarStack from '../../shared/AssigneeAvatarStack';
import RecurrencePanel from './RecurrencePanel';
import { isRecurrenceComplete, normalizeRecurrenceConfig } from '../../shared/recurrenceUtils';


import {
  Close,
  Edit,
  Description,
  Comment,
  Add,
  Delete,
  Check,
  Clear,
  Link,
  SmartToy,
  ContentCopy,
  ChevronRight,
  Timeline,
  PersonOutline,
  DirectionsRun,
  FlagOutlined,
  LocalOfferOutlined,
  AccessTimeOutlined,
  Download,
  GridView,
  ViewList,
  MoreHoriz,
  HubOutlined,
  EventNoteOutlined,
  Reply,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';


const getAttachmentUrl = (attachment) => {
  if (!attachment) return '';
  if (typeof attachment === 'string') return attachment;
  return attachment.url || attachment.access_url || attachment.download_url || attachment.file_url || '';
};

const getAttachmentName = (attachment, fallback = 'attachment') => {
  if (!attachment) return fallback;
  if (typeof attachment === 'string') return attachment.split('/').pop()?.split('?')[0] || fallback;
  // Attachments can come from ticket APIs, FMS uploads, or legacy string URLs.
  const name = attachment.name || attachment.original_file_name || attachment.file_name || attachment.key || fallback;
  return String(name).split('/').pop() || fallback;
};

const triggerAttachmentDownload = async (attachment, { notify = toast, t: translate } = {}) => {
  const url = getAttachmentUrl(attachment);
  if (!url) {
    notify.error(translate?.('taskViewModal.downloadUrlMissing', 'Download URL is not available for this attachment') || 'Download URL is not available for this attachment');
    return;
  }

  const fileName = getAttachmentName(attachment);
  let objectUrl = '';

  try {
    // Blob download keeps cross-origin signed URLs from opening in a new tab.
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Download failed with status ${response.status}`);
    }

    const blob = await response.blob();
    objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    // If storage CORS blocks fetching the file, fall back to direct browser access.
    window.open(url, '_blank', 'noopener,noreferrer');
    notify.info(translate?.('taskViewModal.downloadOpenedInNewTab', 'Download opened in a new tab') || 'Download opened in a new tab');
  } finally {
    if (objectUrl) {
      setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    }
  }
};


// Helper function to fetch ticket details and format for editing
export const fetchAndFormatTicketForEdit = async (dispatch, ticketId) => {
  try {
    const detailsResult = await dispatch(fetchTicketDetails(ticketId)).unwrap();
    
    // Extract sprint ID if sprint is an object, otherwise use the value directly
    const sprintId = detailsResult.sprint 
      ? (typeof detailsResult.sprint === 'object' ? detailsResult.sprint.id : detailsResult.sprint)
      : null;
    
    return {
      id: detailsResult.id,
      title: detailsResult.title,
      description: detailsResult.description,
      project: detailsResult.project?.id,
      priority: detailsResult.priority?.id,
      status: detailsResult.status?.id,
      taskType: detailsResult.type?.id,
      assignees: (detailsResult.users || []).map(u => u.id),
      assignee: detailsResult.users?.[0]?.id,
      startDate: getLocalDatePart(detailsResult.start_date) || null,
      dueDate: getLocalDatePart(detailsResult.end_date) || null,
      // A configured midnight due time must round-trip as 00:00 instead of being treated as empty.
      dueTime: detailsResult.has_due_time ? getLocalTimePart(detailsResult.end_date, { includeMidnight: true }) : '',
      has_due_time: Boolean(detailsResult.has_due_time),
      attachments: detailsResult.attachments || [],
      labels: detailsResult.labels || [],
      checklist: detailsResult.checklist || [],
      estimate_time_in_minutes: detailsResult.estimate_time_in_minutes || 0,
      sprint: sprintId || '',
      sprint_id: sprintId || '',
      acceptanceCriteria: detailsResult.acceptance_criteria || '',
      acceptance_criteria: detailsResult.acceptance_criteria || '',
      is_recurring: Boolean(detailsResult.is_recurring),
      recurrence_config: detailsResult.is_recurring ? normalizeRecurrenceConfig(detailsResult.recurrence_config) : null,
      next_rep_on: detailsResult.next_rep_on || null
    };
  } catch (error) {
    console.error('Failed to fetch ticket details for editing:', error);
    throw error;
  }
};

// Style constants for expandable sections
const COLLAPSED_HEADER_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  p: 1.25,
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  border: '1px solid transparent',
  '&:hover': {
    bgcolor: COLORS.gray[50],
    borderColor: COLORS.gray[100],
    transform: 'translateX(4px)'
  }
};

const ATTACHMENT_MENU_BUTTON_SX = {
  bgcolor: COLORS.white,
  color: COLORS.gray[800],
  border: `1px solid ${COLORS.gray[300]}`,
  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.22)',
  '&:hover': {
    bgcolor: COLORS.gray[100],
    color: COLORS.gray[900],
    borderColor: COLORS.gray[400]
  },
  '&:focus-visible': {
    outline: `2px solid ${COLORS.brand.accent}`,
    outlineOffset: 2
  }
};

const EXPANDED_HEADER_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  bgcolor: COLORS.subtask.headerBg,
  mx: -0.25,
  px: 1.5,
  py: 1,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  border: `1px solid ${COLORS.gray[200]}`,
  borderRadius: '8px',
  zIndex: 1
};

const getChecklistItemCount = (rawChecklist) => {
  if (!rawChecklist) return 0;

  let checklistEntries = [];
  if (rawChecklist && !Array.isArray(rawChecklist) && Array.isArray(rawChecklist.checklists)) {
    checklistEntries = rawChecklist.checklists;
  } else if (Array.isArray(rawChecklist) && rawChecklist.length > 0 && Array.isArray(rawChecklist[0]?.checklists)) {
    checklistEntries = rawChecklist[0].checklists;
  } else if (Array.isArray(rawChecklist)) {
    checklistEntries = rawChecklist;
  }

  if (!Array.isArray(checklistEntries) || checklistEntries.length === 0) return 0;

  const hasNestedEntries = checklistEntries.some((entry) => Array.isArray(entry?.items));
  if (hasNestedEntries) {
    return checklistEntries.reduce((count, entry) => count + (Array.isArray(entry?.items) ? entry.items.length : 0), 0);
  }

  return checklistEntries.length;
};

const FOCUSED_COMMENT_SCROLL_OFFSET = 24;

const TaskViewModal = ({ open, onClose, onEdit, taskData, onViewTask, fullScreen = false, dashboardTasks = [], onTaskUpdate, initialEditMode = false, focusedCommentId = '' }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { comments, commentsLoading, worklogSummary, worklogSummaryLoading, ticketHistory, ticketHistoryLoading, ticketDetails, ticketDetailsLoading, filters, dropdowns, projectDropdownsByUser } = useSelector(state => state.tickets);
  const { user } = useSelector(state => state.auth);
  const canManageTicketAttributes = user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.PM;
  const loadedTicketId = React.useRef(null);
  const worklogFetched = React.useRef(false);
  const historyFetched = React.useRef(false);
  const dropdownsFetched = React.useRef(false);
  const newCommentBoxRef = React.useRef(null);
  const scrollContainerRef = React.useRef(null);
  const [scrollContainer, setScrollContainer] = useState(null);
  const handleScrollContainerRef = React.useCallback((node) => {
    scrollContainerRef.current = node;
    setScrollContainer(node);
  }, []);
  const [showFloatingRail, setShowFloatingRail] = useState(true);
  const [isRailExpanded, setIsRailExpanded] = useState(false);
  const isRailExpandedRef = useRef(false);

  useEffect(() => {
    isRailExpandedRef.current = isRailExpanded;
  }, [isRailExpanded]);
  const isGanttContext = Boolean(onTaskUpdate);
  const autoEnteredEditModeRef = React.useRef(false);
  
  // Debug logging for component props
  useEffect(() => {
    if (taskData) {
      
      // Check if ID is a valid UUID format
      if (taskData.id) {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        const isValidUuid = uuidRegex.test(taskData.id);
        
        if (!isValidUuid) {
          console.warn('Warning: taskData.id is not in valid UUID format:', taskData.id);
        }
      }
    }
  }, [open, fullScreen, taskData]);
  
  useEffect(() => {
    if (open) {
      setIsEditingMode(initialEditMode);
    }
  }, [open, initialEditMode]);

  useEffect(() => {
    if (!open) {
      autoEnteredEditModeRef.current = false;
      return;
    }
    
    // Only check drafts once when modal opens, not on every re-render
    if (autoEnteredEditModeRef.current) return;
    
    // Check if there's existing content or draft
    const draftId = taskData?.id;
    if (!draftId) return;
    
    const descriptionContent = ticketDetails?.description || taskData?.description || '';
    const acceptanceCriteriaContent = ticketDetails?.acceptance_criteria || taskData?.acceptance_criteria || '';
    
    const hasDescriptionDraft = loadDraft(draftId, 'description');
    const hasACDraft = loadDraft(draftId, 'acceptanceCriteria');
    
    // If there are drafts, automatically enter edit mode (only once)
    if ((hasDescriptionDraft || hasACDraft) && !initialEditMode) {
      setIsEditingMode(true);
      autoEnteredEditModeRef.current = true;
    }
    
    // Show if in edit mode, or if there's content or draft
    setIsDescriptionExpanded(initialEditMode || !!descriptionContent || !!hasDescriptionDraft);
    setIsAcceptanceCriteriaExpanded(initialEditMode || !!acceptanceCriteriaContent || !!hasACDraft);
  }, [open, taskData?.id, initialEditMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const [activeTab, setActiveTab] = useState(() => (!fullScreen && Boolean(String(focusedCommentId ?? '').trim()) ? 1 : 0));
  const [ticketViewMode, setTicketViewMode] = useState('sidebar'); // 'sidebar' | 'modal' | 'fullscreen'
  const [viewSwitcherAnchor, setViewSwitcherAnchor] = useState(null);
  const viewSwitcherCloseTimer = React.useRef(null);

  const handleViewSwitcherOpen = (event) => {
    if (viewSwitcherCloseTimer.current) clearTimeout(viewSwitcherCloseTimer.current);
    setViewSwitcherAnchor(event.currentTarget);
  };

  const handleViewSwitcherClose = () => {
    viewSwitcherCloseTimer.current = setTimeout(() => {
      setViewSwitcherAnchor(null);
    }, 150);
  };

  const handleViewSwitcherPopoverEnter = () => {
    if (viewSwitcherCloseTimer.current) clearTimeout(viewSwitcherCloseTimer.current);
  };
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedSubtask, setSelectedSubtask] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteOptionDialogOpen, setDeleteOptionDialogOpen] = useState(false);
  const [optionToDelete, setOptionToDelete] = useState(null);
  const [deleteOptionCategory, setDeleteOptionCategory] = useState(''); // 'type', 'priority', 'labels', 'sprint'
  const [isProjectStatusModalOpen, setIsProjectStatusModalOpen] = useState(false);
  const [projectStatusForModal, setProjectStatusForModal] = useState(null);
  const [activatingProject, setActivatingProject] = useState(false);
  const [projectStatusModalContent, setProjectStatusModalContent] = useState(null);
  const [commentMenuAnchorEl, setCommentMenuAnchorEl] = useState(null);
  const [commentMenuCommentId, setCommentMenuCommentId] = useState(null);
  const [isDeleteCommentDialogOpen, setIsDeleteCommentDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [isDeletingComment, setIsDeletingComment] = useState(false);
  const [isDeleteChecklistDialogOpen, setIsDeleteChecklistDialogOpen] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentContent, setEditingCommentContent] = useState('');
  const [replyingTarget, setReplyingTarget] = useState(null);
  const [openReplyThreads, setOpenReplyThreads] = useState([]);
  const [editingTitleContent, setEditingTitleContent] = useState('');
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [isEditLinkModalOpen, setIsEditLinkModalOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [deletingLinkId, setDeletingLinkId] = useState(null);
  const [isEditingMode, setIsEditingMode] = useState(false);
  const [isInlineEditInitializing, setIsInlineEditInitializing] = useState(false);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [isSavingTaskChanges, setIsSavingTaskChanges] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isEditingAcceptanceCriteria, setIsEditingAcceptanceCriteria] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [editingAcceptanceCriteriaContent, setEditingAcceptanceCriteriaContent] = useState('');
  const [expandedSubtasks, setExpandedSubtasks] = useState(false);
  const [expandedAttachments, setExpandedAttachments] = useState(false);
  const [attachmentMenuAnchor, setAttachmentMenuAnchor] = useState(null);
  const [attachmentViewMode, setAttachmentViewMode] = useState('tiles');
  const [activeAttachmentMenuAnchor, setActiveAttachmentMenuAnchor] = useState(null);
  const [activeAttachment, setActiveAttachment] = useState(null);
  const [addingSubtaskParentId, setAddingSubtaskParentId] = useState(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [attachmentToRename, setAttachmentToRename] = useState(null);
  const [newAttachmentName, setNewAttachmentName] = useState('');
  const [expandedChecklist, setExpandedChecklist] = useState(false);
  const mentionUsers = React.useMemo(() => {
    if (dropdowns?.assignees && dropdowns.assignees.length > 0) return dropdowns.assignees;
    if (Array.isArray(ticketDetails?.users) && ticketDetails.users.length > 0) return ticketDetails.users;
    if (Array.isArray(taskData?.assignees) && taskData.assignees.length > 0) return taskData.assignees;
    if (Array.isArray(taskData?.users) && taskData.users.length > 0) return taskData.users;
    return [];
  }, [dropdowns?.assignees, ticketDetails?.users, taskData?.assignees, taskData?.users]);
  const descriptionMentionOffset = fullScreen ? 0 : -6;
  const [expandedLinks, setExpandedLinks] = useState(false);
  const [checklistMenuAnchor, setChecklistMenuAnchor] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isManagingLinks, setIsManagingLinks] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [linksMenuAnchor, setLinksMenuAnchor] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [subtaskMenuAnchor, setSubtaskMenuAnchor] = useState(null);
  const checklistRef = React.useRef(null);
  const descriptionRef = useRef(null);
  const acceptanceCriteriaRef = useRef(null);
  const inlineEditBaselineRef = useRef(null);
  const pendingProjectReactivationActionRef = useRef(null);
  const projectStatusesFetchedRef = useRef(false);
  const focusedCommentScrollRef = useRef({ ticketId: null, commentId: '' });
  const focusedCommentRetryRef = useRef(null);
  const normalizedFocusedCommentId = React.useMemo(() => {
    if (focusedCommentId === undefined || focusedCommentId === null) return '';
    const value = String(focusedCommentId).trim();
    return value;
  }, [focusedCommentId]);

  const clearFocusedCommentRetry = useCallback(() => {
    if (focusedCommentRetryRef.current) {
      window.clearTimeout(focusedCommentRetryRef.current);
      focusedCommentRetryRef.current = null;
    }
  }, []);

  const resetFocusedCommentState = useCallback((ticketId = null) => {
    focusedCommentScrollRef.current = { ticketId, commentId: '' };
    clearFocusedCommentRetry();
  }, [clearFocusedCommentRetry]);

  const syncOpenReplyThreads = useCallback((commentPath = []) => {
    if (commentPath.length <= 1) return;

    const threadIdsToOpen = commentPath.slice(0, -1);
    setOpenReplyThreads((prev) => {
      const seen = new Set(prev.map((id) => String(id)));
      const next = [...prev];
      let changed = false;

      threadIdsToOpen.forEach((threadId) => {
        const normalizedThreadId = String(threadId);
        if (seen.has(normalizedThreadId)) return;
        seen.add(normalizedThreadId);
        next.push(threadId);
        changed = true;
      });

      return changed ? next : prev;
    });
  }, []);

  const scrollFocusedCommentIntoView = useCallback((commentId, attempt = 0) => {
    const element = document.getElementById(`comment-${commentId}`);
    const container = scrollContainerRef.current;

    if (!element || !container) {
      if (attempt < 12) {
        focusedCommentRetryRef.current = window.setTimeout(() => {
          scrollFocusedCommentIntoView(commentId, attempt + 1);
        }, 75);
      }
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const currentScrollTop = container.scrollTop || 0;
    const targetTop = currentScrollTop + (elementRect.top - containerRect.top) - FOCUSED_COMMENT_SCROLL_OFFSET;
    const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
    const nextScrollTop = Math.min(Math.max(0, targetTop), maxScrollTop);

    // First pass uses smooth motion; retries use immediate correction.
    container.scrollTo({ top: nextScrollTop, behavior: attempt === 0 ? 'smooth' : 'auto' });

    focusedCommentRetryRef.current = window.setTimeout(() => {
      const verifyElement = document.getElementById(`comment-${commentId}`);
      const verifyContainer = scrollContainerRef.current;
      if (!verifyElement || !verifyContainer) {
        if (attempt < 12) {
          scrollFocusedCommentIntoView(commentId, attempt + 1);
        }
        return;
      }

      const verifyContainerRect = verifyContainer.getBoundingClientRect();
      const verifyElementRect = verifyElement.getBoundingClientRect();
      const actualTop = verifyElementRect.top - verifyContainerRect.top;
      const isTooLow = actualTop > (FOCUSED_COMMENT_SCROLL_OFFSET + 48);
      const isTooHigh = actualTop < (FOCUSED_COMMENT_SCROLL_OFFSET - 12);

      if ((isTooLow || isTooHigh) && attempt < 12) {
        scrollFocusedCommentIntoView(commentId, attempt + 1);
        return;
      }

      verifyElement.setAttribute('data-focused-comment', 'true');
      window.setTimeout(() => {
        verifyElement.removeAttribute('data-focused-comment');
      }, 2500);

      focusedCommentScrollRef.current = {
        ticketId: taskData?.id || null,
        commentId
      };
      focusedCommentRetryRef.current = null;
    }, attempt === 0 ? 150 : 75);
  }, [taskData?.id]);

  useEffect(() => {
    if (!scrollContainer) return;

    const handleScroll = () => {
      // Only collapse the floating rail on the Details tab
      if (activeTab === 0 && scrollContainer.scrollTop > 80) {
        setShowFloatingRail(false);
        if (isRailExpandedRef.current) {
          setIsRailExpanded(false);
        }
      } else {
        setShowFloatingRail(true);
      }
    };

    setShowFloatingRail(true);

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [scrollContainer, activeTab]);
  
  const currentChecklistValue = React.useMemo(
    () => ticketDetails?.checklist || taskData?.checklist || [],
    [ticketDetails?.checklist, taskData?.checklist]
  );
  const hasChecklistItems = React.useMemo(
    () => getChecklistItemCount(currentChecklistValue) > 0,
    [currentChecklistValue]
  );
  const checklistItemCount = React.useMemo(
    () => getChecklistItemCount(currentChecklistValue),
    [currentChecklistValue]
  );
  const attachmentCount = React.useMemo(
    () => (ticketDetails?.attachments || taskData?.attachments || []).length,
    [ticketDetails?.attachments, taskData?.attachments]
  );
  const linkedTicketCount = React.useMemo(() => {
    const linkedTickets = ticketDetails?.linked_tickets;
    if (!linkedTickets || typeof linkedTickets !== 'object') return 0;

    return Object.values(linkedTickets).reduce((count, relation) => (
      count + (Array.isArray(relation?.tickets) ? relation.tickets.length : 0)
    ), 0);
  }, [ticketDetails?.linked_tickets]);



  const [localComments, setLocalComments] = useState([]);
  const [localSubtasks, setLocalSubtasks] = useState([]);
  const [localTaskCompleted, setLocalTaskCompleted] = useState(false);
  const [localAssignees, setLocalAssignees] = useState([]);
  const [localStatus, setLocalStatus] = useState(null);
  const [localDueDate, setLocalDueDate] = useState(null);
  const [localStartDate, setLocalStartDate] = useState(null);
  const [datePickerAnchorEl, setDatePickerAnchorEl] = useState(null);
  const [localStartTime, setLocalStartTime] = useState('');
  const [localDueTime, setLocalDueTime] = useState('');

  const [localLabels, setLocalLabels] = useState([]);
  const [localReporter, setLocalReporter] = useState(null);
  const [localType, setLocalType] = useState(null);
  const [localSprint, setLocalSprint] = useState(null);
  const [localPriority, setLocalPriority] = useState(null);
  const [localDescription, setLocalDescription] = useState(null);
  const [localAcceptanceCriteria, setLocalAcceptanceCriteria] = useState(null);
  const [hasPendingDescriptionAttachments, setHasPendingDescriptionAttachments] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isAcceptanceCriteriaExpanded, setIsAcceptanceCriteriaExpanded] = useState(false);
  const [localEstimate, setLocalEstimate] = useState('');
  const [localStoryPoints, setLocalStoryPoints] = useState('');
  const [localIsRecurring, setLocalIsRecurring] = useState(false);
  const [localRecurrenceConfig, setLocalRecurrenceConfig] = useState(null);
  const [localParentTicket, setLocalParentTicket] = useState(null);
  const [parentTicketSearchTerm, setParentTicketSearchTerm] = useState('');
  const [parentTicketOptions, setParentTicketOptions] = useState([{ id: '', isNone: true }]);
  const [isParentTicketLoading, setIsParentTicketLoading] = useState(false);

  const activeProjectId = ticketDetails?.project?.id || taskData?.project_id || (typeof taskData?.project === 'object' ? taskData?.project?.id : taskData?.project);
  const activeProject = projectDropdownsByUser?.find(project => String(project.id) === String(activeProjectId));
  const isScrum = (activeProject?.project_type || '').toUpperCase() === PROJECT_TYPES.SCRUM.toUpperCase();

  const closeProjectStatusModal = useCallback(() => {
    setIsProjectStatusModalOpen(false);
    setProjectStatusForModal(null);
    setProjectStatusModalContent(null);
    pendingProjectReactivationActionRef.current = null;
  }, []);

  // Inline create state for edit mode dropdowns
  const [isCreatingViewTaskType, setIsCreatingViewTaskType] = useState(false);
  const [newViewTaskTypeName, setNewViewTaskTypeName] = useState('');
  const [isCreatingTaskTypeSubmitting, setIsCreatingTaskTypeSubmitting] = useState(false);
  const [isCreatingViewSprint, setIsCreatingViewSprint] = useState(false);
  const [newViewSprintName, setNewViewSprintName] = useState('');
  const [newViewSprintColor, setNewViewSprintColor] = useState(COLORS.primary);
  const [isCreatingSprintSubmitting, setIsCreatingSprintSubmitting] = useState(false);
  const [isCreatingViewPriority, setIsCreatingViewPriority] = useState(false);
  const [newViewPriorityName, setNewViewPriorityName] = useState('');
  const [newViewPriorityColor, setNewViewPriorityColor] = useState(COLORS.primary);
  const [createPriorityAnchor, setCreatePriorityAnchor] = useState(null);
  const [editPriorityAnchor, setEditPriorityAnchor] = useState(null);
  const [isCreatingPrioritySubmitting, setIsCreatingPrioritySubmitting] = useState(false);
  const [isCreatingViewLabel, setIsCreatingViewLabel] = useState(false);
  const [newViewLabelName, setNewViewLabelName] = useState('');
  const [newViewLabelColor, setNewViewLabelColor] = useState(COLORS.primary);
  const [isCreatingLabelSubmitting, setIsCreatingLabelSubmitting] = useState(false);
  const [editingViewTaskType, setEditingViewTaskType] = useState(null);
  const [editingViewPriority, setEditingViewPriority] = useState(null);
  const [editingViewLabel, setEditingViewLabel] = useState(null);
  const [editingViewSprint, setEditingViewSprint] = useState(null);

  const [createLabelAnchor, setCreateLabelAnchor] = useState(null);
  const [editLabelAnchor, setEditLabelAnchor] = useState(null);
  const [createSprintAnchor, setCreateSprintAnchor] = useState(null);
  const [editSprintAnchor, setEditSprintAnchor] = useState(null);

  // Consolidated effect for API calls and dropdown fetching
  useEffect(() => {
    if (open && taskData?.id && loadedTicketId.current !== taskData.id) {
      // Reset refs when a new ticket is opened
      worklogFetched.current = false;
      historyFetched.current = false;
      loadedTicketId.current = taskData.id;
      autoEnteredEditModeRef.current = false;
      setLocalDescription(null);
      setLocalAcceptanceCriteria(null);
      setHasPendingDescriptionAttachments(false);
      
      // Check if there's existing content or draft
      const descriptionContent = ticketDetails?.description || taskData?.description || '';
      const acceptanceCriteriaContent = ticketDetails?.acceptance_criteria || taskData?.acceptance_criteria || '';
      const hasDescriptionDraft = loadDraft(taskData.id, 'description');
      const hasACDraft = loadDraft(taskData.id, 'acceptanceCriteria');
      
      // If there are drafts, automatically enter edit mode (only once per task)
      if ((hasDescriptionDraft || hasACDraft) && !initialEditMode && !autoEnteredEditModeRef.current) {
        setIsEditingMode(true);
        autoEnteredEditModeRef.current = true;
      }
      
      // Show if in edit mode, or if there's content or draft
      setIsDescriptionExpanded(initialEditMode || !!descriptionContent || !!hasDescriptionDraft);
      setIsAcceptanceCriteriaExpanded(initialEditMode || !!acceptanceCriteriaContent || !!hasACDraft);
      // API calls are handled by parent component to prevent duplicates
    }
    
    // Fetch dropdowns (assignees needed for mentions) if not available and prevent duplicate requests
    const projectId = ticketDetails?.project?.id || (typeof taskData?.project === 'object' ? taskData?.project?.id : taskData?.project);
    if (projectId && 
        !dropdownsFetched.current &&
        (!dropdowns.assignees || dropdowns.assignees.length === 0 || 
         !dropdowns.statuses || dropdowns.statuses.length === 0)) {
      dropdownsFetched.current = true;
      dispatch(fetchTicketDropdowns(projectId));
    }

    if (open && !projectStatusesFetchedRef.current && (!projectDropdownsByUser || projectDropdownsByUser.length === 0)) {
      projectStatusesFetchedRef.current = true;
      dispatch(fetchProjectDropdownsByUser());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, taskData?.id, dispatch, fullScreen, ticketDetails?.project?.id, dropdowns.assignees, dropdowns.statuses, projectDropdownsByUser, initialEditMode]);

  // Fetch history/worklog when ticket changes and the respective tab is already active
  // This only runs when taskData.id changes (new ticket opened), not when tab changes
  const previousTicketId = React.useRef(null);
  useEffect(() => {
    if (open && taskData?.id) {
      const ticketChanged = previousTicketId.current !== taskData.id;
      previousTicketId.current = taskData.id;
      
      // Only fetch if ticket changed (new ticket opened) and tab is already active
      if (ticketChanged) {
        const workLogTabIndex = fullScreen ? 1 : 2;
        const historyTabIndex = fullScreen ? 2 : 3;
        
        // If worklog tab is active, fetch worklog data
        if (activeTab === workLogTabIndex && !worklogFetched.current) {
          dispatch(fetchWorklogSummary(taskData.id));
          worklogFetched.current = true;
        }
        
        // If history tab is active, always fetch history data (to get updated data)
        if (activeTab === historyTabIndex) {
          dispatch(fetchTicketHistory(taskData.id));
          historyFetched.current = true;
        }
      }
    }
  }, [open, taskData?.id, activeTab, fullScreen, dispatch]);

  // Reset scroll position when active tab or ticket ID changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [activeTab, taskData?.id]);

  // Log Redux state changes for debugging
  useEffect(() => {
  }, [worklogSummary, worklogSummaryLoading]);

  useEffect(() => {
  }, [ticketHistory, ticketHistoryLoading]);



  const mapApiComment = useCallback((comment) => {
    return mapApiCommentUtil(comment, t, decodeHtmlContent);
  }, [t]);

  // Update local comments when Redux comments change
  useEffect(() => {
    if (comments && comments.length > 0) {
      setLocalComments(comments.map(mapApiComment));
    } else {
      // Clear local comments if no API data
      setLocalComments([]);
    }
  }, [comments, mapApiComment]);

  useEffect(() => {
    if (!open || !taskData?.id) return;

    const activeDraft = getActiveCommentDraft(taskData.id);
    if (!activeDraft) return;

    if (activeDraft.mode === 'new') {
      if (editingCommentId !== null) {
        setEditingCommentId(null);
        setEditingCommentContent('');
      }
      if (replyingTarget !== null) {
        setReplyingTarget(null);
      }
      return;
    }

    if (!activeDraft.commentId || localComments.length === 0) return;

    const targetComment = findCommentById(localComments, activeDraft.commentId);
    if (!targetComment) {
      clearDraft(taskData.id, activeDraft.fieldName);
      return;
    }

    const pathTargetId = activeDraft.mode === 'reply'
      ? (activeDraft.parentReplyId || activeDraft.commentId)
      : activeDraft.commentId;
    const commentPath = findCommentPathById(localComments, pathTargetId);
    if (commentPath.length > 1) {
      setOpenReplyThreads((prev) => Array.from(new Set([...prev, ...commentPath.slice(0, -1)])));
    }

    if (activeDraft.mode === 'reply') {
      if (
        replyingTarget?.commentId !== activeDraft.commentId ||
        replyingTarget?.parentReplyId !== (activeDraft.parentReplyId || null)
      ) {
        setReplyingTarget({
          commentId: activeDraft.commentId,
          parentReplyId: activeDraft.parentReplyId || null
        });
      }
      if (editingCommentId !== null) {
        setEditingCommentId(null);
        setEditingCommentContent('');
      }
      return;
    }

    if (replyingTarget !== null) {
      setReplyingTarget(null);
    }

    if (editingCommentId !== activeDraft.commentId) {
      setEditingCommentId(targetComment.id);
      setEditingCommentContent(targetComment.content || '');
    }
  }, [open, taskData?.id, localComments, editingCommentId, replyingTarget]);

  useEffect(() => {
    if (!open || !taskData?.id) {
      resetFocusedCommentState();
      return;
    }

    if (!normalizedFocusedCommentId) {
      resetFocusedCommentState(taskData.id);
      return;
    }

    if (!fullScreen && activeTab !== 1) {
      setActiveTab(1);
      return;
    }

    if (!localComments.length) return;

    const targetComment = findCommentById(localComments, normalizedFocusedCommentId);
    if (!targetComment) return;

    const commentPath = findCommentPathById(localComments, normalizedFocusedCommentId);
    syncOpenReplyThreads(commentPath);

    const alreadyFocused =
      focusedCommentScrollRef.current.ticketId === taskData.id &&
      focusedCommentScrollRef.current.commentId === normalizedFocusedCommentId;

    if (alreadyFocused) return;

    clearFocusedCommentRetry();
    focusedCommentRetryRef.current = window.setTimeout(() => {
      scrollFocusedCommentIntoView(normalizedFocusedCommentId, 0);
    }, 50);

    return () => {
      clearFocusedCommentRetry();
    };
  }, [
    open,
    taskData?.id,
    normalizedFocusedCommentId,
    localComments,
    fullScreen,
    activeTab,
    resetFocusedCommentState,
    syncOpenReplyThreads,
    scrollFocusedCommentIntoView,
    clearFocusedCommentRetry
  ]);

  // Update local subtasks when ticketDetails change
  useEffect(() => {
    if (ticketDetails?.id && taskData?.id && ticketDetails.id !== taskData.id) return;
    if (ticketDetails?.sub_tickets) {
      setLocalSubtasks(ticketDetails.sub_tickets);
    } else {
      setLocalSubtasks([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketDetails?.sub_tickets]);

  // Initialize local task completion state
  useEffect(() => {
    setLocalTaskCompleted(
      taskData?.completed || 
      ticketDetails?.completed || 
      ticketDetails?.is_completed || 
      taskData?.marked_as_completed || 
      ticketDetails?.marked_as_completed || 
      false
    );
  }, [taskData?.completed, ticketDetails?.completed, ticketDetails?.is_completed, taskData?.marked_as_completed, ticketDetails?.marked_as_completed]);

  // Initialize local assignees state
  useEffect(() => {
    setLocalAssignees(ticketDetails?.users || []);
  }, [ticketDetails?.users]);

  // Initialize local status state
  useEffect(() => {
    setLocalStatus(ticketDetails?.status || null);
  }, [ticketDetails?.status]);

  // Initialize local due date state (and extract time if present)
  useEffect(() => {
    setLocalDueDate(getLocalDatePart(ticketDetails?.end_date) || null);
    setLocalStartDate(getLocalDatePart(ticketDetails?.start_date) || null);
    // Only show due time when the API says the deadline includes one.
    setLocalDueTime(ticketDetails?.has_due_time ? getLocalTimePart(ticketDetails?.end_date, { includeMidnight: true }) : '');
    setLocalStartTime(getLocalTimePart(ticketDetails?.start_date));
  }, [ticketDetails?.end_date, ticketDetails?.has_due_time, ticketDetails?.start_date]);

  // Initialize local labels state
  useEffect(() => {
    setLocalLabels(ticketDetails?.labels?.map(l => typeof l === 'object' ? l.id : l) || []);
  }, [ticketDetails?.labels]);

  // Initialize local reporter state
  useEffect(() => {
    setLocalReporter(ticketDetails?.reporter || taskData?.reporter || null);
  }, [ticketDetails?.reporter, taskData?.reporter]);

  // Initialize local task type state
  useEffect(() => {
    if (ticketDetails?.type) {
      setLocalType(ticketDetails.type);
    } else if (taskData?.taskType) {
      setLocalType(taskData.taskType);
    }
  }, [ticketDetails?.type, taskData?.taskType]);

  useEffect(() => {
    if (isScrum) {
      if (ticketDetails?.scrum_sprint_id || taskData?.scrum_sprint_id) {
        setLocalSprint({
          id: ticketDetails?.scrum_sprint_id || taskData?.scrum_sprint_id,
          name: ticketDetails?.scrum_sprint || taskData?.scrum_sprint || 'Unnamed Sprint'
        });
      } else {
        setLocalSprint(null);
      }
    } else {
      setLocalSprint(ticketDetails?.sprint || taskData?.sprint || null);
    }
  }, [ticketDetails?.sprint, taskData?.sprint, ticketDetails?.scrum_sprint_id, taskData?.scrum_sprint_id, ticketDetails?.scrum_sprint, taskData?.scrum_sprint, isScrum]);

  useEffect(() => {
    setLocalPriority(ticketDetails?.priority || taskData?.priority || null);
  }, [ticketDetails?.priority, taskData?.priority]);

  // Initialize local estimate state
  useEffect(() => {
    const timeStr = minutesToTimeString(ticketDetails?.estimate_time_in_minutes || taskData?.estimate_time_in_minutes || 0);
    setLocalEstimate(timeStr);
  }, [ticketDetails?.estimate_time_in_minutes, taskData?.estimate_time_in_minutes]);

  // Initialize local story points state
  useEffect(() => {
    setLocalStoryPoints(ticketDetails?.story_points || taskData?.story_points || taskData?.storyPoints || '');
  }, [ticketDetails?.story_points, taskData?.story_points, taskData?.storyPoints]);

  useEffect(() => {
    const recurring = Boolean(ticketDetails?.is_recurring ?? taskData?.is_recurring);
    setLocalIsRecurring(recurring);
    setLocalRecurrenceConfig(recurring
      ? normalizeRecurrenceConfig(ticketDetails?.recurrence_config || taskData?.recurrence_config)
      : null);
  }, [ticketDetails?.is_recurring, ticketDetails?.recurrence_config, taskData?.is_recurring, taskData?.recurrence_config]);

  const descriptionContent = localDescription ?? ticketDetails?.description ?? taskData?.description ?? '';
  const acceptanceCriteriaContent = localAcceptanceCriteria ?? ticketDetails?.acceptance_criteria ?? taskData?.acceptance_criteria ?? '';

  useEffect(() => {
    const fallbackParentId = ticketDetails?.parent_ticket_id || taskData?.parent_ticket_id || null;
    const fallbackTicketNumber = ticketDetails?.parent_ticket_number || taskData?.parent_ticket_number || '';
    const fallbackTitle = ticketDetails?.parent_ticket_title || taskData?.parent_ticket_title || '';
    if (ticketDetails?.parent_ticket) {
      setLocalParentTicket(ticketDetails.parent_ticket);
      return;
    }
    if (fallbackParentId) {
      setLocalParentTicket({
        id: fallbackParentId,
        ticket_number: fallbackTicketNumber,
        title: fallbackTitle
      });
      return;
    }
    setLocalParentTicket(null);
  }, [
    ticketDetails?.parent_ticket,
    ticketDetails?.parent_ticket_id,
    ticketDetails?.parent_ticket_number,
    ticketDetails?.parent_ticket_title,
    taskData?.parent_ticket_id,
    taskData?.parent_ticket_number,
    taskData?.parent_ticket_title
  ]);

  useEffect(() => {
    if (!open || !activeProjectId) {
      setParentTicketOptions([{ id: '', isNone: true }]);
      setIsParentTicketLoading(false);
      return;
    }

    let isMounted = true;
    const timeoutId = setTimeout(async () => {
      try {
        setIsParentTicketLoading(true);
        const result = await dispatch(searchTickets({
          projectId: activeProjectId,
          searchTerm: parentTicketSearchTerm
        })).unwrap();

        const normalizedResults = Array.isArray(result)
          ? result
          : (Array.isArray(result?.data) ? result.data : []);

        const options = normalizedResults.filter((ticket) => String(ticket.id) !== String(taskData?.id));
        if (isMounted) {
          setParentTicketOptions([{ id: '', isNone: true }, ...options]);
        }
      } catch (error) {
        if (isMounted) {
          setParentTicketOptions([]);
        }
      } finally {
        if (isMounted) {
          setIsParentTicketLoading(false);
        }
      }
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [open, activeProjectId, parentTicketSearchTerm, taskData?.id, dispatch]);

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const MAX_ATTACHMENTS = 20;
    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    const MIN_FILE_SIZE = 1024;

    const currentAttachmentsCount = ticketDetails?.attachments?.length || 0;
    if (currentAttachmentsCount + files.length > MAX_ATTACHMENTS) {
      toast.error(t('taskModal.maxAttachmentsError'));
      event.target.value = '';
      return;
    }

    setIsUploadingFiles(true);
    const uploadedFiles = [];
    
    try {
      for (const file of files) {
        if (file.size > MAX_FILE_SIZE) {
          toast.error(t('taskModal.fileTooLarge', `File ${file.name} is too large. Max size is 10MB`));
          continue;
        }
        if (file.size < MIN_FILE_SIZE) {
          toast.error(t('taskModal.fileTooSmall', `File ${file.name} is too small. Min size is 1KB`));
          continue;
        }
        try {
          const uploadedFile = await dispatch(uploadFile(file)).unwrap();
          uploadedFiles.push(uploadedFile);
        } catch (error) {
          console.error('Failed to upload file:', file.name, error);
          if (!isStorageQuotaExceededError(error)) {
            let errorMessage = t('taskModal.attachmentUploadError');
            if (typeof error === 'string' && error.trim() !== '') {
              errorMessage = error;
            } else if (error && typeof error === 'object' && (error.msg || error.message)) {
              errorMessage = error.msg || error.message;
            }
            toast.error(errorMessage);
          }
        }
      }
      
      if (uploadedFiles.length > 0 && taskData?.id) {
        // Send only new files; the API merges them with its latest attachment state.
        await dispatch(updateTicketAttachments({ ticketId: taskData.id, add: uploadedFiles })).unwrap();

        toast.success(t(uploadedFiles.length === 1 ? 'taskModal.attachmentUploadSuccess' : 'taskModal.attachmentsUploadSuccess'));
      }
    } finally {
      setIsUploadingFiles(false);
      event.target.value = '';
    }
  };

  const uploadPendingDescriptionAttachments = async (pendingFiles = descriptionRef.current?.getAttachments?.() || []) => {
    if (pendingFiles.length === 0) {
      return [];
    }

    const uploadedFiles = [];
    for (const file of pendingFiles) {
      // Description editor attachments stay local until Save Changes is clicked.
      if (file?.key && file?.url) {
        uploadedFiles.push(file);
        continue;
      }

      try {
        const uploadedFile = await dispatch(uploadFile(file)).unwrap();
        uploadedFiles.push(uploadedFile);
      } catch (error) {
        if (!isStorageQuotaExceededError(error)) {
          let errorMessage = t('taskViewModal.descriptionAttachmentUploadError', 'Failed to upload description attachment. Please try again.');
          if (typeof error === 'string' && error.trim() !== '') {
            errorMessage = error;
          } else if (error && typeof error === 'object' && (error.msg || error.message)) {
            errorMessage = error.msg || error.message;
          }
          toast.error(errorMessage);
        }
        const uploadError = new Error('ATTACHMENT_UPLOAD_ERROR');
        uploadError.isHandled = true;
        throw uploadError;
      }
    }

    return uploadedFiles;
  };

  const handleConfirmRename = async () => {
    if (!attachmentToRename || !newAttachmentName.trim() || !taskData?.id) return;
    
    const fileName = typeof attachmentToRename === 'object' ? (attachmentToRename.name || attachmentToRename.key) : attachmentToRename;
    
    if (newAttachmentName === fileName) {
      setRenameDialogOpen(false);
      return;
    }

    try {
      if (!attachmentToRename?.key) throw new Error('Attachment key is required');
      // Rename by storage key without resubmitting the browser's full attachment snapshot.
      await dispatch(updateTicketAttachments({
        ticketId: taskData.id,
        rename: [{ key: attachmentToRename.key, name: newAttachmentName.trim() }]
      })).unwrap();
      
      toast.success(t('taskViewModal.attachmentRenamed', 'Attachment renamed successfully'));
      setRenameDialogOpen(false);
      setAttachmentToRename(null);
    } catch (error) {
      toast.error(t('taskViewModal.attachmentRenameError', 'Failed to rename attachment'));
    }
  };

  const closeActiveAttachmentMenu = ({ clearAttachment = true } = {}) => {
    if (activeAttachmentMenuAnchor) {
      activeAttachmentMenuAnchor.blur();
    }

    setActiveAttachmentMenuAnchor(null);
    if (clearAttachment) {
      setActiveAttachment(null);
    }
  };

  const handleAttachmentAction = async (action) => {
    if (!activeAttachment || !taskData?.id) return;
    const fileName = getAttachmentName(activeAttachment);

    closeActiveAttachmentMenu({ clearAttachment: false });

    switch (action) {
      case 'rename':
        setAttachmentToRename(activeAttachment);
        setNewAttachmentName(fileName);
        setRenameDialogOpen(true);
        break;
      case 'copyUrl':
        const fileUrl = getAttachmentUrl(activeAttachment);
        if (fileUrl) {
          navigator.clipboard.writeText(fileUrl);
          toast.success(t('taskViewModal.urlCopied', 'URL copied to clipboard'));
        }
        break;
      case 'download':
        await triggerAttachmentDownload(activeAttachment, { t });
        break;
      case 'cover':
        toast.info(t('taskViewModal.featureComingSoon', 'Feature coming soon'));
        break;
      case 'delete':
        const prevActive = activeAttachment;
        try {
          if (!activeAttachment?.key) throw new Error('Attachment key is required');
          setActiveAttachment(null);
          // Explicit removal prevents unrelated concurrent attachments from being deleted.
          await dispatch(updateTicketAttachments({ ticketId: taskData.id, removeKeys: [activeAttachment.key] })).unwrap();
          toast.success(t('taskViewModal.attachmentDeleted', 'Attachment deleted'));
        } catch (error) {
          toast.error(t('taskViewModal.attachmentDeleteError', 'Failed to delete attachment'));
          setActiveAttachment(prevActive);
        }
        break;
      default:
        break;
    }
  };

  // Handle subtask completion change
  const handleSubtaskCompletionChange = (taskId, completed) => {
    setLocalSubtasks(prev => prev.map(subtask => 
      subtask.id === taskId ? { ...subtask, marked_as_completed: completed } : subtask
    ));
  };

  // Handle main task completion change
  const handleTaskCompletionChange = React.useCallback((taskId, completed) => {
    setLocalTaskCompleted(completed);
    
    if (onTaskUpdate) {
      // Gantt view: use optimistic update, API call handled elsewhere
      onTaskUpdate(taskId, { marked_as_completed: completed });
    } else {
      // Non-Gantt views: make API call directly (existing behavior)
      dispatch(updateTicketPartial({ 
        ticketId: taskId, 
        ticketData: { marked_as_completed: completed } 
      }));
    }
  }, [onTaskUpdate, dispatch]);

  const handleTabChange = (event, newValue) => {
    const aiTabIndex = fullScreen ? 3 : 4;
    // If trying to switch to AI tab but it's not available, switch to details tab
    if (newValue === aiTabIndex && !shouldShowAIDeveloperTab()) {
      newValue = 0;
    }
    
    setActiveTab(newValue);
    
    if (newValue === workLogTabIndex && taskData && taskData.id && !worklogFetched.current) {
      dispatch(fetchWorklogSummary(taskData.id));
      worklogFetched.current = true;
    }
    if (newValue === historyTabIndex && taskData && taskData.id) {
      // Always refresh history when tab is clicked to get updated data
      dispatch(fetchTicketHistory(taskData.id));
      historyFetched.current = true;
    }
    // AI Developer tab doesn't need special handling as it manages its own state
  };

  const handleEdit = () => {
    setIsInlineEditInitializing(true);
    inlineEditBaselineRef.current = getCurrentInlineTaskData();
    resetDraftFromCurrentTicket();
    setIsEditingMode(true);
    
    // Auto-expand description and acceptance criteria if they have content or drafts
    const draftId = taskData?.id;
    const descriptionContent = ticketDetails?.description || taskData?.description || '';
    const acceptanceCriteriaContent = ticketDetails?.acceptance_criteria || taskData?.acceptance_criteria || '';
    const hasDescriptionDraft = draftId ? loadDraft(draftId, 'description') : null;
    const hasACDraft = draftId ? loadDraft(draftId, 'acceptanceCriteria') : null;
    
    if (descriptionContent || hasDescriptionDraft) {
      setIsDescriptionExpanded(true);
    }
    if (acceptanceCriteriaContent || hasACDraft) {
      setIsAcceptanceCriteriaExpanded(true);
    }
  };

  useEffect(() => {
    if (!isEditingMode || !isInlineEditInitializing) return undefined;

    const timer = setTimeout(() => {
      setIsInlineEditInitializing(false);
    }, 0);

    return () => clearTimeout(timer);
  }, [isEditingMode, isInlineEditInitializing]);

  const resetDraftFromCurrentTicket = useCallback(() => {
    setEditingTitleContent(ticketDetails?.title || taskData?.title || '');
    setLocalAssignees(ticketDetails?.users || taskData?.users || taskData?.assignees || []);
    setLocalStatus(ticketDetails?.status || taskData?.status || null);
    const dueDate = ticketDetails?.end_date || taskData?.end_date || taskData?.dueDate || taskData?.due_date;
    const startDate = ticketDetails?.start_date || taskData?.start_date || taskData?.startDate;
    setLocalDueDate(getLocalDatePart(dueDate) || null);
    setLocalStartDate(getLocalDatePart(startDate) || null);
    const labels = ticketDetails?.labels || taskData?.labels || [];
    setLocalLabels(labels.map(l => typeof l === 'object' ? l.id : l));
    setLocalReporter(ticketDetails?.reporter || taskData?.reporter || null);
    setLocalType(ticketDetails?.type || taskData?.taskType || null);
    let sprintObj = null;
    if (isScrum) {
      if (ticketDetails?.scrum_sprint_id || taskData?.scrum_sprint_id) {
        sprintObj = {
          id: ticketDetails?.scrum_sprint_id || taskData?.scrum_sprint_id,
          name: ticketDetails?.scrum_sprint || taskData?.scrum_sprint || 'Unnamed Sprint'
        };
      }
    } else {
      sprintObj = ticketDetails?.sprint || taskData?.sprint || null;
      if (typeof sprintObj === 'string') {
        sprintObj = { id: sprintObj };
      }
    }
    setLocalSprint(sprintObj);
    setLocalPriority(ticketDetails?.priority || taskData?.priority || null);
    setLocalEstimate(minutesToTimeString(ticketDetails?.estimate_time_in_minutes || taskData?.estimate_time_in_minutes || 0));
    setLocalStoryPoints(ticketDetails?.story_points || taskData?.story_points || taskData?.storyPoints || '');
    const recurring = Boolean(ticketDetails?.is_recurring ?? taskData?.is_recurring);
    setLocalIsRecurring(recurring);
    setLocalRecurrenceConfig(recurring
      ? normalizeRecurrenceConfig(ticketDetails?.recurrence_config || taskData?.recurrence_config)
      : null);
    setLocalDescription(null);
    setLocalAcceptanceCriteria(null);
    setHasPendingDescriptionAttachments(false);

    const fallbackParentId = ticketDetails?.parent_ticket_id || taskData?.parent_ticket_id || null;
    const fallbackTicketNumber = ticketDetails?.parent_ticket_number || taskData?.parent_ticket_number || '';
    const fallbackTitle = ticketDetails?.parent_ticket_title || taskData?.parent_ticket_title || '';
    if (ticketDetails?.parent_ticket) {
      setLocalParentTicket(ticketDetails.parent_ticket);
    } else if (fallbackParentId) {
      setLocalParentTicket({
        id: fallbackParentId,
        ticket_number: fallbackTicketNumber,
        title: fallbackTitle
      });
    } else {
      setLocalParentTicket(null);
    }
  }, [ticketDetails, taskData, isScrum]);

  const resetState = () => {
    setActiveTab(0);
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setIsDeleteModalOpen(false);
    setSelectedSubtask(null);
    setAddingSubtaskParentId(null);
    loadedTicketId.current = null;
    worklogFetched.current = false;
    historyFetched.current = false;
    dropdownsFetched.current = false;
    setLocalComments([]);
    setExpandedSubtasks(false);
    setExpandedAttachments(false);
    setExpandedChecklist(false);
    setExpandedLinks(false);
    setIsManagingLinks(false);
    setIsEditingMode(false);
    setIsInlineEditInitializing(false);
    setIsEditingAcceptanceCriteria(false);
    setReplyingTarget(null);
    setOpenReplyThreads([]);
    setLocalDescription(null);
    setLocalAcceptanceCriteria(null);
    setHasPendingDescriptionAttachments(false);
    inlineEditBaselineRef.current = null;
    setLocalParentTicket(null);
    setParentTicketSearchTerm('');
    setParentTicketOptions([]);
    setIsParentTicketLoading(false);
  };

  // Handle inline field save (for dropdowns and date changes in edit mode)
  const handleInlineFieldSave = useCallback(async (field, value, optimisticValues = {}) => {
    if (!taskData?.id) return;
    const previousStatus = optimisticValues.previousStatus ?? ticketDetails?.status ?? taskData?.status ?? null;
    const nextStatus = optimisticValues.status ?? localStatus;
    const isStatusChange = field === 'status_id';

    if (isStatusChange && nextStatus) {
      dispatch(updateTicketStatus({
        ticketId: taskData.id,
        oldStatus: previousStatus,
        newStatus: nextStatus
      }));
    }

    try {
      await dispatch(updateTicketPartial({ ticketId: taskData.id, ticketData: { [field]: value } })).unwrap();
      dispatch(updateCalendarTicket({
        ticketId: taskData.id,
        updates: {
          status: nextStatus,
          assignee: localAssignees[0] || null,
          assignees: localAssignees,
          start_date: localStartDate,
          due_date: localDueDate,
          task_type: localType,
          priority: localPriority,
        }
      }));
      if (onTaskUpdate) {
        onTaskUpdate(taskData.id, {
          status: nextStatus,
          assignees: localAssignees,
          assignee: localAssignees[0] || null,
          start_date: localStartDate,
          due_date: localDueDate,
          type: localType,
          priority: localPriority,
          sprint: localSprint,
          labels: localLabels,
          reporter: localReporter,
          parent_ticket_id: localParentTicket?.isNone ? null : (localParentTicket?.id || null),
        });
      }
    } catch (error) {
      if (isStatusChange && nextStatus && previousStatus) {
        dispatch(updateTicketStatus({
          ticketId: taskData.id,
          oldStatus: nextStatus,
          newStatus: previousStatus
        }));
        setLocalStatus(previousStatus);
      }
      const fallbackMsg = t('taskViewModal.saveError', 'Failed to save changes');
      const errorMessage = (typeof error === 'string' ? error : null) || error?.error || error?.message || error?.payload?.error || error?.payload || fallbackMsg;
      toast.error(typeof errorMessage === 'string' ? errorMessage : fallbackMsg);
    }
  }, [taskData?.id, taskData?.status, dispatch, localAssignees, localStatus, localStartDate, localDueDate, localType, localPriority, localSprint, localLabels, localReporter, localParentTicket, ticketDetails?.status, onTaskUpdate, t]);

  const handleCancelEdit = () => {
    if (taskData?.id) {
      clearDraftFields(taskData.id, ['description', 'acceptanceCriteria']);
    }
    inlineEditBaselineRef.current = null;
    setIsEditingMode(false);
    setIsInlineEditInitializing(false);
    setDatePickerAnchorEl(null);
    resetDraftFromCurrentTicket();
  };

  const handleTitleContentChange = (value) => {
    setEditingTitleContent(clampTextToLimit(value, CHARACTER_LIMITS.TICKET_TITLE));
  };

  const handleTitlePaste = (event) => {
    if (!isEditingMode) return;

    const pastedText = event.clipboardData?.getData('text') || '';
    const { value } = buildLimitedPasteValue({
      currentValue: editingTitleContent,
      pastedText,
      selectionStart: event.target.selectionStart,
      selectionEnd: event.target.selectionEnd,
      limit: CHARACTER_LIMITS.TICKET_TITLE
    });

    event.preventDefault();
    setEditingTitleContent(value);
  };

  const getInlineEntityId = useCallback((value) => getEntityId(value), []);

  const normalizeInlineDate = useCallback((value) => normalizeDateValue(value, null), []);

  // Combines a date string (YYYY-MM-DD) and time string (HH:MM) into a datetime
  // string for the API. If no time is provided, just the date is returned.

  const handleDatePickerApply = useCallback(async ({ fromDate, toDate, startTime, dueTime } = {}) => {
    if (!taskData?.id) return;


    const nextStartDate = fromDate || null;
    const nextDueDate = toDate || null;
    
    // Fallback to local state if not provided
    const nextStartTime = startTime !== undefined ? startTime : localStartTime;
    const nextDueTime = dueTime !== undefined ? dueTime : localDueTime;

    const ticketData = {};

    const newStartStr = buildDateTimeString(nextStartDate, nextStartTime);
    const newDueStr = buildDateTimeString(nextDueDate, nextDueTime);

    const savedStartStr = ticketDetails?.start_date || taskData?.start_date || taskData?.startDate || null;
    const savedDueStr = ticketDetails?.end_date || taskData?.end_date || taskData?.dueDate || taskData?.due_date || null;

    const isSameTime = (d1, d2) => {
      if (d1 === d2) return true;
      if (!d1 || !d2) return false;
      const t1 = new Date(d1).getTime();
      const t2 = new Date(d2).getTime();
      return !isNaN(t1) && !isNaN(t2) && t1 === t2;
    };

    if (!isSameTime(newStartStr, savedStartStr)) {
      ticketData.start_date = newStartStr;
    }

    if (!isSameTime(newDueStr, savedDueStr)) {
      ticketData.due_date = newDueStr; // API expects due_date when batching
      ticketData.has_due_time = Boolean(nextDueTime);
    }

    if (Object.keys(ticketData).length > 0) {
      await dispatch(updateTicketPartial({ ticketId: taskData.id, ticketData })).unwrap();

      dispatch(updateCalendarTicket({
        ticketId: taskData.id,
        updates: {
          status: localStatus,
          assignee: localAssignees[0] || null,
          assignees: localAssignees,
          start_date: nextStartDate,
          due_date: nextDueDate,
          has_due_time: Boolean(nextDueTime),
          task_type: localType,
          priority: localPriority,
        }
      }));

      if (onTaskUpdate) {
        onTaskUpdate(taskData.id, {
          status: localStatus,
          assignees: localAssignees,
          assignee: localAssignees[0] || null,
          start_date: nextStartDate,
          due_date: nextDueDate,
          has_due_time: Boolean(nextDueTime),
          type: localType,
          priority: localPriority,
          sprint: localSprint,
          labels: localLabels,
          reporter: localReporter,
          parent_ticket_id: localParentTicket?.isNone ? null : (localParentTicket?.id || null),
        });
      }
    }

    setDatePickerAnchorEl(null);
  }, [taskData?.id, ticketDetails?.start_date, ticketDetails?.end_date, taskData?.start_date, taskData?.startDate, taskData?.end_date, taskData?.dueDate, taskData?.due_date, dispatch, localAssignees, localStatus, localType, localPriority, localSprint, localLabels, localReporter, localParentTicket, onTaskUpdate, localStartTime, localDueTime]);




  const normalizeInlineComparable = useCallback((key, value) => {
    if (key === 'assignee' || key === 'labels') {
      return (value || []).map(getInlineEntityId).filter(Boolean).map(String).sort();
    }
    if (key === 'description' || key === 'acceptance_criteria') {
      return normalizeRichTextHtml(value || '');
    }
    if (key === 'start_date' || key === 'due_date') {
      return normalizeInlineDate(value);
    }
    if (key === 'recurrence_config') {
      return stableStringify(normalizeRecurrenceConfig(value));
    }
    if (value == null || value === '') return null;
    return String(value);
  }, [getInlineEntityId, normalizeInlineDate]);

  const normalizeInlineTaskData = useCallback((data) => (
    Object.entries(data || {}).reduce((acc, [key, value]) => {
      acc[key] = normalizeInlineComparable(key, value);
      return acc;
    }, {})
  ), [normalizeInlineComparable]);

  const getChangedInlinePayload = useCallback((draftData, baselineData) => {
    const normalizedBaseline = normalizeInlineTaskData(baselineData);

    return Object.entries(draftData).reduce((acc, [key, value]) => {
      if (stableStringify(normalizeInlineComparable(key, value)) !== stableStringify(normalizedBaseline[key])) {
        acc[key] = value;
      }
      return acc;
    }, {});
  }, [normalizeInlineComparable, normalizeInlineTaskData]);

  const getCurrentInlineTaskData = useCallback(() => {
    const currentAssignees = ticketDetails?.users || taskData?.users || taskData?.assignees || [];
    const currentLabels = ticketDetails?.labels || taskData?.labels || [];

    const data = {
      title: (ticketDetails?.title || taskData?.title || '').trim(),
      assignee: currentAssignees.map(getInlineEntityId),
      status_id: getInlineEntityId(ticketDetails?.status || taskData?.status),
      start_date: normalizeInlineDate(ticketDetails?.start_date || taskData?.start_date || taskData?.startDate),
      due_date: normalizeInlineDate(ticketDetails?.end_date || taskData?.end_date || taskData?.dueDate || taskData?.due_date),
      labels: currentLabels.map(getInlineEntityId),
      reporter_id: getInlineEntityId(ticketDetails?.reporter || taskData?.reporter),
      type_id: getInlineEntityId(ticketDetails?.type || taskData?.taskType),
      ...(isScrum 
        ? { scrum_sprint_id: ticketDetails?.scrum_sprint_id || taskData?.scrum_sprint_id || null } 
        : { sprint_id: getInlineEntityId(ticketDetails?.sprint || taskData?.sprint) }
      ),
      priority_id: getInlineEntityId(ticketDetails?.priority || taskData?.priority),
      parent_ticket_id: getInlineEntityId(ticketDetails?.parent_ticket || ticketDetails?.parent_ticket_id || taskData?.parent_ticket || taskData?.parent_ticket_id),
      description: ticketDetails?.description || taskData?.description || '',
      acceptance_criteria: ticketDetails?.acceptance_criteria || taskData?.acceptance_criteria || '',
      is_recurring: Boolean(ticketDetails?.is_recurring ?? taskData?.is_recurring),
      recurrence_config: (ticketDetails?.is_recurring ?? taskData?.is_recurring)
        ? normalizeRecurrenceConfig(ticketDetails?.recurrence_config || taskData?.recurrence_config)
        : null
    };
    if (isScrum) {
      data.story_points = ticketDetails?.story_points || taskData?.story_points || null;
    } else {
      data.estimate_time_in_minutes = ticketDetails?.estimate_time_in_minutes || taskData?.estimate_time_in_minutes || 0;
    }
    return data;
  }, [ticketDetails, taskData, getInlineEntityId, normalizeInlineDate, isScrum]);

  const draftInlineInput = React.useMemo(() => ({
    title: editingTitleContent.trim(),
    localAssignees,
    localStatus,
    localStartDate,
    localDueDate,
    localLabels,
    localReporter,
    localType,
    localSprint,
    localPriority,
    localParentTicket,
    localEstimate,
    localStoryPoints,
    localIsRecurring,
    localRecurrenceConfig,
    description: localDescription
      ?? (isEditingMode && taskData?.id ? loadDraft(taskData.id, 'description') : null)
      ?? ticketDetails?.description
      ?? taskData?.description
      ?? '',
    acceptanceCriteria: localAcceptanceCriteria
      ?? (isEditingMode && taskData?.id ? loadDraft(taskData.id, 'acceptanceCriteria') : null)
      ?? ticketDetails?.acceptance_criteria
      ?? taskData?.acceptance_criteria
      ?? ''
  }), [
    editingTitleContent,
    isEditingMode,
    localAssignees,
    localStatus,
    localStartDate,
    localDueDate,
    localLabels,
    localReporter,
    localType,
    localSprint,
    localPriority,
    localParentTicket,
    localEstimate,
    localStoryPoints,
    localIsRecurring,
    localRecurrenceConfig,
    localDescription,
    localAcceptanceCriteria,
    ticketDetails?.description,
    ticketDetails?.acceptance_criteria,
    taskData?.id,
    taskData?.description,
    taskData?.acceptance_criteria
  ]);

  const getDraftInlineTaskData = useCallback(() => {
    const data = {
    title: draftInlineInput.title,
    assignee: draftInlineInput.localAssignees.map(getInlineEntityId),
    status_id: getInlineEntityId(draftInlineInput.localStatus),
    start_date: draftInlineInput.localStartDate,
    due_date: draftInlineInput.localDueDate,
    labels: draftInlineInput.localLabels,
    reporter_id: getInlineEntityId(draftInlineInput.localReporter),
    type_id: getInlineEntityId(draftInlineInput.localType),
    ...(isScrum ? { scrum_sprint_id: getInlineEntityId(draftInlineInput.localSprint) } : { sprint_id: getInlineEntityId(draftInlineInput.localSprint) }),
    priority_id: getInlineEntityId(draftInlineInput.localPriority),
    parent_ticket_id: draftInlineInput.localParentTicket?.isNone ? null : getInlineEntityId(draftInlineInput.localParentTicket),
    description: draftInlineInput.description,
    acceptance_criteria: draftInlineInput.acceptanceCriteria,
    is_recurring: draftInlineInput.localIsRecurring,
    recurrence_config: draftInlineInput.localIsRecurring
      ? normalizeRecurrenceConfig(draftInlineInput.localRecurrenceConfig)
      : null
  };
  if (isScrum) {
    data.story_points = draftInlineInput.localStoryPoints ? Number(draftInlineInput.localStoryPoints) : null;
  } else {
    data.estimate_time_in_minutes = timeStringToMinutes(draftInlineInput.localEstimate);
  }
  return data;
  }, [draftInlineInput, getInlineEntityId, isScrum]);

  const hasInlineTaskChanges = React.useMemo(() => {
    if (!isEditingMode || isInlineEditInitializing) return false;

    const currentTicketData = inlineEditBaselineRef.current || getCurrentInlineTaskData();
    const draftData = getDraftInlineTaskData();

    return hasPendingDescriptionAttachments || Object.keys(getChangedInlinePayload(draftData, currentTicketData)).length > 0;
  }, [isEditingMode, isInlineEditInitializing, hasPendingDescriptionAttachments, getCurrentInlineTaskData, getDraftInlineTaskData, getChangedInlinePayload]);

  const handleSaveAll = async () => {
    if (!taskData?.id) return;
    
    setIsSavingTaskChanges(true);
    try {
      // 1. Collect HTML data via refs
      const descHtml = descriptionRef.current?.getContent ? descriptionRef.current.getContent() : '';
      const acHtml = acceptanceCriteriaRef.current?.getContent ? acceptanceCriteriaRef.current.getContent() : '';
      const title = editingTitleContent.trim();
      const estimateError = validateTimeDigitLimits(localEstimate);
      if (!title) {
        toast.error(t('taskViewModal.updateTitleError'));
        return;
      }
      if (title.length > CHARACTER_LIMITS.TICKET_TITLE) {
        toast.error(t('taskModal.titleCannotExceed255Chars', 'Task title cannot exceed 255 characters'));
        return;
      }
      if (estimateError || (localEstimate && !isValidTimeFormat(localEstimate))) {
        toast.error(estimateError || t('taskModal.invalidEstimateFormat', "Use '1h 30m' format"));
        return;
      }
      if (!isRecurrenceComplete(localIsRecurring, localRecurrenceConfig)) {
        toast.error('Please select both a recurrence frequency and repeat value.');
        return;
      }

      const currentAttachments = ticketDetails?.attachments || taskData?.attachments || [];
      const pendingDescriptionAttachments = descriptionRef.current?.getAttachments?.() || [];
      if (currentAttachments.length + pendingDescriptionAttachments.length > CHARACTER_LIMITS.MAX_ATTACHMENTS) {
        toast.error(t('taskModal.attachmentLimitExceededOnSave'));
        return;
      }

      const descriptionAttachments = await uploadPendingDescriptionAttachments(pendingDescriptionAttachments);
      const savedAttachments = [
        ...currentAttachments,
        ...descriptionAttachments
      ];

      const draftData = {
        title,
        assignee: localAssignees.map(getInlineEntityId),
        status_id: getInlineEntityId(localStatus),
        start_date: localStartDate,
        due_date: localDueDate,
        labels: localLabels,
        reporter_id: getInlineEntityId(localReporter),
        type_id: getInlineEntityId(localType),
        ...(isScrum ? { scrum_sprint_id: getInlineEntityId(localSprint) } : { sprint_id: getInlineEntityId(localSprint) }),
        priority_id: getInlineEntityId(localPriority),
        parent_ticket_id: localParentTicket?.isNone ? null : getInlineEntityId(localParentTicket),
        description: descHtml,
        acceptance_criteria: acHtml,
        is_recurring: localIsRecurring,
        recurrence_config: localIsRecurring ? normalizeRecurrenceConfig(localRecurrenceConfig) : null
      };
      
      if (isScrum) {
        if (localStoryPoints !== '' && localStoryPoints !== null) {
          const sp = Number(localStoryPoints);
          if (isNaN(sp) || sp < 1 || sp > 40) {
            toast.error(t('taskModal.storyPointsInvalid'));
            setIsSavingTaskChanges(false);
            return;
          }
        }
        draftData.story_points = localStoryPoints ? Number(localStoryPoints) : null;
        
        if (draftData.scrum_sprint_id && !draftData.story_points) {
          toast.error(t('taskModal.storyPointsRequiredForSprint'));
          setIsSavingTaskChanges(false);
          return;
        }
      } else {
        draftData.estimate_time_in_minutes = timeStringToMinutes(localEstimate) || null;
      }

      // 2. Compare against the edit-mode baseline and create a payload with only changed fields.
      const changedPayload = getChangedInlinePayload(draftData, inlineEditBaselineRef.current || getCurrentInlineTaskData());
      if (descriptionAttachments.length > 0) {
        changedPayload.attachment_operations = { add: descriptionAttachments };
      }

      // 
      if (Object.keys(changedPayload).length === 0) {
        clearDraftFields(taskData.id, ['description', 'acceptanceCriteria']);
        setDatePickerAnchorEl(null);
        setIsEditingMode(false);
        return;
      }

      // 3. Save to backend
      const updatedTicket = await dispatch(updateTicketPartial({
        ticketId: taskData.id, 
        ticketData: changedPayload 
      })).unwrap();

      // 4. Clear auto-save drafts after successful save
      clearDraftFields(taskData.id, ['description', 'acceptanceCriteria']);
      descriptionRef.current?.setAttachments?.([]);
      setHasPendingDescriptionAttachments(false);

      dispatch(updateTicketTitle({ ticketId: taskData.id, title }));
      dispatch(updateTicketAssignee({ ticketId: taskData.id, assignees: localAssignees }));
      dispatch(updateTicketStatus({
        ticketId: taskData.id,
        oldStatus: ticketDetails?.status || taskData?.status || null,
        newStatus: localStatus
      }));
      dispatch(updateCalendarTicket({
        ticketId: taskData.id,
        updates: {
          title,
          assignee: localAssignees[0] || null,
          assignees: localAssignees,
          status: localStatus,
          start_date: localStartDate,
          due_date: localDueDate,
          task_type: localType,
          priority: localPriority
        }
      }));
      if (onTaskUpdate) {
        onTaskUpdate(taskData.id, {
          title,
          assignees: localAssignees,
          assignee: localAssignees[0] || null,
          status: localStatus,
          start_date: localStartDate,
          due_date: localDueDate,
          labels: localLabels,
          reporter: localReporter,
          type: localType,
          sprint: localSprint,
          priority: localPriority,
          parent_ticket_id: draftData.parent_ticket_id,
          estimate_time_in_minutes: draftData.estimate_time_in_minutes,
          description: descHtml,
          acceptance_criteria: acHtml,
          attachments: updatedTicket?.attachments || savedAttachments,
          is_recurring: draftData.is_recurring,
          recurrence_config: draftData.recurrence_config
        });
      }

      setEditingTitleContent(title);
      setLocalDescription(descHtml);
      setLocalAcceptanceCriteria(acHtml);
      setHasPendingDescriptionAttachments(false);
      inlineEditBaselineRef.current = draftData;
      setDatePickerAnchorEl(null);
      setIsEditingMode(false);
      toast.success(t('taskViewModal.updatedSuccessfully', 'Task updated successfully'));
    } catch (error) {
      if (error?.isHandled) return;
      console.error('Failed to save task changes:', error);
      const fallbackMsg = t('taskViewModal.saveError', 'Failed to save changes');
      const errorMessage = (typeof error === 'string' ? error : null) || error?.error || error?.message || error?.payload?.error || error?.payload || fallbackMsg;
      toast.error(typeof errorMessage === 'string' ? errorMessage : fallbackMsg);
    } finally {
      setIsSavingTaskChanges(false);
    }
  };


  const handleClose = () => {
    resetState();
    if (onClose) onClose();
  };

  // eslint-disable-next-line no-unused-vars
  const handleOpenCommentMenu = (event, commentId) => {
    setCommentMenuAnchorEl(event.currentTarget);
    setCommentMenuCommentId(commentId);
  };

  const handleCloseCommentMenu = () => {
    setCommentMenuAnchorEl(null);
    setCommentMenuCommentId(null);
  };

  const handleActivateCommentDraft = useCallback((activeFieldName) => {
    if (!taskData?.id) return;

    clearOtherCommentDrafts(taskData.id, activeFieldName);

    if (activeFieldName === 'comment') {
      setEditingCommentId(null);
      setEditingCommentContent('');
      setReplyingTarget(null);
      return;
    }

    if (isCommentDraftField(activeFieldName) && activeFieldName !== 'comment') {
      clearDraft(taskData.id, 'comment');
      newCommentBoxRef.current?.clearEditor?.();
      if (activeFieldName.startsWith('comment_reply_')) {
        setEditingCommentId(null);
        setEditingCommentContent('');
      } else {
        setReplyingTarget(null);
      }
    }
  }, [taskData?.id]);

  const handleEditComment = (id = null) => {
    const targetId = id || commentMenuCommentId;
    const target = findCommentById(localComments, targetId);
    if (target) {
      if (taskData?.id) {
        clearOtherCommentDrafts(taskData.id, getCommentEditDraftFieldName(target.id));
      }
      newCommentBoxRef.current?.clearEditor?.();
      setReplyingTarget(null);
      setEditingCommentId(target.id);
      setEditingCommentContent(target.content || '');
    }
    handleCloseCommentMenu();
  };

  const handleDeleteComment = (id = null) => {
    const targetId = id || commentMenuCommentId;
    if (!targetId) return;
    setCommentToDelete(targetId);
    setIsDeleteCommentDialogOpen(true);
    handleCloseCommentMenu();
  };

  const handleConfirmDeleteComment = async () => {
    if (!commentToDelete || isDeletingComment) return;

    setIsDeletingComment(true);
    try {
      await dispatch(deleteComment(commentToDelete)).unwrap();
      setLocalComments((prev) => removeCommentFromTree(prev, commentToDelete));
      setIsDeleteCommentDialogOpen(false);
      setCommentToDelete(null);
    } catch (error) {
      console.error('Failed to delete comment:', error);
    } finally {
      setIsDeletingComment(false);
    }
  };

  const handleDeleteChecklistAction = () => {
    if (!hasChecklistItems) {
      setChecklistMenuAnchor(null);
      return;
    }
    setIsDeleteChecklistDialogOpen(true);
    setChecklistMenuAnchor(null);
  };

  const handleConfirmDeleteChecklist = () => {
    if (!hasChecklistItems) {
      setIsDeleteChecklistDialogOpen(false);
      return;
    }
    if (checklistRef.current) {
      // Expose a clearAll method in ChecklistManager if needed, 
      // or just call update via parent if manageable. 
      // We'll update the ref method.
      checklistRef.current.clearAllChecklists();
    }
    setIsDeleteChecklistDialogOpen(false);
  };

  const handleCancelCommentEdit = () => {
    // Clear the draft for the comment being edited
    if (editingCommentId && taskData?.id) {
      clearDraft(taskData.id, getCommentEditDraftFieldName(editingCommentId));
    }
    setEditingCommentId(null);
    setEditingCommentContent('');
  };

  const getCurrentUserDisplay = useCallback(() => {
    return getCurrentUserDisplayUtil(user, t);
  }, [user, t]);

  const handleStartReply = (commentId) => {
    const replyFieldName = getCommentReplyDraftFieldName(commentId, null);
    setReplyingTarget((prev) => {
      if (prev && prev.commentId === commentId && !prev.parentReplyId) {
        if (taskData?.id) {
          clearDraft(taskData.id, replyFieldName);
        }
        return null;
      }
      return { commentId, parentReplyId: null };
    });
    if (taskData?.id) {
      clearOtherCommentDrafts(taskData.id, replyFieldName);
    }
    newCommentBoxRef.current?.clearEditor?.();
    setEditingCommentId(null);
    setEditingCommentContent('');
  };

  const handleCancelReply = () => {
    if (replyingTarget && taskData?.id) {
      clearDraft(
        taskData.id,
        getCommentReplyDraftFieldName(replyingTarget.commentId, replyingTarget.parentReplyId)
      );
    }
    setReplyingTarget(null);
  };

  const isReplyThreadOpen = useCallback((commentId) => {
    return isReplyThreadOpenUtil(commentId, openReplyThreads);
  }, [openReplyThreads]);

  const toggleReplyThread = (commentId) => {
    setOpenReplyThreads((prev) => toggleReplyThreadUtil(commentId, prev));
  };

  const getReplyCountLabel = (count) => {
    return getReplyCountLabelUtil(count, t);
  };

  const isCommentOwnedByUser = useCallback((commentAuthorName) => {
    return isCommentOwnedByUserUtil(commentAuthorName, user);
  }, [user]);

  const getReplyAuthors = (replies = []) => {
    return getReplyAuthorsUtil(replies, COMMENT_REPLY.AVATAR_STACK_MAX, t);
  };

  const handleSubmitReply = async (commentId, parentReplyId, content) => {
    const trimmedContent = trimEdgeHtml(content || '').trim();
    if (!trimmedContent) return;
    const author = getCurrentUserDisplay();
    const fallbackReply = {
      id: Date.now(),
      author,
      content: typeof trimmedContent === 'string' ? trimmedContent : '',
      timestamp: t('taskViewModal.justNow'),
      attachments: [],
      replies: []
    };
    try {
      const created = await dispatch(createTicketComment({
        ticketId: taskData?.id,
        comment: trimmedContent,
        attachments: [],
        parentCommentId: parentReplyId || commentId
      })).unwrap();
      if (taskData?.id) {
        dispatch(fetchTicketComments(taskData.id));
      }
      const apiReply = created ? mapApiComment(created) : null;
      const replyToInsert = apiReply ? {
        ...apiReply,
        timestamp: apiReply.timestamp && apiReply.timestamp !== 'Invalid Date'
          ? apiReply.timestamp
          : t('taskViewModal.justNow'),
        content: apiReply.content || fallbackReply.content,
        attachments: Array.isArray(apiReply.attachments) ? apiReply.attachments : [],
        replies: apiReply.replies || []
      } : fallbackReply;
      setLocalComments((prev) => prev.map((comment) => {
        if (comment.id !== commentId) return comment;
        const result = insertReplyIntoTree(comment.replies || [], parentReplyId, replyToInsert);
        return { ...comment, replies: result.replies };
      }));
      if (taskData?.id) {
        clearDraft(taskData.id, getCommentReplyDraftFieldName(commentId, parentReplyId));
      }
      setOpenReplyThreads((prev) => (prev.includes(commentId) ? prev : [...prev, commentId]));
      setReplyingTarget(null);
    } catch (error) {
      return;
    }
  };

  const handleStartNestedReply = (commentId, parentReplyId) => {
    if (taskData?.id) {
      clearOtherCommentDrafts(taskData.id, getCommentReplyDraftFieldName(commentId, parentReplyId));
    }
    newCommentBoxRef.current?.clearEditor?.();
    setReplyingTarget({ commentId, parentReplyId });
    setEditingCommentId(null);
    setEditingCommentContent('');
  };

  const renderReplyNodes = (replies, commentId, depth) => {
    if (!replies || replies.length === 0) return null;
    return (
      <div className="comment-replies">
        {replies.map((reply) => (
          <div
            key={reply.id}
            id={`comment-${reply.id}`}
            className={`comment-reply comment-reply-level-${depth} comment-focused-anchor`}
          >
            <div className="comment-reply-avatar" style={{ background: stringToColor(reply.author?.name || '') }}>
              {reply.author?.avatar || reply.author?.name?.charAt(0) || t('taskViewModal.unknownUserInitial')}
            </div>
            <div className="comment-reply-body">
              <div className="comment-reply-meta">
                <div className="comment-reply-meta-left">
                  <span className="comment-reply-author">
                    {reply.author?.name || t('taskViewModal.unknownUser')}
                  </span>
                  <span className="comment-reply-timestamp">
                    {reply.timestamp || t('taskViewModal.unknownTime')}
                  </span>
                </div>
                <div className="comment-action-bar">
                  {depth < 3 && (
                    <Tooltip title={t('taskViewModal.reply')} placement="top" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                      <IconButton
                        size="small"
                        onClick={() => handleStartNestedReply(commentId, reply.id)}
                        className="comment-action-button comment-action-button--reply"
                        aria-label={t('taskViewModal.reply')}
                      >
                        <Reply className="comment-action-icon" />
                        </IconButton>
                      </Tooltip>
                    )}
                  <Tooltip title={t('common.copyLink', 'Copy Link')} placement="top" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                    <IconButton
                      size="small"
                                      onClick={() => handleCopyCommentLinkMemoized(reply.id)}
                      className="comment-action-button"
                      aria-label={t('common.copyLink', 'Copy Link')}
                    >
                      <ContentCopy className="comment-action-icon" />
                    </IconButton>
                  </Tooltip>
                  {user && isCommentOwnedByUser(reply.author?.name) && (
                    <>
                      <Tooltip title={t('common.edit')} placement="top" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditComment(reply.id)}
                          className="comment-action-button"
                          aria-label={t('common.edit')}
                        >
                          <img src={ICONS.edit} alt={t('common.edit')} style={{ width: 14, height: 14 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete')} placement="top" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteComment(reply.id)}
                          className="comment-action-button"
                          aria-label={t('common.delete')}
                        >
                          <img src={ICONS.delete} alt={t('common.delete')} style={{ width: 14, height: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </div>
              </div>
              {editingCommentId === reply.id ? (
                <div className="comment-reply-editor">
                  <CommentBox
                    onSubmit={async (content, attachments) => {
                      const trimmedContent = trimEdgeHtml(content || '').trim();
                      if (!trimmedContent && attachments.length === 0) return;
                      try {
                        await dispatch(updateComment({
                          commentId: editingCommentId,
                          comment: trimmedContent,
                          attachments
                        })).unwrap();
                        setLocalComments((prev) => updateCommentInTree(prev, editingCommentId, (c) => (
                          { ...c, content: trimmedContent, attachments }
                        )));
                        
                        // Clear draft after successful update
                        if (taskData?.id) {
                          clearDraft(taskData.id, getCommentEditDraftFieldName(editingCommentId));
                        }
                        setEditingCommentId(null);
                        setEditingCommentContent('');
                      } catch (error) {
                        console.error('Failed to update comment:', error);
                      }
                    }}
                    onCancel={handleCancelCommentEdit}
                    placeholder={t('taskViewModal.editYourComment')}
                    initialContent={editingCommentContent}
                    initialAttachments={findCommentById(localComments, editingCommentId)?.attachments || []}
                    mentionUsers={mentionUsers}
                    ticketId={taskData?.id}
                    fieldName={getCommentEditDraftFieldName(editingCommentId)}
                    enableAutoSave={true}
                    onAutoSaveActivate={handleActivateCommentDraft}
                  />
                </div>
              ) : (
                <div className="comment-content comment-reply-content">
                  <SafeCommentRenderer content={reply.content || ''} mentionUsers={mentionUsers} />
                </div>
              )}
              {reply.replies?.length > 0 && depth < 3 && (
                <div className="comment-replies-toggle">
                  <div className="comment-replies-summary">
                    <AssigneeAvatarStack
                      assignees={getReplyAuthors(reply.replies)}
                      size={COMMENT_REPLY.AVATAR_SIZE}
                      max={COMMENT_REPLY.AVATAR_STACK_MAX}
                    />
                    <button
                      type="button"
                      className="comment-replies-toggle-button"
                      onClick={() => toggleReplyThread(reply.id)}
                    >
                      {getReplyCountLabel(reply.replies.length)}
                    </button>
                  </div>
                </div>
              )}
              {replyingTarget?.commentId === commentId && replyingTarget?.parentReplyId === reply.id && (
                <div className="comment-reply-editor">
                  <CommentBox
                    onSubmit={(content) => handleSubmitReply(commentId, reply.id, content)}
                    onCancel={handleCancelReply}
                    hideAttachments
                    mentionUsers={mentionUsers}
                    ticketId={taskData?.id}
                    fieldName={getCommentReplyDraftFieldName(commentId, reply.id)}
                    enableAutoSave={true}
                    onAutoSaveActivate={handleActivateCommentDraft}
                  />
                </div>
              )}
              {depth < 3 && isReplyThreadOpen(reply.id) && renderReplyNodes(reply.replies, commentId, depth + 1)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderReplies = (comment) => {
    if (!comment?.replies?.length) return null;
    if (!isReplyThreadOpen(comment.id)) return null;
    return renderReplyNodes(comment.replies, comment.id, 1);
  };

  // Initialize title content when component loads
  React.useEffect(() => {
    setEditingTitleContent(ticketDetails?.title || taskData?.title || '');
  }, [ticketDetails?.title, taskData?.title]);

  const handleUpdateViewTaskType = async () => {
    if (editingViewTaskType?.newName?.trim() && taskData?.id) {
      try {
        await dispatch(updateTicketDropdown({
          id: editingViewTaskType.id,
          name: editingViewTaskType.newName.trim(),
          category: 'type'
        })).unwrap();
        dispatch(fetchTicketDropdowns(ticketDetails?.project?.id || taskData?.project_id || taskData?.project));
      } catch (error) {
        toast.error(error?.message || t('taskModal.taskTypeUpdateError', 'Failed to update Task Type'));
      } finally {
        setEditingViewTaskType(null);
      }
    }
  };

  const handleDeleteViewTaskType = (typeItem) => {
    setOptionToDelete(typeItem);
    setDeleteOptionCategory('type');
    setDeleteOptionDialogOpen(true);
  };

  const handleCancelDeleteOption = () => {
    setDeleteOptionDialogOpen(false);
    setOptionToDelete(null);
    setDeleteOptionCategory('');
  };

  const handleConfirmDeleteOption = async () => {
    if (!optionToDelete || !deleteOptionCategory) return;
    try {
      if (deleteOptionCategory === 'type') {
        await dispatch(deleteTicketDropdown({ id: optionToDelete.id, category: 'type' })).unwrap();
        dispatch(fetchTicketDropdowns(ticketDetails?.project?.id || taskData?.project_id || taskData?.project));
        if ((localType?.id || localType) === optionToDelete.id) {
          setLocalType(null);
        }
      } else if (deleteOptionCategory === 'priority') {
        await dispatch(deleteTicketDropdown({ id: optionToDelete.id, category: 'priority' })).unwrap();
        dispatch(fetchTicketDropdowns(ticketDetails?.project?.id || taskData?.project_id || taskData?.project));
      } else if (deleteOptionCategory === 'labels') {
        await dispatch(deleteTicketDropdown({ id: optionToDelete.id, category: 'labels' })).unwrap();
        dispatch(fetchTicketDropdowns(ticketDetails?.project?.id || taskData?.project_id || taskData?.project));
        setLocalLabels(prev => prev.filter(id => id !== optionToDelete.id));
      } else if (deleteOptionCategory === 'sprint') {
        await dispatch(deleteTicketDropdown({ id: optionToDelete.id, category: 'sprint' })).unwrap();
        dispatch(fetchTicketDropdowns(ticketDetails?.project?.id || taskData?.project_id || taskData?.project));
        if (localSprint?.id === optionToDelete.id) {
          setLocalSprint(null);
        }
      }
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.TICKET_ATTRIBUTE_DELETE_FAILED;
      toast.error(errorMessage);
      throw error;
    } finally {
      setDeleteOptionDialogOpen(false);
      setOptionToDelete(null);
      setDeleteOptionCategory('');
    }
  };

  const getDeleteOptionTitle = () => {
    if (deleteOptionCategory === 'type') return t('taskModal.deleteTaskType');
    if (deleteOptionCategory === 'priority') return t('taskModal.deletePriority');
    if (deleteOptionCategory === 'labels') return t('taskModal.deleteLabel');
    if (deleteOptionCategory === 'sprint') return t('taskModal.deleteSprint');
    return '';
  };

  const getDeleteOptionQuestion = () => {
    if (deleteOptionCategory === 'type') return t('taskModal.confirmDeleteTaskType');
    if (deleteOptionCategory === 'priority') return t('taskModal.confirmDeletePriority');
    if (deleteOptionCategory === 'labels') return t('taskModal.confirmDeleteLabel');
    if (deleteOptionCategory === 'sprint') return t('taskModal.confirmDeleteSprint');
    return '';
  };

  const handleUpdateViewPriority = async () => {
    if (editingViewPriority?.newName?.trim() && taskData?.id) {
      try {
        await dispatch(updateTicketDropdown({
          id: editingViewPriority.id,
          name: editingViewPriority.newName.trim(),
          colour: editingViewPriority.newColor,
          category: 'priority'
        })).unwrap();
        dispatch(fetchTicketDropdowns(ticketDetails?.project?.id || taskData?.project_id || taskData?.project));
      } catch (error) {
        toast.error(error?.message || t('taskModal.priorityUpdateError', 'Failed to update Priority'));
      } finally {
        setEditingViewPriority(null);
      }
    }
  };

  const handleDeleteViewPriority = (priorityItem) => {
    setOptionToDelete(priorityItem);
    setDeleteOptionCategory('priority');
    setDeleteOptionDialogOpen(true);
  };

  const handleUpdateViewLabel = async () => {
    if (editingViewLabel?.newName?.trim() && taskData?.id) {
      try {
        await dispatch(updateTicketDropdown({
          id: editingViewLabel.id,
          name: editingViewLabel.newName.trim(),
          colour: editingViewLabel.newColor,
          category: 'labels'
        })).unwrap();
        dispatch(fetchTicketDropdowns(ticketDetails?.project?.id || taskData?.project_id || taskData?.project));
      } catch (error) {
        toast.error(error?.message || t('taskModal.labelUpdateError', 'Failed to update Label'));
      } finally {
        setEditingViewLabel(null);
      }
    }
  };

  const handleDeleteViewLabel = (labelItem) => {
    setOptionToDelete(labelItem);
    setDeleteOptionCategory('labels');
    setDeleteOptionDialogOpen(true);
  };

  const handleUpdateViewSprint = async () => {
    if (editingViewSprint?.newName?.trim() && taskData?.id) {
      try {
        await dispatch(updateTicketDropdown({
          id: editingViewSprint.id,
          name: editingViewSprint.newName.trim(),
          colour: editingViewSprint.newColor,
          category: 'sprint'
        })).unwrap();
        dispatch(fetchTicketDropdowns(ticketDetails?.project?.id || taskData?.project_id || taskData?.project));
      } catch (error) {
        toast.error(error?.message || t('taskModal.sprintUpdateError', 'Failed to update Sprint'));
      } finally {
        setEditingViewSprint(null);
      }
    }
  };

  const handleDeleteViewSprint = (sprintItem) => {
    setOptionToDelete(sprintItem);
    setDeleteOptionCategory('sprint');
    setDeleteOptionDialogOpen(true);
  };

  // Helper function to clean undefined filters
  const cleanFilters = React.useCallback((filtersObj) => {
    return Object.fromEntries(
      Object.entries(filtersObj).filter(([_, v]) => v !== undefined)
    );
  }, []);

  // Helper function to format current filters
  const getCurrentFilters = React.useCallback(() => ({
      search: filters.searchTerm || '',
      priority: filters.selectedPriority !== FILTER_VALUES.ALL ? filters.selectedPriority : undefined,
      status: filters.selectedStatus !== FILTER_VALUES.ALL ? filters.selectedStatus : undefined,
      assignee: filters.selectedAssignee !== FILTER_VALUES.ALL ? filters.selectedAssignee : undefined,
      taskType: filters.selectedTaskType !== FILTER_VALUES.ALL ? filters.selectedTaskType : undefined,
      labels: filters.selectedLabels && filters.selectedLabels.length > 0 && !filters.selectedLabels.includes(FILTER_VALUES.ALL) ? filters.selectedLabels : undefined
  }), [filters]);

  const handleAddComment = async (content, attachments = []) => {
    const trimmedContent = trimEdgeHtml(content || '').trim();
    if (!trimmedContent && attachments.length === 0) return;
    if (!taskData?.id) return;
    
    try {
      // Use standard API for both modes
      const created = await dispatch(createTicketComment({ 
        ticketId: taskData.id, 
        comment: trimmedContent,
        attachments,
        parentCommentId: null
      })).unwrap();
      
      // Refresh comments using standard API
      dispatch(fetchTicketComments(taskData.id));
      
      // Add comment to local state for immediate UI update
      const apiComment = created ? mapApiComment(created) : null;
      const userDisplay = getCurrentUserDisplay();
      setLocalComments((prev) => [
        apiComment ? {
          ...apiComment,
          timestamp: apiComment.timestamp && apiComment.timestamp !== 'Invalid Date'
            ? apiComment.timestamp
            : t('taskViewModal.justNow'),
          content: apiComment.content || (typeof trimmedContent === 'string' ? trimmedContent : ''),
          author: apiComment.author || userDisplay,
          attachments: Array.isArray(apiComment.attachments) ? apiComment.attachments : (attachments || []),
          replies: apiComment.replies || []
        } : {
          id: Date.now(),
          author: userDisplay,
          content: typeof trimmedContent === 'string' ? trimmedContent : '',
          timestamp: t('taskViewModal.justNow'),
          attachments: attachments || [],
          replies: []
        },
        ...prev
      ]);
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const handleOpenAddSubtask = (parentId) => {
    const projectStatus = activeProject?.status || ticketDetails?.project?.status || taskData?.project?.status;
    
    if (
      projectStatus === PROJECT_STATUS.ON_HOLD ||
      projectStatus === PROJECT_STATUS.INACTIVE ||
      projectStatus === PROJECT_STATUS.COMPLETED
    ) {
      pendingProjectReactivationActionRef.current = null;
      setProjectStatusModalContent(null);
      setProjectStatusForModal(projectStatus);
      setIsProjectStatusModalOpen(true);
      return;
    }
    setAddingSubtaskParentId(parentId);
    setIsAddModalOpen(true);
  };

  const handleActivateProject = async () => {
    const pendingAction = pendingProjectReactivationActionRef.current;
    const projectId = pendingAction?.projectId || activeProject?.id || ticketDetails?.project?.id || activeProjectId;
    const previousStatus = projectStatusForModal;
    if (!projectId) return;
    
    setActivatingProject(true);
    dispatch(setProjectDropdownStatus({ projectId, status: PROJECT_STATUS.ACTIVE }));
    
    try {
      await dispatch(updateProject({ 
        id: projectId, 
        data: { status: PROJECT_STATUS.ACTIVE } 
      })).unwrap();
      
      toast.success(t('projects.projectUpdatedSuccess'));
      closeProjectStatusModal();
      if (pendingAction?.onConfirm) {
        await pendingAction.onConfirm();
      }
    } catch (error) {
      if (previousStatus) {
        dispatch(setProjectDropdownStatus({ projectId, status: previousStatus }));
      }
      toast.error(t('projects.projectUpdateFailed'));
    } finally {
      setActivatingProject(false);
    }
  };

  const handleCreateSubtask = async (data) => {
    try {
      await dispatch(createTicket(data)).unwrap();
      setIsAddModalOpen(false);
      
      // Refresh background data after modal is dismissed
      if (taskData?.id && !ticketDetailsLoading) {
        dispatch(fetchTicketDetails(taskData.id));
      }
      dispatch(markSubtaskUpdated());
      if (!isGanttContext && data.project) {
        dispatch(fetchTicketsByProject({
          projectId: data.project,
          filters: cleanFilters(getCurrentFilters())
        }));
      }
    } catch (error) {
      console.error('Failed to create subtask:', error);
    }
  };

  const openEditSubtaskModal = (subtask) => {
    setSelectedSubtask(subtask);
    setIsEditModalOpen(true);
  };

  const handleUpdateSubtask = async (data) => {
    try {
      if (!data.id) {
        console.error('Subtask ID is missing');
        return;
      }
      await dispatch(updateTicket({ ticketId: data.id, ticketData: data })).unwrap();
      setIsEditModalOpen(false);
      setSelectedSubtask(null);
      
      // Refresh background data after modal is dismissed
      if (taskData?.id && !ticketDetailsLoading) {
        dispatch(fetchTicketDetails(taskData.id));
      }
      dispatch(markSubtaskUpdated());
    } catch (error) {
      console.error('Failed to update subtask:', error);
    }
  };

  const handleInlineSubtaskUpdate = useCallback((taskId, updates) => {
    if (!taskId || !updates) return;

    const applyUpdatesRecursively = (items) => items.map((item) => {
      if (String(item.id) === String(taskId)) {
        return { ...item, ...updates };
      }

      if (Array.isArray(item.subtasks) && item.subtasks.length > 0) {
        return {
          ...item,
          subtasks: applyUpdatesRecursively(item.subtasks)
        };
      }

      return item;
    });

    setLocalSubtasks(prev => applyUpdatesRecursively(prev));
  }, []);

  const openDeleteSubtaskModal = (subtask) => {
    setSelectedSubtask(subtask);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteSubtask = async (data) => {
    try {
      if (!data.id) {
        console.error('Subtask ID is missing');
        return;
      }
      await dispatch(deleteTicket(data.id)).unwrap();
      setIsDeleteModalOpen(false);
      setSelectedSubtask(null);

      // Refresh ticket details to get updated subtasks
      if (taskData?.id && !ticketDetailsLoading) {
        await dispatch(fetchTicketDetails(taskData.id)).unwrap();
      }
      dispatch(markSubtaskUpdated());
      // Refresh dashboard with current filters
      const projectId = ticketDetails?.project?.id || taskData?.project_id || (typeof taskData?.project === 'object' ? taskData?.project?.id : null);
      if (!isGanttContext && projectId) {
        await dispatch(fetchTicketsByProject({ 
          projectId: projectId,
          filters: cleanFilters(getCurrentFilters())
        })).unwrap();
      }
    } catch (error) {
      console.error('Failed to delete subtask:', error);
    }
  };

  // WorkLog event handlers
  const handleCreateWorklog = async (worklogData) => {
    if (!taskData?.id) return;
    try {
      await dispatch(createWorklog({ ticketId: taskData.id, worklogData })).unwrap();
      dispatch(fetchWorklogSummary(taskData.id));
    } catch (error) {
      console.error('Failed to create worklog:', error);
    }
  };

  const handleUpdateWorklog = async (worklogId, worklogData) => {
    try {
      await dispatch(updateWorklog({ worklogId, worklogData })).unwrap();
      dispatch(fetchWorklogSummary(taskData.id));
    } catch (error) {
      console.error('Failed to update worklog:', error);
    }
  };

  const handleDeleteWorklog = async (worklogId) => {
    try {
      await dispatch(deleteWorklog(worklogId)).unwrap();
      dispatch(fetchWorklogSummary(taskData.id));
    } catch (error) {
      console.error('Failed to delete worklog:', error);
    }
  };

  const handleChecklistUpdate = React.useCallback(async (checklist, changeMeta = {}) => {
    if (!taskData?.id) return;
    try {
      const latestResponse = await api.get(ENDPOINTS.TICKETS_DETAILS(taskData.id));
      const latestTicket = latestResponse.data?.data || {};
      const mergedChecklist = mergeChecklistChange(latestTicket?.checklist || [], checklist, changeMeta);

      await dispatch(updateTicketPartial({ 
        ticketId: taskData.id, 
        ticketData: { checklist: mergedChecklist } 
      })).unwrap();
    } catch (error) {
      console.error('Failed to update checklist:', error);
    }
  }, [taskData?.id, dispatch]);

  const handleViewCreateTaskType = async () => {
    if (!newViewTaskTypeName.trim() || !ticketDetails?.project?.id || isCreatingTaskTypeSubmitting) return;
    setIsCreatingTaskTypeSubmitting(true);
    try {
      await dispatch(createTicketType({ name: newViewTaskTypeName, projectId: ticketDetails.project.id })).unwrap();
    } catch (error) { toast.error(error?.message || 'Failed to create task type'); }
    finally { setIsCreatingViewTaskType(false); setNewViewTaskTypeName(''); setIsCreatingTaskTypeSubmitting(false); }
  };

  const handleViewCreateSprint = async () => {
    if (!newViewSprintName.trim() || !ticketDetails?.project?.id || isCreatingSprintSubmitting) return;
    setIsCreatingSprintSubmitting(true);
    try {
      const result = await dispatch(createTicketSprint({ name: newViewSprintName, colour: newViewSprintColor, projectId: ticketDetails.project.id })).unwrap();
      if (result?.id && taskData?.id) await dispatch(updateTicketPartial({ ticketId: taskData.id, ticketData: { sprint_id: result.id } })).unwrap();
    } catch (error) { toast.error(error?.message || 'Failed to create sprint'); }
    finally { setIsCreatingViewSprint(false); setNewViewSprintName(''); setNewViewSprintColor(COLORS.primary); setIsCreatingSprintSubmitting(false); }
  };

  const handleViewCreatePriority = async () => {
    if (!newViewPriorityName.trim() || !ticketDetails?.project?.id || isCreatingPrioritySubmitting) return;
    setIsCreatingPrioritySubmitting(true);
    try {
      await dispatch(createTicketPriority({ name: newViewPriorityName, colour: newViewPriorityColor, projectId: ticketDetails.project.id })).unwrap();
    } catch (error) { toast.error(error?.message || 'Failed to create priority'); }
    finally { setIsCreatingViewPriority(false); setNewViewPriorityName(''); setNewViewPriorityColor(COLORS.primary); setIsCreatingPrioritySubmitting(false); }
  };

  const handleViewCreateLabel = async () => {
    if (!newViewLabelName.trim() || !ticketDetails?.project?.id || isCreatingLabelSubmitting) return;
    setIsCreatingLabelSubmitting(true);
    try {
      const result = await dispatch(createTicketLabel({ name: newViewLabelName, colour: newViewLabelColor, projectId: ticketDetails.project.id })).unwrap();
      if (result?.id) {
        const updated = [...localLabels, result.id];
        setLocalLabels(updated);
        if (taskData?.id) await dispatch(updateTicketPartial({ ticketId: taskData.id, ticketData: { labels: updated } })).unwrap();
      }
    } catch (error) { toast.error(error?.message || 'Failed to create label'); }
    finally { setIsCreatingViewLabel(false); setNewViewLabelName(''); setNewViewLabelColor(COLORS.primary); setIsCreatingLabelSubmitting(false); }
  };

  const handleCopyTaskLinkMemoized = useCallback(() => {
    const task = {
      id: taskData?.id,
      title: ticketDetails?.title || taskData?.title || 'Task',
      code: formatTaskCode(taskData, ticketDetails)
    };
    handleCopyTaskLink(task, t);
  }, [taskData, ticketDetails, t]);

  const handleCopyCommentLinkMemoized = useCallback((commentId) => {
    const task = {
      id: taskData?.id,
      title: ticketDetails?.title || taskData?.title || 'Task',
      code: formatTaskCode(taskData, ticketDetails)
    };

    handleCopyCommentLink(task, t, commentId);
  }, [taskData, ticketDetails, t]);

  // Helper function to check if AI Developer tab should be shown
  const shouldShowAIDeveloperTab = () => {
    const projectSettings = ticketDetails?.project?.project_settings;
    return projectSettings?.status === PROJECT_AI_SETTING_STATUS.ACTIVE;
  };

  if (!taskData) {
    return null;
  }

  // Ensure we have at least some basic data to work with
  if (!taskData.id && !taskData.code) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          {t('taskViewModal.invalidTaskData')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('taskViewModal.missingTicketId')}
        </Typography>
      </Box>
    );
  }

  // Check if we have an ID but it's not in UUID format
  if (taskData.code && !taskData.id) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          {t('taskViewModal.missingTicketIdTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('taskViewModal.ticketCode')} {taskData.code}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('taskViewModal.requiresUuidId')}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {t('taskViewModal.ensureCompleteTaskData')}
        </Typography>
      </Box>
    );
  }
  const getAvatarInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ').filter(Boolean);
    return parts.slice(0, 2).map(part => part[0]?.toUpperCase()).join('') || name[0]?.toUpperCase() || '?';
  };

  // eslint-disable-next-line no-unused-vars
  const getStatusColor = (name) => {
    switch (name) {
      case 'Open': return COLORS.status.open;
      case 'In Progress': return COLORS.status.inProgress;
      case 'Review': return COLORS.status.review;
      case 'Done': return COLORS.status.done;
      case 'Closed': return COLORS.status.closed;
      case 'Blocked': return COLORS.status.blocked;
      default: return stringToColor(name || '');
    }
  };

  const formatDateRange = (startDateStr, endDateStr, startTimeStr, endTimeStr) => {
    if (!startDateStr && !endDateStr) return null;
    const formatDateStr = (dateStr, timeStr) => {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return '';
      const m = d.toLocaleString('en-US', { month: 'short' });
      let str = `${d.getDate()}/${m}/${d.getFullYear().toString().slice(-2)}`;
      
      if (timeStr && typeof timeStr === 'string' && timeStr.includes(':')) {
        const [h, min] = timeStr.split(':');
        if (h !== undefined && min !== undefined) {
          let hour = parseInt(h, 10);
          const ampm = hour >= 12 ? TIME_FORMATS.PM : TIME_FORMATS.AM;
          hour = hour % 12 || 12;
          str += ` ${hour}:${min} ${ampm}`;
        }
      }
      return str;
    };
    if (startDateStr && !endDateStr) {
      return `${formatDateStr(startDateStr, startTimeStr)} → ${t('taskViewModal.due', 'Due')}`;
    }
    if (!startDateStr && endDateStr) {
      return `${t('taskViewModal.start', 'Start')} → ${formatDateStr(endDateStr, endTimeStr)}`;
    }
    return `${formatDateStr(startDateStr, startTimeStr)} → ${formatDateStr(endDateStr, endTimeStr)}`;
  };


  // Helper function to get display name
  const getDisplayName = (value, type = 'string') => {
    if (typeof value === 'object' && value && value.name) {
      return String(value.name);
    }
    if (typeof value === 'object' && value) {
      return String(value.id || value.title || 'Unknown');
    }
    return String(value || t('taskViewModal.notSet'));
  };

  // Reset active tab if currently on AI tab but it's not available
  // Define tab indices based on view mode (modal vs fullscreen)
  // In fullScreen (new tab): 0: Details, 1: Work Log, 2: History, 3: AI Developer
  // In modal: 0: Details, 1: Comments, 2: Work Log, 3: History, 4: AI Developer
  const detailsTabIndex = 0;
  const commentsTabIndex = 1; // Only in modal mode
  const workLogTabIndex = fullScreen ? 1 : 2;
  const historyTabIndex = fullScreen ? 2 : 3;
  const aiTabIndex = fullScreen ? 3 : 4;

  const currentActiveTab = (activeTab === aiTabIndex && !shouldShowAIDeveloperTab()) ? detailsTabIndex : activeTab;
  const parentTicketNumber = ticketDetails?.parent_ticket?.ticket_number || '';
  const currentTicketNumber = getTaskTicketNumber(ticketDetails, taskData) || t('common.notAvailable');

  const content = (
      <Box className={fullScreen ? 'task-view-modal' : ''} sx={{ height: fullScreen ? '100vh' : '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        {fullScreen ? (
          <Box sx={{ position: 'sticky', top: 0, zIndex: 1100, bgcolor: COLORS.white, borderBottom: `1px solid ${COLORS.brand.primaryDark}` }}>
            <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <TaskCompletionCheckbox
                    task={{
                      id: taskData?.id,
                      completed: localTaskCompleted,
                      marked_as_completed: localTaskCompleted
                    }}
                    size="medium"
                    onStatusChange={handleTaskCompletionChange}
                  />
                  <Typography variant="h5" fontWeight="500" sx={{ mb: 0.25, color: COLORS.brand.primaryDark }}>
                    {parentTicketNumber ? (
                      <>
                        <span
                          className="parent-ticket-link"
                          onClick={() => window.open(`/task/${encodeURIComponent(parentTicketNumber)}`, '_blank')}
                        >
                          {parentTicketNumber}
                        </span>
                        <span className="parent-ticket-separator"> &gt; </span>
                        <span>{currentTicketNumber}</span>
                      </>
                    ) : (
                      currentTicketNumber
                    )}
                  </Typography>
                </Box>
                {/* Show loading indicator if no data is loaded yet */}
                {(!comments && !worklogSummary && !ticketHistory) && (
                  <Typography variant="caption" sx={{ color: COLORS.warning, fontStyle: 'italic' }}>
                    {t('taskViewModal.loadingData')}
                  </Typography>
                )}
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Tooltip title={t('common.copyLink', 'Copy Link')} slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                  <IconButton
                    onClick={handleCopyTaskLinkMemoized}
                    sx={{ color: COLORS.gray[500], '&:hover': { color: COLORS.gray[800], bgcolor: COLORS.gray[100] } }}
                  >
                    <img src={ICONS.copyLink} alt="Copy Link" style={{ width: 20, height: 20 }} />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title={t('taskViewModal.editTask')} slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                  <IconButton
                    onClick={handleEdit}
                    sx={{ 
                      color: isEditingMode ? COLORS.primary : COLORS.gray[500],
                      '&:hover': { 
                        color: isEditingMode ? COLORS.primary : COLORS.gray[800],
                        bgcolor: isEditingMode ? COLORS.primary + '10' : COLORS.gray[100] 
                      }
                    }}
                  >
                    <img src={ICONS.editTask} alt="" style={{ width: 20, height: 20 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Tabs
              value={currentActiveTab}
              onChange={handleTabChange}
              sx={{
                px: 2,
                '& .MuiTab-root': {
                  color: COLORS.gray[500],
                  textTransform: 'none',
                  minHeight: 48,
                  fontWeight: 500,
                  '&.Mui-selected': { color: COLORS.brand.primaryDark }
                },
                '& .MuiTabs-indicator': { backgroundColor: COLORS.brand.primaryDark, height: 2 }
              }}
            >
              <Tab 
                value={detailsTabIndex} 
                icon={<img src={currentActiveTab === detailsTabIndex ? ICONS.file : ICONS.fileDisabled} alt="" style={{ width: 18, height: 18 }} />} 
                label={t('taskViewModal.details')} 
                iconPosition="start" 
              />
              <Tab 
                value={workLogTabIndex} 
                icon={<img src={currentActiveTab === workLogTabIndex ? ICONS.worklog : ICONS.worklogDisabled} alt="" style={{ width: 18, height: 18 }} />} 
                label={t('taskViewModal.workLog')} 
                iconPosition="start" 
              />
              <Tab 
                value={historyTabIndex} 
                icon={<img src={currentActiveTab === historyTabIndex ? ICONS.history : ICONS.historyDisabled} alt="" style={{ width: 18, height: 18 }} />} 
                label={t('taskViewModal.history')} 
                iconPosition="start" 
              />
              {shouldShowAIDeveloperTab() && (
                <Tab value={aiTabIndex} icon={<SmartToy sx={{ color: currentActiveTab === aiTabIndex ? COLORS.brand.primaryDark : COLORS.gray[500] }} />} label="AI Developer" iconPosition="start" />
              )}
            </Tabs>
          </Box>
        ) : (
          <Box sx={{
            bgcolor: COLORS.white,
            borderBottom: `1px solid ${COLORS.brand.primaryDark}`,
            overflow: 'hidden'
          }}>
            {/* Top Bar: Breadcrumb + View Switcher + Action Buttons */}
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 2, pt: 1.5, pb: 1 }}>
              {/* Left: Breadcrumb */}
              <Box display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
                <TaskCompletionCheckbox
                  task={{
                    id: taskData?.id,
                    completed: localTaskCompleted,
                    marked_as_completed: localTaskCompleted
                  }}
                  size="small"
                  onStatusChange={handleTaskCompletionChange}
                  sx={{ mr: 0.5 }}
                />
                <OverflowTooltip 
                  title={ticketDetails?.project?.name || t('taskViewModal.project', 'Project')}
                  variant="caption"
                  sx={{ color: COLORS.brand.primaryDark, fontWeight: 500, maxWidth: 200 }}
                />
                <ChevronRight sx={{ fontSize: 14, color: COLORS.gray[400], flexShrink: 0 }} />
                <OverflowTooltip
                  title={parentTicketNumber ? `${parentTicketNumber} > ${currentTicketNumber}` : currentTicketNumber}
                  variant="caption"
                  sx={{ color: COLORS.brand.primaryDark, fontWeight: 500, maxWidth: 200 }}
                >
                  {parentTicketNumber ? (
                    <>
                      <span
                        className="parent-ticket-link"
                        onClick={() => window.open(`/task/${encodeURIComponent(parentTicketNumber)}`, '_blank')}
                      >
                        {parentTicketNumber}
                      </span>
                      <span className="parent-ticket-separator"> &gt; </span>
                      <span>{currentTicketNumber}</span>
                    </>
                  ) : (
                    currentTicketNumber
                  )}
                </OverflowTooltip>
              </Box>

              {/* Right: View Mode Switcher + Action Buttons */}
              <Box display="flex" alignItems="center" gap={0.5} sx={{ flexShrink: 0, ml: 1, whiteSpace: 'nowrap' }}>

                {/* View Mode Switcher — shown on hover */}
                <Tooltip title="Change view" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                  <IconButton
                    size="small"
                    onMouseEnter={handleViewSwitcherOpen}
                    onMouseLeave={handleViewSwitcherClose}
                    sx={{
                      color: Boolean(viewSwitcherAnchor) ? COLORS.primary : COLORS.gray[500],
                      bgcolor: Boolean(viewSwitcherAnchor) ? COLORS.primary + '10' : 'transparent',
                      '&:hover': { color: COLORS.gray[800], bgcolor: COLORS.gray[100] },
                      mr: 0.5,
                    }}
                  >
                    <img src={ICONS.changeView} alt="Change View" style={{ width: 25, height: 24 }} />
                  </IconButton>
                </Tooltip>
                <Popover
                  open={Boolean(viewSwitcherAnchor)}
                  anchorEl={viewSwitcherAnchor}
                  onClose={() => setViewSwitcherAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  disableRestoreFocus
                  sx={{
                    pointerEvents: 'none',
                    '& .MuiPaper-root': {
                      pointerEvents: 'auto',
                      mt: 0.5,
                      borderRadius: '10px',
                      boxShadow: `0 8px 24px ${COLORS.ui.shadow12}`,
                      border: `1px solid ${COLORS.gray[200]}`,
                      overflow: 'hidden',
                      minWidth: 220,
                    }
                  }}
                  PaperProps={{
                    onMouseEnter: handleViewSwitcherPopoverEnter,
                    onMouseLeave: handleViewSwitcherClose,
                  }}
                >
                  <Box sx={{ p: 1.5 }}>
                    <Typography variant="caption" sx={{ color: COLORS.gray[500], fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 1, px: 0.5 }}>
                      View
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {/* Modal Option */}
                      <Box
                        onClick={() => { setTicketViewMode('modal'); setViewSwitcherAnchor(null); }}
                        sx={{
                          flex: 1,
                          py: 1.5,
                          px: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 1,
                          cursor: 'pointer',
                          borderRadius: '8px',
                          border: `1.5px solid ${ticketViewMode === 'modal' ? COLORS.primary : COLORS.gray[200]}`,
                          bgcolor: ticketViewMode === 'modal' ? COLORS.primary + '08' : 'transparent',
                          transition: 'all 0.15s ease',
                          '&:hover': { bgcolor: COLORS.gray[50], borderColor: COLORS.primary + '80' },
                        }}
                      >
                        {/* Modal thumbnail */}
                        <Box sx={{ width: 32, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                          <Box sx={{
                            width: 26, height: 20, border: `1.5px solid ${ticketViewMode === 'modal' ? COLORS.primary : COLORS.gray[400]}`,
                            borderRadius: '3px',
                            bgcolor: ticketViewMode === 'modal' ? COLORS.primary + '15' : COLORS.gray[50],
                            boxShadow: `0 2px 4px ${COLORS.ui.shadow10}`
                          }} />
                        </Box>
                        <Typography variant="caption" sx={{ fontSize: '11px', color: ticketViewMode === 'modal' ? COLORS.primary : COLORS.gray[600], fontWeight: 600, lineHeight: 1 }}>Modal</Typography>
                      </Box>

                      {/* Sidebar Option */}
                      <Box
                        onClick={() => { setTicketViewMode('sidebar'); setViewSwitcherAnchor(null); }}
                        sx={{
                          flex: 1,
                          py: 1.5,
                          px: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 1,
                          cursor: 'pointer',
                          borderRadius: '8px',
                          border: `1.5px solid ${ticketViewMode === 'sidebar' ? COLORS.primary : COLORS.gray[200]}`,
                          bgcolor: ticketViewMode === 'sidebar' ? COLORS.primary + '08' : 'transparent',
                          transition: 'all 0.15s ease',
                          '&:hover': { bgcolor: COLORS.gray[50], borderColor: COLORS.primary + '80' },
                        }}
                      >
                        {/* Sidebar thumbnail */}
                        <Box sx={{ width: 32, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Box sx={{
                            width: 30, height: 22, border: `1.5px solid ${ticketViewMode === 'sidebar' ? COLORS.primary : COLORS.gray[400]}`,
                            borderRadius: '3px', overflow: 'hidden', display: 'flex'
                          }}>
                            <Box sx={{ width: '60%', height: '100%', bgcolor: ticketViewMode === 'sidebar' ? COLORS.gray[50] : COLORS.gray[50], borderRight: `1.5px solid ${ticketViewMode === 'sidebar' ? COLORS.primary + '60' : COLORS.gray[300]}` }} />
                            <Box sx={{ width: '40%', height: '100%', bgcolor: ticketViewMode === 'sidebar' ? COLORS.primary + '20' : COLORS.gray[100] }} />
                          </Box>
                        </Box>
                        <Typography variant="caption" sx={{ fontSize: '11px', color: ticketViewMode === 'sidebar' ? COLORS.primary : COLORS.gray[600], fontWeight: 600, lineHeight: 1 }}>Sidebar</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Popover>

                {/* Action Buttons */}
                <Tooltip title={t('taskViewModal.copyLink')} slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                  <IconButton
                    size="small"
                    onClick={handleCopyTaskLinkMemoized}
                    sx={{ color: COLORS.gray[500], '&:hover': { color: COLORS.gray[800], bgcolor: COLORS.gray[100] } }}
                  >
                    <img src={ICONS.copyLink} alt="Copy Link" style={{ width: 18, height: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('taskViewModal.openInNewTab')} slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const ticketNumber = getTaskTicketNumber(ticketDetails, taskData);
                      if (ticketNumber) {
                        window.open(`/task/${encodeURIComponent(ticketNumber)}`, '_blank');
                      }
                    }}
                    sx={{ color: COLORS.gray[500], '&:hover': { color: COLORS.gray[800], bgcolor: COLORS.gray[100] } }}
                  >
                    <img src={ICONS.openInNewTab} alt="Open in New Tab" style={{ width: 18, height: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('taskViewModal.editTask')} slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                  <IconButton
                    size="small"
                    onClick={handleEdit}
                    sx={{ color: COLORS.gray[500], '&:hover': { color: COLORS.gray[800], bgcolor: COLORS.gray[100] } }}
                  >
                    <img src={ICONS.editTask} alt="Edit Task" style={{ width: 18, height: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('taskViewModal.close', 'Close')} slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                  <IconButton
                    size="small"
                    onClick={handleClose}
                    sx={{ color: COLORS.gray[500], '&:hover': { color: COLORS.gray[800], bgcolor: COLORS.gray[100] } }}
                  >
                    <img src={ICONS.close} alt="Close" style={{ width: 18, height: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        )}

        {/* Content Wrapper */}
        <Box 
          sx={{ 
            display: 'flex', 
            flex: 1, 
            minHeight: 0,
            position: 'relative'
          }}
        >

          {/* Main scrollable content */}
          <Box 
            ref={handleScrollContainerRef}
            sx={{
              flex: 1,
              p: fullScreen ? 0 : 0,
              bgcolor: fullScreen ? COLORS.white : 'transparent',
              minWidth: 0,
              overflow: 'auto'
            }}
          >
            <Box sx={{ width: '100%', maxWidth: fullScreen ? 'none' : 'none', mx: fullScreen ? 0 : 'auto', px: fullScreen ? 2 : 0, py: fullScreen ? 2 : 0 }}>
            {currentActiveTab === detailsTabIndex && (
              <Box sx={{ p: fullScreen ? 0 : 3 }}>
                <Box sx={{ pr: fullScreen ? 0 : 5 }}>

              <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 3 }}>
                <TextField
                  variant="standard"
                  multiline
                  value={editingTitleContent}
                  onChange={(e) => {
                    if (!isEditingMode) return;
                    handleTitleContentChange(e.target.value);
                  }}
                  onPaste={handleTitlePaste}
                  readOnly={!isEditingMode}
                  inputProps={{ maxLength: CHARACTER_LIMITS.TICKET_TITLE }}
                  sx={{
                    ...taskTitleFieldStyles,
                    '& .MuiInputBase-root': {
                      cursor: isEditingMode ? 'text' : 'default',
                      pointerEvents: isEditingMode ? 'auto' : 'none',
                      p: 0
                    },
                    '& .MuiInputBase-input': {
                      fontWeight: 400,
                      fontSize: '1.25rem',
                      lineHeight: 1.2
                    }
                  }}
                />
              </Box>

              {/* COMPACT TWO-COLUMN GRID */}
              <Box className="task-detail-compact-grid">
                {/* 1. STATUS */}
                <Box className="task-detail-field-row task-detail-field-row--align-center">
                  <Box className="task-detail-field-label">
                    <Timeline sx={{ fontSize: 16, color: COLORS.gray[400] }} />
                    <Typography variant="caption" sx={{ color: COLORS.gray[500], fontWeight: 500, fontSize: '12px', textTransform: 'none' }}>
                      Status
                    </Typography>
                  </Box>
                  <FormControl size="small" sx={{ width: 'fit-content', maxWidth: 160 }}>
                    <Select
                      value={localStatus?.id || ''}
                      onChange={(e) => {
                        const newStatusId = e.target.value;
                        const newStatus = dropdowns.statuses?.find(s => s.id === newStatusId);
                        const previousStatus = localStatus || ticketDetails?.status || taskData?.status || null;
                        setLocalStatus(newStatus);
                        // Only trigger save on change if not in editing mode, to avoid saving on every selection change while editing
                        if (!isEditingMode) {
                          handleInlineFieldSave('status_id', newStatusId, {
                            previousStatus,
                            status: newStatus
                          });
                        }
                      }}
                      renderValue={(selected) => {
                        const status = dropdowns.statuses?.find(s => s.id === selected) || localStatus;
                        return (
                          <Box display="flex" alignItems="center" gap={1} sx={{ overflow: 'hidden', maxWidth: '100%', minWidth: 0, width: '100%' }}>
                            {getStatusIcon(status?.name) && (
                              <img src={getStatusIcon(status.name)} alt="" style={{ width: 14, height: 14, flexShrink: 0 }} />
                            )}
                            <OverflowTooltip 
                              title={status?.name || t('taskViewModal.notSet')} 
                              tooltipWidth={300}
                              placement="top-start"
                              sx={{ fontWeight: 500, fontSize: '12px', color: COLORS.gray[700], whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }} 
                            />
                          </Box>
                        );
                      }}
                      sx={{
                        height: 32, bgcolor: COLORS.white, maxWidth: 160, width: '100%',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.gray[200], borderRadius: '4px' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.gray[300] },
                        '& .MuiSelect-select': { 
                          py: '0 !important', 
                          display: 'flex', 
                          alignItems: 'center', 
                          height: '100%',
                          pr: '28px !important',
                          minWidth: 0,
                          overflow: 'hidden'
                        }
                      }}
                      MenuProps={taskSelectMenuProps}
                    >
                      {dropdowns.statuses?.map((status) => (
                        <MenuItem key={status.id} value={status.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                          {getStatusIcon(status.name) && (
                            <img src={getStatusIcon(status.name)} alt="" style={{ width: 14, height: 14, flexShrink: 0 }} />
                          )}
                          <OverflowTooltip
                            title={status.name}
                            tooltipWidth={300}
                            placement="top-start"
                            sx={{ fontWeight: 500, fontSize: '12px', color: COLORS.gray[700], minWidth: 0, flex: 1 }}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* 2. TASK TYPE */}
                <Box className="task-detail-field-row task-detail-field-row--align-center">
                  <Box className="task-detail-field-label">
                    <Description sx={{ fontSize: 16, color: COLORS.gray[400] }} />
                    <Typography variant="caption" sx={{ color: COLORS.gray[500], fontWeight: 500, fontSize: '12px', textTransform: 'none' }}>
                      Task type
                    </Typography>
                  </Box>
                  <FormControl size="small" sx={{ width: 'fit-content', maxWidth: 160 }}>
                    <Select
                      value={localType?.id || (typeof localType === 'string' || typeof localType === 'number' ? localType : '')}
                      renderValue={(selected) => {
                        const type = dropdowns.types?.find(t => t.id === selected);
                        if (!type) return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%', overflow: 'hidden' }}>
                            <img src={getTaskTypeIcon(getDisplayName(ticketDetails?.type || taskData.taskType, 'taskType'))} alt="" style={{ width: 13, height: 13, flexShrink: 0 }} />
                            <Typography sx={{ fontWeight: 500, fontSize: '12px', color: COLORS.gray[700], whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                              {getDisplayName(ticketDetails?.type || taskData.taskType, 'taskType')}
                            </Typography>
                          </Box>
                        );
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden', maxWidth: '100%', minWidth: 0, width: '100%' }}>
                            <img src={getTaskTypeIcon(type.name)} alt="" style={{ width: 13, height: 13, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: COLORS.gray[800], whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                              {type.name}
                            </Typography>
                          </Box>
                        );
                      }}
                      onChange={(e) => {
                        const newTypeId = e.target.value;
                        const newType = dropdowns.types?.find(t => t.id === newTypeId);
                        setLocalType(newType);
                        // Only trigger save on change if not in editing mode, to avoid saving on every selection change while editing
                        if (!isEditingMode) {
                          setTimeout(() => handleInlineFieldSave('type_id', newTypeId), 0);
                        }
                      }}
                      onClose={() => { setIsCreatingViewTaskType(false); setNewViewTaskTypeName(''); }}
                      sx={{
                        height: 32, bgcolor: COLORS.white, fontSize: '12px', maxWidth: 160, width: '100%',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.gray[200], borderRadius: '4px' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.gray[300] },
                        '& .MuiSelect-select': { 
                          py: '0 !important', 
                          display: 'flex', 
                          alignItems: 'center', 
                          height: '100%',
                          pr: '28px !important',
                          minWidth: 0,
                          overflow: 'hidden'
                        }
                      }}
                      MenuProps={taskSelectMenuProps}
                    >
                      {!isCreatingViewTaskType && (
                        <MenuItem className="create-option" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setIsCreatingViewTaskType(true); }} sx={{ borderBottom: `1px solid ${COLORS.gray[200]}` }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: COLORS.primary }}><Add sx={{ fontSize: 16 }} /><span>{t('taskModal.createNewTaskType')}</span></Box>
                        </MenuItem>
                      )}
                      {isCreatingViewTaskType && (
                        <MenuItem disableRipple onMouseDown={(e) => e.preventDefault()} sx={{ borderBottom: `1px solid ${COLORS.gray[200]}` }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                            <TextField size="small" autoFocus placeholder={t('taskModal.enterTaskTypeName')} value={newViewTaskTypeName}
                              onChange={(e) => { e.stopPropagation(); setNewViewTaskTypeName(e.target.value); }}
                              onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); if (!isCreatingTaskTypeSubmitting) handleViewCreateTaskType(); } if (e.key === 'Escape') { e.preventDefault(); setIsCreatingViewTaskType(false); } }}
                              onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}
                              inputProps={{ maxLength: CHARACTER_LIMITS.TASK_TYPE_NAME }}
                              helperText={`${newViewTaskTypeName.length}/${CHARACTER_LIMITS.TASK_TYPE_NAME}`}
                              sx={{ flex: 1 }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: 40 }}>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleViewCreateTaskType(); }} disabled={isCreatingTaskTypeSubmitting || !newViewTaskTypeName.trim() || newViewTaskTypeName.length > CHARACTER_LIMITS.TASK_TYPE_NAME} sx={{ color: COLORS.success }}><Check sx={{ fontSize: 16 }} /></IconButton>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setIsCreatingViewTaskType(false); setNewViewTaskTypeName(''); }} sx={{ color: COLORS.error }}><Clear sx={{ fontSize: 16 }} /></IconButton>
                            </Box>
                          </Box>
                        </MenuItem>
                      )}
                       {dropdowns.types?.map(type => (
                         <MenuItem key={type.id} value={editingViewTaskType?.id === type.id ? '' : type.id} sx={{ fontSize: '12px', maxWidth: 300, overflow: 'hidden' }}>
                           {editingViewTaskType?.id === type.id ? (
                             <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, width: '100%' }} onClick={(e) => e.stopPropagation()}>
                               <TextField
                                 size="small" autoFocus
                                 value={editingViewTaskType.newName}
                                 onChange={(e) => { e.stopPropagation(); setEditingViewTaskType(prev => ({ ...prev, newName: e.target.value })); }}
                                 onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); handleUpdateViewTaskType(); } if (e.key === 'Escape') { e.preventDefault(); setEditingViewTaskType(null); } }}
                                 onMouseDown={(e) => e.stopPropagation()}
                                 onClick={(e) => e.stopPropagation()}
                                 inputProps={{ maxLength: CHARACTER_LIMITS.TASK_TYPE_NAME }}
                                 helperText={`${editingViewTaskType.newName?.length ?? 0}/${CHARACTER_LIMITS.TASK_TYPE_NAME}`}
                                 sx={{ flex: 1 }}
                               />
                               <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: 40 }}>
                                 <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleUpdateViewTaskType(); }} disabled={!editingViewTaskType.newName?.trim() || editingViewTaskType.newName.length > CHARACTER_LIMITS.TASK_TYPE_NAME} sx={{ color: COLORS.success }}>
                                   <Check sx={{ fontSize: 16 }} />
                                 </IconButton>
                                 <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingViewTaskType(null); }} sx={{ color: COLORS.error }}>
                                   <Clear sx={{ fontSize: 16 }} />
                                 </IconButton>
                               </Box>
                             </Box>
                           ) : (
                             <Box display="flex" alignItems="center" gap={1} width="100%" overflow="hidden">
                               <img src={getTaskTypeIcon(type.name)} alt="" style={{ width: 14, height: 14, flexShrink: 0 }} />
                               <OverflowTooltip
                                 title={type.name}
                                 tooltipWidth={300}
                                 placement="top-start"
                                 sx={{ fontSize: '12px', flex: 1, minWidth: 0 }}
                               />
                               {canManageTicketAttributes && (!type.status && (localType?.id || localType) !== type.id) && (
                                 <Box sx={{ display: 'flex', gap: 0.25, ml: 'auto' }}>
                                   <IconButton
                                     size="small"
                                     onClick={(e) => {
                                       e.stopPropagation();
                                       setEditingViewTaskType({ ...type, newName: type.name });
                                     }}
                                     sx={{ p: 0.25 }}
                                   >
                                     <Edit sx={{ fontSize: 14 }} />
                                   </IconButton>
                                   <IconButton 
                                     size="small" 
                                     onClick={(e) => { 
                                       e.stopPropagation(); 
                                       handleDeleteViewTaskType(type); 
                                     }} 
                                     sx={{ p: 0.25 }}
                                   >
                                     <Delete sx={{ fontSize: 14 }} />
                                   </IconButton>
                                 </Box>
                               )}
                             </Box>
                           )}
                         </MenuItem>
                       ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* 3. ASSIGNEE */}
                <Box className="task-detail-field-row task-detail-field-row--align-center">
                  <Box className="task-detail-field-label">
                    <PersonOutline sx={{ fontSize: 16, color: COLORS.gray[400] }} />
                    <Typography variant="caption" sx={{ color: COLORS.gray[500], fontWeight: 500, fontSize: '12px', textTransform: 'none' }}>
                      Assignee
                    </Typography>
                  </Box>
                  <Box sx={{ minHeight: 32, display: 'flex', alignItems: 'center', width: 'fit-content', justifyContent: 'flex-end' }}>
                    <AssigneeSelector
                      value={localAssignees.map(a => a.id)}
                      onChange={(newIds) => {
                        const newAssignees = newIds
                          .map(id => dropdowns.assignees?.find(a => a.id === id))
                          .filter(Boolean);
                        setLocalAssignees(newAssignees);
                        if (!isEditingMode) {
                          dispatch(updateTicketAssignee({ ticketId: taskData.id, assignees: newAssignees }));
                          setTimeout(() => handleInlineFieldSave('assignee', newIds), 0);
                        }
                      }}
                      assignees={dropdowns.assignees || []}
                      variant="stack"
                    />
                  </Box>
                </Box>

                {/* 4. SPRINTS */}
                {(!isScrum || (isScrum && localSprint?.id)) && (
                <Box className="task-detail-field-row task-detail-field-row--align-center">
                  <Box className="task-detail-field-label">
                    <DirectionsRun sx={{ fontSize: 16, color: COLORS.gray[400] }} />
                    <Typography variant="caption" sx={{ color: COLORS.gray[500], fontWeight: 500, fontSize: '12px', textTransform: 'none' }}>
                      Sprints
                    </Typography>
                  </Box>
                  <FormControl size="small" sx={{ width: 'fit-content', maxWidth: 160 }}>
                    <Select
                      value={localSprint?.id || ''}
                      disabled={isScrum}
                      onChange={(e) => {
                        const newSprintId = e.target.value;
                        
                        if (isScrum && newSprintId && (!localStoryPoints || localStoryPoints === '')) {
                           toast.error(t('taskModal.storyPointsRequiredForSprint'));
                           return;
                        }

                        const newSprint = newSprintId ? dropdowns.sprints?.find(s => s.id === newSprintId) : null;
                        setLocalSprint(newSprint);
                        if (!isEditingMode) {
                          setTimeout(() => handleInlineFieldSave(isScrum ? 'scrum_sprint_id' : 'sprint_id', newSprintId || null), 0);
                        }
                      }}
                      onClose={() => { setIsCreatingViewSprint(false); setNewViewSprintName(''); }}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) return <em style={{ color: COLORS.gray[500], fontStyle: 'italic' }}>None</em>;
                        let sprint = dropdowns.sprints?.find(s => s.id === selected);
                        
                        if (!sprint && isScrum && localSprint?.id === selected) {
                           sprint = {
                             id: localSprint.id,
                             name: localSprint.name || 'Unnamed Sprint',
                             colour: COLORS.primary
                           };
                        }

                        if (!sprint) return <em style={{ color: COLORS.gray[500], fontStyle: 'italic' }}>None</em>;
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%', overflow: 'hidden' }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: sprint.colour || sprint.color || COLORS.primary, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{sprint.name}</Typography>
                          </Box>
                        );
                      }}
                      sx={{
                        height: 32, bgcolor: COLORS.white, fontSize: '12px', maxWidth: 160, width: '100%',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.gray[200], borderRadius: '4px' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.gray[300] },
                        '& .MuiSelect-select': { 
                          py: '0 !important', 
                          display: 'flex', 
                          alignItems: 'center', 
                          height: '100%',
                          pr: '28px !important',
                          minWidth: 0,
                          overflow: 'hidden'
                        }
                      }}
                      MenuProps={taskSelectMenuProps}
                    >
                      {!isCreatingViewSprint && (
                        <MenuItem className="create-option" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setIsCreatingViewSprint(true); }} sx={{ borderBottom: `1px solid ${COLORS.gray[200]}` }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: COLORS.primary }}><Add sx={{ fontSize: 16 }} /><span>{t('taskModal.createNewSprint')}</span></Box>
                        </MenuItem>
                      )}
                      {isCreatingViewSprint && (
                        <MenuItem disableRipple onMouseDown={(e) => e.preventDefault()} sx={{ borderBottom: `1px solid ${COLORS.gray[200]}`, py: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                            <TextField 
                              size="small" 
                              autoFocus 
                              placeholder={t('taskModal.enterSprintName')} 
                              value={newViewSprintName}
                              onChange={(e) => { e.stopPropagation(); setNewViewSprintName(e.target.value); }}
                              onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); if (!isCreatingSprintSubmitting) handleViewCreateSprint(); } if (e.key === 'Escape') { e.preventDefault(); setIsCreatingViewSprint(false); } }}
                              onMouseDown={(e) => e.stopPropagation()} 
                              onClick={(e) => e.stopPropagation()}
                              inputProps={{ maxLength: CHARACTER_LIMITS.SPRINT_NAME }}
                              helperText={`${newViewSprintName.length}/${CHARACTER_LIMITS.SPRINT_NAME}`}
                              sx={{ 
                                flex: 1,
                                '& .MuiInputBase-root': { height: 32, fontSize: '12px' },
                                '& .MuiOutlinedInput-input': { py: 0 }
                              }} 
                            />
                            <Box
                              onClick={(e) => {
                                e.stopPropagation();
                                setCreateSprintAnchor(e.currentTarget);
                              }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="color-picker-preview"
                    sx={{
                                width: 32,
                                height: 32,
                                backgroundColor: newViewSprintColor,
                                
                              }}
                            />
                            <CustomColorPopover
                              open={Boolean(createSprintAnchor)}
                              anchorEl={createSprintAnchor}
                              onClose={() => setCreateSprintAnchor(null)}
                              colour={newViewSprintColor}
                              onChange={setNewViewSprintColor}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: 32 }}>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleViewCreateSprint(); }} disabled={isCreatingSprintSubmitting || !newViewSprintName.trim() || newViewSprintName.length > CHARACTER_LIMITS.SPRINT_NAME} sx={{ color: COLORS.success, p: 0.5 }}>
                                <Check sx={{ fontSize: 18 }} />
                              </IconButton>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setIsCreatingViewSprint(false); setNewViewSprintName(''); }} sx={{ color: COLORS.error, p: 0.5 }}>
                                <Clear sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>
                          </Box>
                        </MenuItem>
                      )}
                      <MenuItem value=""><em>None</em></MenuItem>
                       {dropdowns.sprints?.map(sprint => (
                        <MenuItem key={sprint.id} value={editingViewSprint?.id === sprint.id ? '' : sprint.id} sx={{ fontSize: '12px', maxWidth: 300, overflow: 'hidden' }}>
                          {editingViewSprint?.id === sprint.id ? (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }} onClick={(e) => e.stopPropagation()}>
                              <TextField 
                                size="small" 
                                autoFocus 
                                value={editingViewSprint.newName}
                                onChange={(e) => { e.stopPropagation(); setEditingViewSprint(prev => ({ ...prev, newName: e.target.value })); }}
                                onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); handleUpdateViewSprint(); } if (e.key === 'Escape') { e.preventDefault(); setEditingViewSprint(null); } }}
                                onMouseDown={(e) => e.stopPropagation()} 
                                onClick={(e) => e.stopPropagation()}
                                inputProps={{ maxLength: CHARACTER_LIMITS.SPRINT_NAME }}
                                helperText={`${editingViewSprint.newName?.length ?? 0}/${CHARACTER_LIMITS.SPRINT_NAME}`}
                                sx={{ 
                                  flex: 1,
                                  '& .MuiInputBase-root': { height: 32, fontSize: '12px' },
                                  '& .MuiOutlinedInput-input': { py: 0 }
                                }} 
                              />
                              <Box
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditSprintAnchor(e.currentTarget);
                                }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="color-picker-preview"
                    sx={{
                                  width: 24,
                                  height: 24,
                                  backgroundColor: editingViewSprint.newColor,
                                  
                                  mt: 0.5,
                                }}
                              />
                              <CustomColorPopover
                                open={Boolean(editSprintAnchor)}
                                anchorEl={editSprintAnchor}
                                onClose={() => setEditSprintAnchor(null)}
                                colour={editingViewSprint.newColor}
                                onChange={(color) => setEditingViewSprint(prev => ({ ...prev, newColor: color }))}
                              />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: 32 }}>
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleUpdateViewSprint(); }} disabled={!editingViewSprint.newName?.trim() || editingViewSprint.newName.length > CHARACTER_LIMITS.SPRINT_NAME} sx={{ color: COLORS.success, p: 0.5 }}>
                                  <Check sx={{ fontSize: 18 }} />
                                </IconButton>
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingViewSprint(null); }} sx={{ color: COLORS.error, p: 0.5 }}>
                                  <Clear sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Box>
                            </Box>
                          ) : (
                            <Box display="flex" alignItems="center" gap={1} width="100%" overflow="hidden">
                              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: sprint.colour || sprint.color || COLORS.primary, flexShrink: 0 }} />
                              <OverflowTooltip
                                title={sprint.name}
                                tooltipWidth={300}
                                placement="top-start"
                                sx={{ fontSize: '12px', flex: 1, minWidth: 0 }}
                              />
                              {canManageTicketAttributes && (!sprint.status && localSprint?.id !== sprint.id) && (
                                <Box sx={{ display: 'flex', gap: 0.25, ml: 'auto' }}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingViewSprint({ ...sprint, newName: sprint.name, newColor: sprint.colour || sprint.color || COLORS.primary });
                                    }}
                                    sx={{ p: 0.25 }}
                                  >
                                    <Edit sx={{ fontSize: 14 }} />
                                  </IconButton>
                                  <IconButton 
                                    size="small" 
                                    onClick={(e) => { 
                                      e.stopPropagation(); 
                                      handleDeleteViewSprint(sprint); 
                                    }} 
                                    sx={{ p: 0.25 }}
                                  >
                                    <Delete sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Box>
                              )}
                            </Box>
                          )}
                        </MenuItem>
                       ))}
                    </Select>
                  </FormControl>
                </Box>
                )}

                {/* 5. PRIORITY */}
                <Box className="task-detail-field-row task-detail-field-row--align-center">
                  <Box className="task-detail-field-label">
                    <FlagOutlined sx={{ fontSize: 16, color: COLORS.gray[400] }} />
                    <Typography variant="caption" sx={{ color: COLORS.gray[500], fontWeight: 500, fontSize: '12px', textTransform: 'none' }}>
                      Priority
                    </Typography>
                  </Box>
                  <FormControl size="small" sx={{ width: 'fit-content', maxWidth: 160 }}>
                    <Select
                      value={localPriority?.id || (typeof localPriority === 'string' || typeof localPriority === 'number' ? localPriority : '')}
                      onChange={(e) => {
                        const nextPriority = dropdowns.priorities?.find(p => p.id === e.target.value) || null;
                        setLocalPriority(nextPriority);
                        if (!isEditingMode) {
                          setTimeout(() => handleInlineFieldSave('priority_id', e.target.value || null), 0);
                        }
                      }}
                      onClose={() => { setIsCreatingViewPriority(false); setNewViewPriorityName(''); }}
                      renderValue={(selected) => {
                        const p = dropdowns.priorities?.find(p => p.id === selected);
                        if (!p) return (
                          <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0, width: '100%', overflow: 'hidden' }}>
                            <FlagOutlined sx={{ fontSize: 14, color: getPriorityColor(ticketDetails?.priority || taskData.priority, { priorities: dropdowns.priorities }), flexShrink: 0 }} />
                            <Typography sx={{ fontWeight: 500, fontSize: '12px', color: getPriorityColor(ticketDetails?.priority || taskData.priority, { priorities: dropdowns.priorities }), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                              {getDisplayName(ticketDetails?.priority || taskData.priority, 'priority')}
                            </Typography>
                          </Box>
                        );
                        return (
                          <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0, width: '100%', overflow: 'hidden' }}>
                            <FlagOutlined sx={{ fontSize: 14, color: getPriorityColor(p, { priorities: dropdowns.priorities }), flexShrink: 0 }} />
                            <Typography sx={{ fontWeight: 500, fontSize: '12px', color: getPriorityColor(p, { priorities: dropdowns.priorities }), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                              {p.name}
                            </Typography>
                          </Box>
                        );
                      }}
                      sx={{
                        height: 32, bgcolor: COLORS.white, fontSize: '12px', maxWidth: 160, width: '100%',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.gray[200], borderRadius: '4px' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.gray[300] },
                        '& .MuiSelect-select': { 
                          py: '0 !important', 
                          display: 'flex', 
                          alignItems: 'center', 
                          height: '100%',
                          pr: '28px !important',
                          minWidth: 0,
                          overflow: 'hidden'
                        }
                      }}
                      MenuProps={taskSelectMenuProps}
                    >
                      {!isCreatingViewPriority && (
                        <MenuItem className="create-option" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setIsCreatingViewPriority(true); }} sx={{ borderBottom: `1px solid ${COLORS.gray[200]}` }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: COLORS.primary }}><Add sx={{ fontSize: 16 }} /><span>{t('taskModal.createNewPriority')}</span></Box>
                        </MenuItem>
                      )}
                      {isCreatingViewPriority && (
                        <MenuItem disableRipple onMouseDown={(e) => e.preventDefault()} sx={{ borderBottom: `1px solid ${COLORS.gray[200]}` }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                            <TextField size="small" autoFocus placeholder={t('taskModal.enterPriorityName')} value={newViewPriorityName}
                              onChange={(e) => { e.stopPropagation(); setNewViewPriorityName(e.target.value); }}
                              onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); if (!isCreatingPrioritySubmitting) handleViewCreatePriority(); } if (e.key === 'Escape') { e.preventDefault(); setIsCreatingViewPriority(false); } }}
                              onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}
                              inputProps={{ maxLength: CHARACTER_LIMITS.PRIORITY_NAME }}
                              helperText={`${newViewPriorityName.length}/${CHARACTER_LIMITS.PRIORITY_NAME}`}
                              sx={{ flex: 1 }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: 40 }}>
                              <Box
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCreatePriorityAnchor(e.currentTarget);
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                className="color-picker-preview"
                                sx={{
                                  width: 24,
                                  height: 24,
                                  backgroundColor: newViewPriorityColor,
                                  
                                  mr: 0.5,
                                  mt: 0.5
                                }}
                              />
                              <CustomColorPopover
                                open={Boolean(createPriorityAnchor)}
                                anchorEl={createPriorityAnchor}
                                onClose={() => setCreatePriorityAnchor(null)}
                                colour={newViewPriorityColor}
                                onChange={setNewViewPriorityColor}
                              />
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleViewCreatePriority(); }} disabled={isCreatingPrioritySubmitting || !newViewPriorityName.trim() || newViewPriorityName.length > CHARACTER_LIMITS.PRIORITY_NAME} sx={{ color: COLORS.success }}><Check sx={{ fontSize: 16 }} /></IconButton>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setIsCreatingViewPriority(false); setNewViewPriorityName(''); setNewViewPriorityColor(COLORS.primary); }} sx={{ color: COLORS.error }}><Clear sx={{ fontSize: 16 }} /></IconButton>
                            </Box>
                          </Box>
                        </MenuItem>
                      )}
                       {dropdowns.priorities?.map(p => (
                         <MenuItem key={p.id} value={editingViewPriority?.id === p.id ? '' : p.id} sx={{ fontSize: '12px', maxWidth: 300, overflow: 'hidden' }}>
                           {editingViewPriority?.id === p.id ? (
                             <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, width: '100%' }} onClick={(e) => e.stopPropagation()}>
                               <TextField
                                 size="small" autoFocus
                                 value={editingViewPriority.newName}
                                 onChange={(e) => { e.stopPropagation(); setEditingViewPriority(prev => ({ ...prev, newName: e.target.value })); }}
                                 onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); handleUpdateViewPriority(); } if (e.key === 'Escape') { e.preventDefault(); setEditingViewPriority(null); } }}
                                 onMouseDown={(e) => e.stopPropagation()}
                                 onClick={(e) => e.stopPropagation()}
                                 inputProps={{ maxLength: CHARACTER_LIMITS.PRIORITY_NAME }}
                                 helperText={`${editingViewPriority.newName?.length ?? 0}/${CHARACTER_LIMITS.PRIORITY_NAME}`}
                                 sx={{ flex: 1 }}
                               />
                               <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: 40 }}>
                                 <Box
                                   onClick={(e) => {
                                     e.stopPropagation();
                                     setEditPriorityAnchor(e.currentTarget);
                                   }}
                                   onMouseDown={(e) => e.stopPropagation()}
                                   className="color-picker-preview"
                                   sx={{
                                     width: 24,
                                     height: 24,
                                     backgroundColor: editingViewPriority.newColor,
                                     
                                     mr: 0.5,
                                     mt: 0.5
                                   }}
                                 />
                                 <CustomColorPopover
                                   open={Boolean(editPriorityAnchor)}
                                   anchorEl={editPriorityAnchor}
                                   onClose={() => setEditPriorityAnchor(null)}
                                   colour={editingViewPriority.newColor}
                                   onChange={(color) => setEditingViewPriority(prev => ({ ...prev, newColor: color }))}
                                 />
                                 <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleUpdateViewPriority(); }} disabled={!editingViewPriority.newName?.trim() || editingViewPriority.newName.length > CHARACTER_LIMITS.PRIORITY_NAME} sx={{ color: COLORS.success }}>
                                   <Check sx={{ fontSize: 16 }} />
                                 </IconButton>
                                 <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingViewPriority(null); }} sx={{ color: COLORS.error }}>
                                   <Clear sx={{ fontSize: 16 }} />
                                 </IconButton>
                               </Box>
                             </Box>
                           ) : (
                             <Box display="flex" alignItems="center" gap={1} width="100%" overflow="hidden">
                               <FlagOutlined sx={{ fontSize: 14, color: getPriorityColor(p, { priorities: dropdowns.priorities }), flexShrink: 0 }} />
                               <OverflowTooltip
                                 title={p.name}
                                 tooltipWidth={300}
                                 placement="top-start"
                                 sx={{ fontSize: '12px', flex: 1, minWidth: 0 }}
                               />
                               {canManageTicketAttributes && (!p.status && (localPriority?.id || localPriority) !== p.id) && (
                                 <Box sx={{ display: 'flex', gap: 0.25, ml: 'auto' }}>
                                   <IconButton
                                     size="small"
                                     onClick={(e) => {
                                       e.stopPropagation();
                                       setEditingViewPriority({ ...p, newName: p.name, newColor: p.colour || COLORS.primary });
                                     }}
                                     sx={{ p: 0.25 }}
                                   >
                                     <Edit sx={{ fontSize: 14 }} />
                                   </IconButton>
                                   <IconButton 
                                     size="small" 
                                     onClick={(e) => { 
                                       e.stopPropagation(); 
                                       handleDeleteViewPriority(p); 
                                     }} 
                                     sx={{ p: 0.25 }}
                                   >
                                     <Delete sx={{ fontSize: 14 }} />
                                   </IconButton>
                                 </Box>
                               )}
                             </Box>
                           )}
                         </MenuItem>
                       ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* 6. DATES + TIME */}
                <Box className="task-detail-field-row task-detail-field-row--align-start">
                  <Box className="task-detail-field-label task-detail-field-label--pt">
                    <EventNoteOutlined sx={{ fontSize: 16, color: COLORS.gray[400] }} />
                    <Typography variant="caption" sx={{ color: COLORS.gray[500], fontWeight: 500, fontSize: '12px', textTransform: 'none' }}>
                      {t('common.dateTime')}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{ border: `1px solid ${COLORS.gray[200]}`, borderRadius: '4px', px: 1, py: 0.5, minHeight: 32, display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: COLORS.white, maxWidth: '100%', cursor: 'pointer', '&:hover': { borderColor: COLORS.primary } }}
                      onClick={(e) => setDatePickerAnchorEl(e.currentTarget)}
                    >
                      <img src={ICONS.calendar} alt="date" style={{ width: 12, height: 12, flexShrink: 0 }} />
                      <OverflowTooltip
                        title={formatDateRange(localStartDate, localDueDate, localStartTime, localDueTime) || t('taskModal.startDue')}
                        placement="top-start"
                        sx={{ color: (localStartDate || localDueDate) ? COLORS.gray[700] : COLORS.gray[400], fontSize: '10px', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}
                      />
                    </Box>
                  </Box>

                  <DatePickerModal
                    open={Boolean(datePickerAnchorEl)}
                    anchorEl={datePickerAnchorEl}
                    onClose={() => setDatePickerAnchorEl(null)}
                    mode="dateRange"
                    allowSingleSelection={true}
                    title={t('taskViewModal.selectDates', 'Select Dates')}
                    selectedStartDate={localStartDate}
                    selectedDueDate={localDueDate}
                    selectedDates={localStartDate && localDueDate ? [localStartDate, localDueDate] : (localStartDate ? [localStartDate] : (localDueDate ? [localDueDate] : []))}
                    minDays={1}
                    maxDays={3650}
                    showTime={true}
                    selectedStartTime={localStartTime}
                    selectedDueTime={localDueTime}
                    onApply={(data) => {
                      setLocalStartDate(data.fromDate || null);
                      setLocalDueDate(data.toDate || null);
                      setLocalStartTime(data.startTime || '');
                      setLocalDueTime(data.dueTime || '');
                      
                      // Now save logic via batch function
                      handleDatePickerApply(data);
                    }}
                    onClearStartDate={() => {}}
                    onClearDueDate={() => {}}
                  />
                </Box>

                {/* 7. LABELS */}
                <Box className="task-detail-field-row task-detail-field-row--align-center">
                  <Box className="task-detail-field-label">
                    <LocalOfferOutlined sx={{ fontSize: 16, color: COLORS.gray[400] }} />
                    <Typography variant="caption" sx={{ color: COLORS.gray[500], fontWeight: 500, fontSize: '12px', textTransform: 'none' }}>
                      Labels
                    </Typography>
                  </Box>
                  <FormControl size="small" sx={{ width: 'fit-content', maxWidth: 160 }}>
                    <Select
                      multiple
                      displayEmpty
                      value={localLabels}
                      onChange={(e) => {
                        const newLabels = e.target.value.filter(v => v !== '' && v != null);
                        setLocalLabels(newLabels);
                        if (!isEditingMode) {
                          setTimeout(() => handleInlineFieldSave('labels', newLabels), 0);
                        }
                      }}
                      onClose={() => { setIsCreatingViewLabel(false); setNewViewLabelName(''); setNewViewLabelColor(COLORS.primary); }}
                      renderValue={(selected) => {
                        const selectedItems = selected.map(id => dropdowns.labels?.find(l => l.id === id)).filter(Boolean);
                        if (selectedItems.length === 0) return <span style={{ color: COLORS.ui.shadow04, fontSize: '12px' }}>{t('taskModal.labels')}</span>;
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap', minWidth: 0, width: '100%', overflow: 'hidden' }}>
                            {selectedItems.slice(0, 2).map(item => (
                              <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: 80, overflow: 'hidden', minWidth: 0, flexShrink: 1 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.colour || COLORS.primary, flexShrink: 0 }} />
                                <Typography className="text-truncate" sx={{ fontSize: '12px', fontWeight: 500, color: COLORS.gray[700], whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                                  {item.name}
                                </Typography>
                              </Box>
                            ))}
                            {selectedItems.length > 2 && <Typography sx={{ fontSize: '12px', color: COLORS.gray[500], flexShrink: 0 }}>+{selectedItems.length - 2}</Typography>}
                          </Box>
                        );
                      }}
                      sx={{
                        height: 32, bgcolor: COLORS.white, fontSize: '12px', minWidth: 100, maxWidth: 160, width: '100%',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.gray[200], borderRadius: '4px' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.gray[300] },
                        '& .MuiSelect-select': { 
                          py: '0 !important', 
                          display: 'flex', 
                          alignItems: 'center', 
                          height: '100%',
                          pr: '28px !important',
                          minWidth: 0,
                          overflow: 'hidden'
                        }
                      }}
                      MenuProps={taskSelectMenuProps}
                    >
                      {!isCreatingViewLabel && (
                        <MenuItem className="create-option" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setIsCreatingViewLabel(true); }} sx={{ borderBottom: `1px solid ${COLORS.gray[200]}` }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: COLORS.primary }}><Add sx={{ fontSize: 16 }} /><span>{t('taskModal.createNewLabel')}</span></Box>
                        </MenuItem>
                      )}
                      {isCreatingViewLabel && (
                        <MenuItem disableRipple onMouseDown={(e) => e.preventDefault()} sx={{ borderBottom: `1px solid ${COLORS.gray[200]}`, py: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                            <TextField 
                              size="small" 
                              autoFocus 
                              placeholder={t('taskModal.enterLabelName')} 
                              value={newViewLabelName}
                              onChange={(e) => { e.stopPropagation(); setNewViewLabelName(e.target.value); }}
                              onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); if (!isCreatingLabelSubmitting) handleViewCreateLabel(); } if (e.key === 'Escape') { e.preventDefault(); setIsCreatingViewLabel(false); } }}
                              onMouseDown={(e) => e.stopPropagation()} 
                              onClick={(e) => e.stopPropagation()}
                              inputProps={{ maxLength: CHARACTER_LIMITS.LABEL_NAME }}
                              helperText={`${newViewLabelName.length}/${CHARACTER_LIMITS.LABEL_NAME}`}
                              sx={{ 
                                flex: 1,
                                '& .MuiInputBase-root': { height: 32, fontSize: '12px' },
                                '& .MuiOutlinedInput-input': { py: 0 }
                              }} 
                            />
                            <Box
                              onClick={(e) => {
                                e.stopPropagation();
                                setCreateLabelAnchor(e.currentTarget);
                              }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="color-picker-preview"
                    sx={{
                                width: 32,
                                height: 32,
                                backgroundColor: newViewLabelColor,
                                
                              }}
                            />
                            <CustomColorPopover
                              open={Boolean(createLabelAnchor)}
                              anchorEl={createLabelAnchor}
                              onClose={() => setCreateLabelAnchor(null)}
                              colour={newViewLabelColor}
                              onChange={setNewViewLabelColor}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: 32 }}>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleViewCreateLabel(); }} disabled={isCreatingLabelSubmitting || !newViewLabelName.trim() || newViewLabelName.length > CHARACTER_LIMITS.LABEL_NAME} sx={{ color: COLORS.success, p: 0.5 }}>
                                <Check sx={{ fontSize: 18 }} />
                              </IconButton>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setIsCreatingViewLabel(false); setNewViewLabelName(''); }} sx={{ color: COLORS.error, p: 0.5 }}>
                                <Clear sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>
                          </Box>
                        </MenuItem>
                      )}
                      {(dropdowns.labels || []).map(label => (
                        <MenuItem key={label.id} value={editingViewLabel?.id === label.id ? '' : label.id} sx={{ maxWidth: 300, overflow: 'hidden' }}>
                          {editingViewLabel?.id === label.id ? (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }} onClick={(e) => e.stopPropagation()}>
                              <TextField 
                                size="small" 
                                autoFocus 
                                value={editingViewLabel.newName}
                                onChange={(e) => { e.stopPropagation(); setEditingViewLabel(prev => ({ ...prev, newName: e.target.value })); }}
                                onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); handleUpdateViewLabel(); } if (e.key === 'Escape') { e.preventDefault(); setEditingViewLabel(null); } }}
                                onMouseDown={(e) => e.stopPropagation()} 
                                onClick={(e) => e.stopPropagation()}
                                inputProps={{ maxLength: CHARACTER_LIMITS.LABEL_NAME }}
                                helperText={`${editingViewLabel.newName?.length ?? 0}/${CHARACTER_LIMITS.LABEL_NAME}`}
                                sx={{ 
                                  flex: 1,
                                  '& .MuiInputBase-root': { height: 32, fontSize: '12px' },
                                  '& .MuiOutlinedInput-input': { py: 0 }
                                }} 
                              />
                              <Box
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditLabelAnchor(e.currentTarget);
                                }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="color-picker-preview"
                    sx={{
                                  width: 24,
                                  height: 24,
                                  backgroundColor: editingViewLabel.newColor,
                                  
                                  mt: 0.5,
                                }}
                              />
                              <CustomColorPopover
                                open={Boolean(editLabelAnchor)}
                                anchorEl={editLabelAnchor}
                                onClose={() => setEditLabelAnchor(null)}
                                colour={editingViewLabel.newColor}
                                onChange={(color) => setEditingViewLabel(prev => ({ ...prev, newColor: color }))}
                              />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: 32 }}>
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleUpdateViewLabel(); }} disabled={!editingViewLabel.newName?.trim() || editingViewLabel.newName.length > CHARACTER_LIMITS.LABEL_NAME} sx={{ color: COLORS.success, p: 0.5 }}>
                                  <Check sx={{ fontSize: 18 }} />
                                </IconButton>
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingViewLabel(null); }} sx={{ color: COLORS.error, p: 0.5 }}>
                                  <Clear sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Box>
                            </Box>
                          ) : (
                            <Box display="flex" alignItems="center" gap={1} width="100%" overflow="hidden">
                              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: label.colour || COLORS.primary, flexShrink: 0 }} />
                              <OverflowTooltip
                                title={label.name}
                                tooltipWidth={300}
                                placement="top-start"
                                sx={{ fontSize: '12px', flex: 1, minWidth: 0 }}
                              />
                              {localLabels.includes(label.id) && <Check sx={{ fontSize: 14, ml: 'auto', color: COLORS.primary, flexShrink: 0, alignSelf: 'center' }} />}
                              {canManageTicketAttributes && !label.status && (
                                <Box sx={{ display: 'flex', gap: 0.25, ml: 'auto' }}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingViewLabel({ ...label, newName: label.name, newColor: label.colour || COLORS.primary });
                                    }}
                                    sx={{ p: 0.25 }}
                                  >
                                    <Edit sx={{ fontSize: 14 }} />
                                  </IconButton>
                                  <IconButton 
                                    size="small" 
                                    onClick={(e) => { 
                                      e.stopPropagation(); 
                                      handleDeleteViewLabel(label); 
                                    }} 
                                    sx={{ p: 0.25 }}
                                  >
                                    <Delete sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Box>
                              )}
                            </Box>
                          )}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* 8. SUBTASK OF */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                  <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 112 }}>
                    <HubOutlined sx={{ fontSize: 16, color: COLORS.gray[400] }} />
                    <Typography variant="caption" sx={{ color: COLORS.gray[500], fontWeight: 500, fontSize: '12px', textTransform: 'none' }}>
                      {t('taskModal.subtaskOf', 'Subtask Of')}
                    </Typography>
                  </Box>
                  {true ? (
                    <Autocomplete
                      size="small"
                      sx={{ 
                        minWidth: 120, 
                        maxWidth: 320, 
                        bgcolor: COLORS.white,
                        '& .MuiOutlinedInput-root': {
                          height: 32,
                          py: 0,
                          fontSize: '12px',
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.gray[200], borderRadius: '4px' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.gray[300] }
                        }, 
                        '& .MuiAutocomplete-inputRoot': { py: '0 !important', pr: '24px !important' }, 
                        '& .MuiAutocomplete-input': { py: '0 !important', fontSize: '12px' }
                      }}
                      slotProps={{
                        listbox: {
                          sx: {
                            '& .MuiAutocomplete-option': {
                              fontSize: '12px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: 'block'
                            }
                          }
                        },
                        popper: {
                          sx: {
                            '& .MuiAutocomplete-paper': {
                              width: 'fit-content',
                              minWidth: '100px',
                              maxWidth: '150px'
                            }
                          }
                        }
                      }}
                      renderOption={(props, option) => {
                        const { key, ...optionProps } = props;
                        const label = option.isNone ? t('taskModal.none', 'None') : (typeof option === 'string' ? option : `${option.ticket_number || option.number || ''} - ${option.title || option.name || option.ticket_title || ''}`.replace(/^ - /, ''));
                        return (
                          <li key={key} {...optionProps}>
                            <OverflowTooltip title={label} sx={{ fontSize: '12px', width: '100%' }} />
                          </li>
                        );
                      }}
                      options={parentTicketOptions}
                      loading={isParentTicketLoading}
                      disableClearable={true}
                      value={localParentTicket?.isNone ? null : localParentTicket}
                      onChange={(_, selectedOption) => {
                        const val = selectedOption || { id: '', isNone: true };
                        setLocalParentTicket(val);
                        if (!isEditingMode) {
                          const parentId = val?.isNone ? null : (val?.id || null);
                          setTimeout(() => handleInlineFieldSave('parent_ticket_id', parentId), 0);
                        }
                      }}
                      onInputChange={(_, value, reason) => {
                        if (reason === 'input' || reason === 'clear') {
                          setParentTicketSearchTerm(value);
                        }
                      }}
                      openOnFocus
                      filterOptions={(options) => options}
                      isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                      getOptionLabel={(option) => {
                        if (!option) return '';
                        if (option.isNone) return t('taskModal.none', 'None');
                        if (typeof option === 'string') return option;
                        const ticketNumber = option.ticket_number || option.number;
                        const title = option.title || option.name || option.ticket_title || '';
                        return ticketNumber ? `${ticketNumber} - ${title}` : title;
                      }}
                      noOptionsText={parentTicketSearchTerm ? t('taskModal.noTasksFound', 'No tasks found') : t('taskModal.noTicketsAvailable', 'No tickets available')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={t('taskModal.searchTask', 'Search task')}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {isParentTicketLoading ? <CircularProgress color="inherit" size={14} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                    />
                  ) : null}
                </Box>

                {/* 9. REPORTER */}
                <Box className="task-detail-field-row task-detail-field-row--align-center">
                  <Box className="task-detail-field-label">
                    <PersonOutline sx={{ fontSize: 16, color: COLORS.gray[400] }} />
                    <Typography variant="caption" sx={{ color: COLORS.gray[500], fontWeight: 500, fontSize: '12px', textTransform: 'none' }}>
                      {t('taskViewModal.reporter', 'Reporter')}
                    </Typography>
                  </Box>
                  <FormControl size="small" sx={{ minWidth: 120, maxWidth: 160, width: '100%' }}>
                    <Select
                      value={localReporter?.id || ''}
                      displayEmpty
                      onChange={(e) => {
                        const reporterId = e.target.value;
                        const nextReporter = dropdowns.assignees?.find(assignee => assignee.id === reporterId) || null;
                        setLocalReporter(nextReporter);
                        if (!isEditingMode) {
                          setTimeout(() => handleInlineFieldSave('reporter_id', reporterId || null), 0);
                        }
                      }}
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <OverflowTooltip
                              title={t('taskViewModal.notSet')}
                              placement="top-start"
                              sx={{ fontSize: '12px', color: COLORS.gray[400], fontWeight: 400 }}
                            />
                          );
                        }

                        const selectedReporter = dropdowns.assignees?.find(assignee => assignee.id === selected) || localReporter;
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%', overflow: 'hidden' }}>
                            <Avatar
                              sx={{
                                width: 22,
                                height: 22,
                                fontSize: '10px',
                                bgcolor: stringToColor(selectedReporter?.name || ''),
                                fontWeight: 600,
                                flexShrink: 0
                              }}
                            >
                              {getAvatarInitials(selectedReporter?.name)}
                            </Avatar>
                            <OverflowTooltip
                              title={selectedReporter?.name || t('taskViewModal.notSet')}
                              tooltipWidth={360}
                              placement="top-start"
                              sx={{ fontSize: '12px', fontWeight: 500, color: COLORS.gray[700], flex: 1, minWidth: 0 }}
                            />
                          </Box>
                        );
                      }}
                      sx={{
                        fontSize: '12px',
                        height: 32,
                        bgcolor: COLORS.white,
                        minWidth: 120,
                        maxWidth: 160,
                        width: '100%',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.gray[200], borderRadius: '4px' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.gray[300] },
                        '& .MuiSelect-select': {
                          py: '0 !important',
                          pr: '32px !important',
                          pl: '8px !important',
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: 0,
                          overflow: 'hidden',
                          height: '100%'
                        }
                      }}
                      MenuProps={taskSelectMenuProps}
                    >
                      <MenuItem value="">
                        <Typography sx={{ fontSize: '12px', color: COLORS.gray[500] }}>
                          {t('taskViewModal.notSet')}
                        </Typography>
                      </MenuItem>
                      {(dropdowns.assignees || []).map(assignee => (
                        <MenuItem key={assignee.id} value={assignee.id} sx={{ fontSize: '12px', maxWidth: 320 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
                            <Avatar
                              sx={{
                                width: 24,
                                height: 24,
                                fontSize: '10px',
                                bgcolor: stringToColor(assignee.name || ''),
                                fontWeight: 600,
                                flexShrink: 0
                              }}
                            >
                              {getAvatarInitials(assignee.name)}
                            </Avatar>
                            <OverflowTooltip
                              title={assignee.name}
                              tooltipWidth={360}
                              placement="top-start"
                              className="overflow-tooltip-dropdown"
                              sx={{ maxWidth: '100%' }}
                            />
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* 10. TIME ESTIMATE / STORY POINTS */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                  {isScrum ? (
                    <>
                      <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 133 }}>
                        <AccessTimeOutlined sx={{ fontSize: 16, color: COLORS.gray[400] }} />
                        <Typography variant="caption" sx={{ color: COLORS.gray[500], fontWeight: 500, fontSize: '12px', textTransform: 'none' }}>
                          Story Points
                        </Typography>
                      </Box>
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ min: 1, max: 40, tabIndex: !isEditingMode ? -1 : 0 }}
                        value={isEditingMode ? localStoryPoints : (localStoryPoints || t('taskViewModal.notSet'))}
                        onChange={(e) => setLocalStoryPoints(e.target.value.replace(REGEX.nonNumeric, ''))}
                        onBlur={() => {
                          if (isEditingMode) {
                            if (localStoryPoints !== '' && localStoryPoints !== null) {
                              const val = Number(localStoryPoints);
                              if (isNaN(val) || val < 1 || val > 40) {
                                toast.error(t('taskModal.storyPointsInvalid'));
                                setLocalStoryPoints(ticketDetails?.story_points || taskData?.story_points || '');
                              }
                            }
                          }
                        }}
                        onKeyDown={async (e) => {
                          if (RESTRICTED_NUMBER_KEYS.includes(e.key)) {
                            e.preventDefault();
                          }
                          if (e.key === 'Enter') {
                            e.target.blur();
                          }
                        }}
                        placeholder="e.g. 3"
                        sx={{ pointerEvents: !isEditingMode ? 'none' : 'auto' }}
                        InputProps={{
                          readOnly: !isEditingMode,
                          startAdornment: (
                            <InputAdornment position="start" sx={{ mr: 0.5 }}>
                              <img src={ICONS.estimation} alt="estimate" style={{ width: 13, height: 13, opacity: isEditingMode ? 1 : 0.55 }} />
                            </InputAdornment>
                          )
                        }}
                        className={`estimation-input ${isEditingMode ? 'estimation-input--editing' : ''}`}
                      />
                    </>
                  ) : (
                    <>
                      <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 133 }}>
                        <AccessTimeOutlined sx={{ fontSize: 16, color: COLORS.gray[400] }} />
                        <Typography variant="caption" sx={{ color: COLORS.gray[500], fontWeight: 500, fontSize: '12px', textTransform: 'none' }}>
                          Time estimate
                        </Typography>
                      </Box>
                      <TextField
                        size="small"
                        inputProps={{ tabIndex: !isEditingMode ? -1 : 0 }}
                        value={isEditingMode ? localEstimate : (localEstimate || t('taskViewModal.notSet'))}
                        onChange={(e) => {
                          const val = e.target.value;
                          const filteredVal = val.replace(/[^0-9hmHM ]/g, '');
                          setLocalEstimate(filteredVal);
                        }}
                        onBlur={() => {
                          const error = validateTimeDigitLimits(localEstimate);
                          if (error) {
                            setLocalEstimate(minutesToTimeString(ticketDetails?.estimate_time_in_minutes || taskData?.estimate_time_in_minutes || 0));
                            return;
                          }
                          
                          if (isValidTimeFormat(localEstimate)) {
                            const minutes = timeStringToMinutes(localEstimate);
                            setLocalEstimate(minutesToTimeString(minutes));
                          } else {
                            setLocalEstimate(minutesToTimeString(ticketDetails?.estimate_time_in_minutes || taskData?.estimate_time_in_minutes || 0));
                          }
                        }}
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter') {
                            e.target.blur();
                          }
                        }}
                        error={!!(isEditingMode && localEstimate && (validateTimeDigitLimits(localEstimate) || !isValidTimeFormat(localEstimate)))}
                        helperText={isEditingMode && localEstimate && (validateTimeDigitLimits(localEstimate) || (!isValidTimeFormat(localEstimate) ? "Use '1h 30m' format" : ""))}
                        placeholder="0h 0m"
                        sx={{ pointerEvents: !isEditingMode ? 'none' : 'auto' }}
                        InputProps={{
                          readOnly: !isEditingMode,
                          startAdornment: (
                            <InputAdornment position="start" sx={{ mr: 0.5 }}>
                              <img src={ICONS.estimation} alt="estimate" style={{ width: 13, height: 13, opacity: isEditingMode ? 1 : 0.55 }} />
                            </InputAdornment>
                          )
                        }}
                        className={`estimation-input ${isEditingMode ? 'estimation-input--editing' : ''}`}
                      />
                    </>
                  )}
                </Box>
              </Box>
              </Box>

              <RecurrencePanel
                isRecurring={localIsRecurring}
                recurrenceConfig={localRecurrenceConfig}
                initialRecurrenceConfig={ticketDetails?.recurrence_config || taskData?.recurrence_config}
                nextRepOn={ticketDetails?.next_rep_on || taskData?.next_rep_on}
                readOnly={!isEditingMode}
                compact={!fullScreen}
                onChange={({ isRecurring, recurrenceConfig }) => {
                  setLocalIsRecurring(isRecurring);
                  setLocalRecurrenceConfig(isRecurring ? recurrenceConfig : null);
                }}
              />

              <Box className={`task-detail-section ${isEditingMode ? 'task-detail-section-editing' : ''}`} sx={{ mb: 4 }}>
                <Box
                  className={`task-detail-section-header task-detail-section-header-description ${!isEditingMode && isDescriptionExpanded ? 'expanded' : 'collapsed'}`}
                  role={isEditingMode ? undefined : 'button'}
                  tabIndex={isEditingMode ? -1 : 0}
                  aria-expanded={isEditingMode || isDescriptionExpanded}
                  onClick={isEditingMode ? undefined : () => setIsDescriptionExpanded(prev => !prev)}
                  onKeyDown={isEditingMode ? undefined : (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setIsDescriptionExpanded(prev => !prev);
                    }
                  }}
                >
                  <Typography variant="h6" fontWeight="400" className="task-detail-section-title">
                    {t('taskViewModal.description', 'Description')}
                  </Typography>
                  {!isEditingMode && (
                    <Box component="span" className="task-detail-section-toggle" aria-hidden="true">
                      {isDescriptionExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </Box>
                  )}
                </Box>
                {isEditingMode ? (
                  <Box className="task-detail-section-body task-detail-section-body-editing">
                    <CommentBox
                      key={`description-${taskData?.id}-${isEditingMode}`}
                      ref={descriptionRef}
                      hideActions={true}
                      persistent={true}
                      mentionUsers={mentionUsers}
                      mentionOffset={descriptionMentionOffset}
                      initialContent={decodeHtmlContent(descriptionContent)}
                      initialAttachments={ticketDetails?.attachments?.filter(a => a.type === 'description') || []}
                      onSubmit={(newVal) => setLocalDescription(newVal)}
                      onContentChange={(newVal) => setLocalDescription(newVal)}
                      onAttachmentsChange={(attachments) => setHasPendingDescriptionAttachments((attachments || []).length > 0)}
                      ticketId={taskData?.id}
                      fieldName="description"
                      enableAutoSave={true}
                      disableAttachmentRemoval={isSavingTaskChanges}
                    />
                  </Box>
                ) : (
                  <Collapse in={isDescriptionExpanded} timeout="auto" unmountOnExit={false}>
                    <Box className="description-content task-detail-section-body" sx={{
                      lineHeight: 1.7,
                      color: COLORS.gray[800],
                      p: 1.5,
                      bgcolor: COLORS.gray[50],
                      borderRadius: 1,
                      border: `1px solid ${COLORS.gray[200]}`,
                      fontStyle: descriptionContent ? 'normal' : 'italic',
                      '& h1, & h2, & h3, & h4, & h5, & h6': {
                        fontWeight: 600,
                        margin: '16px 0 8px 0',
                        color: COLORS.gray[800]
                      },
                      '& h1': { fontSize: '1.5rem' },
                      '& h2': { fontSize: '1.25rem' },
                      '& h3': { fontSize: '1.1rem' },
                      '& p': { margin: '8px 0' },
                      '& ul, & ol': { paddingLeft: '20px', margin: '8px 0' },
                      '& li': { margin: '4px 0' },
                      '& strong': { fontWeight: 600 },
                      '& em': { fontStyle: 'italic' },
                      '& code': {
                        backgroundColor: COLORS.gray[100],
                        padding: '2px 4px',
                        borderRadius: '3px',
                        fontFamily: 'monospace',
                        fontSize: '0.9em'
                      },
                      '& pre': {
                        backgroundColor: COLORS.gray[100],
                        padding: '12px',
                        borderRadius: '8px',
                        overflowX: 'auto',
                        margin: '12px 0',
                        fontFamily: 'monospace',
                        fontSize: '0.9em'
                      }
                    }}>
                      {descriptionContent ? (
                        <div dangerouslySetInnerHTML={{ __html: renderHtmlContentWithMentions(descriptionContent, mentionUsers) }} />
                      ) : (
                        <Typography variant="body1" sx={{ fontStyle: 'italic', color: COLORS.gray[500] }}>
                          {t('taskViewModal.noDescription', 'No description provided')}
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                )}
              </Box>

              {/* Acceptance Criteria Section */}
              <Box className={`task-detail-section ${isEditingMode ? 'task-detail-section-editing' : ''}`} sx={{ mb: 4 }}>
                <Box
                  className={`task-detail-section-header task-detail-section-header-acceptance ${!isEditingMode && isAcceptanceCriteriaExpanded ? 'expanded' : 'collapsed'}`}
                  role={isEditingMode ? undefined : 'button'}
                  tabIndex={isEditingMode ? -1 : 0}
                  aria-expanded={isEditingMode || isAcceptanceCriteriaExpanded}
                  onClick={isEditingMode ? undefined : () => setIsAcceptanceCriteriaExpanded(prev => !prev)}
                  onKeyDown={isEditingMode ? undefined : (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setIsAcceptanceCriteriaExpanded(prev => !prev);
                    }
                  }}
                >
                  <Typography variant="h6" fontWeight="400" className="task-detail-section-title">
                    {t('taskViewModal.acceptanceCriteria')}
                  </Typography>
                  {!isEditingMode && (
                    <Box component="span" className="task-detail-section-toggle" aria-hidden="true">
                      {isAcceptanceCriteriaExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </Box>
                  )}
                </Box>
                {isEditingMode ? (
                  <Box className="task-detail-section-body task-detail-section-body-editing">
                    <CommentBox
                      key={`acceptanceCriteria-${taskData?.id}-${isEditingMode}`}
                      ref={acceptanceCriteriaRef}
                      hideAttachments={true}
                      hideActions={true}
                      persistent={true}
                      mentionUsers={mentionUsers}
                      mentionOffset={descriptionMentionOffset}
                      initialContent={decodeHtmlContent(acceptanceCriteriaContent)}
                      onSubmit={(newVal) => setLocalAcceptanceCriteria(newVal)}
                      onContentChange={(newVal) => setLocalAcceptanceCriteria(newVal)}
                      placeholder={t('taskViewModal.addAcceptanceCriteria')}
                      ticketId={taskData?.id}
                      fieldName="acceptanceCriteria"
                      enableAutoSave={true}
                    />
                  </Box>
                ) : (
                  <Collapse in={isAcceptanceCriteriaExpanded} timeout="auto" unmountOnExit={false}>
                    <Box className="description-content acceptance-criteria-content task-detail-section-body" sx={{
                      color: COLORS.gray[800],
                      minHeight: '60px',
                      p: 1.5,
                      bgcolor: COLORS.gray[50],
                      borderRadius: 1,
                      border: `1px solid ${COLORS.gray[200]}`,
                      fontStyle: acceptanceCriteriaContent ? 'normal' : 'italic'
                    }}>
                      {acceptanceCriteriaContent ? (
                        <div dangerouslySetInnerHTML={{ __html: renderHtmlContentWithMentions(acceptanceCriteriaContent, mentionUsers) }} />
                      ) : (
                        <Typography variant="body1" sx={{ fontStyle: 'italic', color: COLORS.gray[500] }}>
                          {t('taskViewModal.noAcceptanceCriteria', 'No Acceptance Criteria provided')}
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                )}
              </Box>

                  {/* Combined Save/Cancel Bar */}
                  {isEditingMode && (
                    <Box sx={{ 
                      mt: -2, 
                      mb: 4, 
                      p: 2, 
                      display: 'flex', 
                      justifyContent: 'flex-end', 
                      gap: 2,
                      borderTop: `1px solid ${COLORS.gray[100]}`
                    }}>
                      <Button 
                        variant="text" 
                        onClick={handleCancelEdit}
                        disabled={isSavingTaskChanges}
                        sx={{ color: COLORS.gray[600], textTransform: 'none', fontWeight: 600 }}
                      >
                        {t('common.cancel', 'Cancel')}
                      </Button>
                      <Button 
                        variant="contained" 
                        onClick={handleSaveAll}
                        disabled={isSavingTaskChanges || isUploadingFiles || !hasInlineTaskChanges}
                        sx={{ 
                          bgcolor: COLORS.primary, 
                          color: COLORS.white, 
                          textTransform: 'none', 
                          fontWeight: 600, 
                          px: 3,
                          '&:hover': { bgcolor: COLORS.primaryHover }
                        }}
                      >
                        {isSavingTaskChanges ? <CircularProgress size={18} sx={{ color: COLORS.white, mr: 1 }} /> : null}
                        {t('common.saveChanges', 'Save changes')}
                      </Button>
                    </Box>
                  )}

                  {/* QUICK ACTIONS LIST */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 4 }}>
                    {/* 1. SUBTASKS */}
                    <Box sx={{ mb: expandedSubtasks ? 2 : 0 }}>
                      {expandedSubtasks ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {/* Floating Header */}
                          <Box sx={{ 
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
                            bgcolor: COLORS.subtask.headerBg, 
                            mx: -0.5, 
                            px: 1, 
                            py: 1, 
                            boxShadow: '0px 3px 10px rgba(0, 0, 0, 0.25)',
                            borderRadius: '6px',
                            zIndex: 1
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Close sx={{ fontSize: 16, cursor: 'pointer', color: COLORS.gray[600] }} onClick={() => setExpandedSubtasks(false)} />
                              <img src={ICONS.addSubtask} alt="subtasks" style={{ width: 22, height: 22 }} />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="h6" fontWeight="400" sx={{ color: COLORS.gray[800], lineHeight: 1.2 }}>
                                  {`${t('taskViewModal.addSubtasks', 'Add Subtasks')} (${localSubtasks.length})`}
                                </Typography>
                                <Typography variant="body2" sx={{ color: COLORS.gray[500], fontWeight: 400, whiteSpace: 'nowrap' }}>
                                  {localSubtasks.filter(st => !st.is_completed && !st.completed && !st.marked_as_completed && st.status_name !== 'Completed' && st.status_name !== 'Done').length} open
                                </Typography>
                              </Box>
                              <Box sx={{ width: '100px', ml: 1, display: 'flex', alignItems: 'center' }}>
                                <SubtaskProgressBar subtasks={localSubtasks} hideText={true} />
                              </Box>
                            </Box>
                            <Tooltip title={t('taskViewModal.addSubtask', 'Add Subtask')} slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenAddSubtask(null)}
                                sx={{ p: 0.5, bgcolor: COLORS.white, borderRadius: '4px', border: `1px solid ${COLORS.gray[300]}`, '&:hover': { bgcolor: COLORS.gray[50] } }}
                              >
                                <Add sx={{ fontSize: 18, color: COLORS.gray[700] }} />
                              </IconButton>
                            </Tooltip>
                          </Box>

                          {/* Table Box */}
                          <Box sx={{ border: `1px solid ${COLORS.ui.border}`, borderRadius: '6px', overflow: 'hidden', bgcolor: COLORS.white }}>
                            {/* Subtask Table Headers */}
                            <Box sx={{ 
                              ...checklistStyles.tableHeader, 
                              gap: (fullScreen || window.location.pathname.startsWith('/task/')) ? 3 : 0, 
                              borderTop: 'none', 
                              borderBottom: `1px solid ${COLORS.ui.border}`, 
                              bgcolor: COLORS.white,
                              '& .MuiTypography-root': {
                                fontSize: (fullScreen || window.location.pathname.startsWith('/task/')) ? '0.95rem' : '0.85rem',
                                fontWeight: 800
                              }
                            }}>
                              <Box sx={{ flex: 1 }}><Typography variant="caption" sx={{ fontWeight: 800, color: COLORS.gray[500] }}>{t('common.name', 'Name')}</Typography></Box>
                              <Box sx={{ width: (fullScreen || window.location.pathname.startsWith('/task/')) ? 120 : 100, textAlign: 'center' }}><Typography variant="caption" sx={{ fontWeight: 800, color: COLORS.gray[500] }}>{t('common.status', 'Status')}</Typography></Box>
                              <Box sx={{ width: 80, textAlign: 'center' }}><Typography variant="caption" sx={{ fontWeight: 800, color: COLORS.gray[500] }}>{t('taskModal.assigneeShort', 'Assignee')}</Typography></Box>
                              <Box sx={{ width: 60, textAlign: 'center' }}><Typography variant="caption" sx={{ fontWeight: 800, color: COLORS.gray[500] }}>{t('taskModal.priorityShort', 'Priority')}</Typography></Box>
                              <Box sx={{ width: 126, textAlign: 'center' }}><Typography variant="caption" sx={{ fontWeight: 800, color: COLORS.gray[500] }}>{t('taskModal.dueDateShort', 'Due Date')}</Typography></Box>
                              <Box sx={{ width: 104, textAlign: 'center' }}><Typography variant="caption" sx={{ fontWeight: 800, color: COLORS.gray[500] }}>{t('common.actions', 'Actions')}</Typography></Box>
                            </Box>

                            <Box>
                              {localSubtasks.length > 0 ? (
                                localSubtasks.map((subtask) => (
                                  <NestedSubtaskDrawer
                                    key={subtask.id}
                                    fullScreen={fullScreen || window.location.pathname.startsWith('/task/')}
                                    readOnly={!isEditingMode}
                                    subtask={{
                                      id: subtask.id,
                                      title: subtask.title,
                                      ticket_number: subtask.ticket_number,
                                      status: subtask.status_name,
                                      project: subtask.project_name,
                                      subtask_count: subtask.subtask_count || 0,
                                      marked_as_completed: subtask.marked_as_completed,
                                      completed: subtask.completed,
                                      is_completed: subtask.is_completed,
                                      estimate_time_in_minutes: subtask.estimate_time_in_minutes,
                                      assignees: subtask.dataValues?.assignees || subtask.assignees || [],
                                      assignee_initials: (subtask.dataValues?.assignees?.[0]?.name || subtask.assignees?.[0]?.name)?.substring(0, 2).toUpperCase() || 'U',
                                      priority: subtask.priority || subtask.priority_id || subtask.dataValues?.priority_name || subtask.priority_name,
                                      priority_color: subtask.priority?.colour || subtask.priority?.color || subtask.priority_color,
                                      due_date: subtask.dataValues?.end_date || subtask.end_date || subtask.due_date,
                                      labels: subtask.labels || []
                                    }}
                                    onCompletionChange={handleSubtaskCompletionChange}
                                    onAddSubtask={isEditingMode ? handleOpenAddSubtask : undefined}
                                    onLabelsUpdate={isEditingMode ? (async (taskId, newLabels) => {
                                      try {
                                        await dispatch(updateTicketPartial({ 
                                          ticketId: taskId, 
                                          ticketData: { labels: newLabels } 
                                        })).unwrap();
                                        // Update local state for immediate feedback
                                        setLocalSubtasks(prev => prev.map(st => 
                                          st.id === taskId ? { ...st, labels: newLabels } : st
                                        ));
                                      } catch (error) {
                                        console.error('Failed to update subtask labels:', error);
                                      }
                                    }) : undefined}
                                    onSubtaskUpdate={handleInlineSubtaskUpdate}
                                    level={0}
                                    onEdit={isEditingMode ? (async (subtaskData) => {
                                      try {
                                        const editData = await fetchAndFormatTicketForEdit(dispatch, subtaskData.id);
                                        openEditSubtaskModal(editData);
                                      } catch (error) {
                                        openEditSubtaskModal({ id: subtaskData.id, title: subtaskData.title });
                                      }
                                    }) : undefined}
                                    onDelete={isEditingMode ? ((subtaskData) => {
                                      openDeleteSubtaskModal({ id: subtaskData.id, title: subtaskData.title });
                                    }) : undefined}
                                  />
                                ))
                              ) : (
                                <Box sx={{ p: 1.5, textAlign: 'center' }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>{t('taskViewModal.noSubtasksYet')}</Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      ) : (
                        <Box 
                          onClick={() => setExpandedSubtasks(true)}
                          sx={COLLAPSED_HEADER_SX}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <img src={ICONS.addSubtask} alt="subtasks" style={{ width: 22, height: 22 }} />
                            <Typography variant="h6" sx={{ fontWeight: 500, color: COLORS.gray[800] }}>
                              {`${t('taskViewModal.addSubtasks', 'Subtasks')} (${localSubtasks.length})`}
                            </Typography>
                          </Box>
                          <ChevronRight sx={{ fontSize: 20, color: COLORS.gray[400], transition: 'transform 0.2s' }} />
                        </Box>
                      )}
                    </Box>

                    {/* 2. ATTACHMENTS */}
                    <Box sx={{ mb: expandedAttachments ? 2 : 0 }}>
                      {expandedAttachments ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {/* Floating Header */}
                           <Box sx={{ 
                             display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
                             bgcolor: COLORS.subtask.headerBg, 
                             mx: -0.25, 
                             px: 1.5, 
                             py: 1, 
                             boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                             border: `1px solid ${COLORS.gray[200]}`,
                             borderRadius: '8px',
                             zIndex: 1
                           }} onClick={(e) => e.stopPropagation()}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Close sx={{ fontSize: 16, cursor: 'pointer', color: COLORS.gray[600] }} onClick={() => setExpandedAttachments(false)} />
                              {isUploadingFiles ? <CircularProgress size={22} /> : <img src={ICONS.attachFile} alt="attachments" style={{ width: 22, height: 22 }} />}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
                                <Typography variant="h6" fontWeight="400" sx={{ color: COLORS.gray[800], lineHeight: 1.2, overflow: 'hidden' }}>
                                  {isUploadingFiles 
                                    ? t('taskModal.uploadingFiles', 'Uploading...') 
                                    : `${t('taskViewModal.attachFile', 'Attach File')} (${attachmentCount})`
                                  }
                                </Typography>
                              </Box>
                            </Box>
                            <Box 
                              onClick={(e) => { e.stopPropagation(); setAttachmentMenuAnchor(e.currentTarget); }}
                              sx={{ bgcolor: COLORS.white, borderRadius: '4px', border: `1px solid ${COLORS.gray[300]}`, '&:hover': { bgcolor: COLORS.gray[50] }, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: isUploadingFiles ? 'not-allowed' : 'pointer', opacity: isUploadingFiles ? 0.6 : 1 }}
                            >
                              <IconButton size="small" sx={{ p: 0.5 }}>
                                <Add sx={{ fontSize: 18, color: COLORS.gray[600] }} />
                              </IconButton>
                            </Box>
                            <Menu
                              anchorEl={attachmentMenuAnchor}
                              open={Boolean(attachmentMenuAnchor)}
                              onClose={() => setAttachmentMenuAnchor(null)}
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            >
                              {(() => {
                                 const attachmentsList = ticketDetails?.attachments || taskData.attachments || [];
                                 const hasAttachments = attachmentsList.length > 0;
                                 return (
                                   <>
                                     <MenuItem 
                                       disabled={!hasAttachments}
                                       onClick={async () => { 
                                         setAttachmentMenuAnchor(null);
                                         for (const file of attachmentsList) {
                                           await triggerAttachmentDownload(file, { t });
                                         }
                                       }}
                                     >
                                       <ListItemIcon><Download fontSize="small" /></ListItemIcon>
                                       <Typography variant="body2">{t('taskViewModal.download', 'Download')}</Typography>
                                     </MenuItem>
                                     <MenuItem 
                                       disabled={!hasAttachments}
                                       onClick={() => { setAttachmentMenuAnchor(null); setAttachmentViewMode('tiles'); }}
                                     >
                                       <ListItemIcon><GridView fontSize="small" /></ListItemIcon>
                                       <Typography variant="body2">{t('taskViewModal.tiles', 'Tiles')}</Typography>
                                     </MenuItem>
                                     <MenuItem 
                                       disabled={!hasAttachments}
                                       onClick={() => { setAttachmentMenuAnchor(null); setAttachmentViewMode('list'); }}
                                     >
                                       <ListItemIcon><ViewList fontSize="small" /></ListItemIcon>
                                       <Typography variant="body2">{t('taskViewModal.list', 'List View')}</Typography>
                                     </MenuItem>
                                   </>
                                 );
                               })()}
                              <Divider />
                              <MenuItem onClick={() => { 
                                setAttachmentMenuAnchor(null); 
                                document.getElementById('description-file-upload-main')?.click(); 
                              }}>
                                <ListItemIcon><Add fontSize="small" /></ListItemIcon>
                                <Typography variant="body2">{t('taskViewModal.addAttachment', 'Upload File')}</Typography>
                              </MenuItem>
                            </Menu>
                          </Box>
                          <Box sx={{ p: 2, bgcolor: COLORS.white, border: `1px solid ${COLORS.ui.border}`, borderRadius: '6px' }}>
                            <input id="description-file-upload-main" type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.svg,.webp,.zip,.mp4,.mov,.avi,.webm,.mkv" hidden multiple onChange={handleFileUpload} disabled={isUploadingFiles} />
                            <Box sx={{ display: 'flex', flexWrap: attachmentViewMode === 'tiles' ? 'wrap' : 'nowrap', flexDirection: attachmentViewMode === 'tiles' ? 'row' : 'column', gap: 1.5 }}>
                              {(ticketDetails?.attachments || taskData.attachments || []).length === 0 && (
                                <Box sx={{ py: 1, textAlign: 'center', width: '100%', color: COLORS.gray[500] }}>
                                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                    {t('taskViewModal.noAttachments', 'No attachment added')}
                                  </Typography>
                                </Box>
                              )}
                              {(ticketDetails?.attachments || taskData.attachments || []).map((file, index) => {
                                const fileName = getAttachmentName(file, t('taskViewModal.attachment', 'Attachment'));
                                const fileUrl = getAttachmentUrl(file);
                                const createdAt = typeof file === 'object' && file.created_at ? new Date(file.created_at) : new Date();
                                const dateStr = `${createdAt.getDate()}/${createdAt.toLocaleString('en-US', { month: 'short' }).split(' ')[0]}/${createdAt.getFullYear().toString().slice(-2)}`;
                                const userName = typeof file === 'object' && file.user_name ? file.user_name : (user?.first_name || 'U');
                                const userInitials = userName.substring(0, 2).toUpperCase();
                                const isImage = fileName.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                                
                                return attachmentViewMode === 'tiles' ? (
                                  <Box key={index} 
                                    onClick={() => fileUrl && window.open(fileUrl, '_blank')}
                                    sx={{ 
                                      position: 'relative', width: 140, border: `1px solid ${COLORS.gray[200]}`, 
                                      borderRadius: 2, overflow: 'hidden', bgcolor: COLORS.white, 
                                      cursor: fileUrl ? 'pointer' : 'default', display: 'flex', flexDirection: 'column',
                                      '&:hover .attachment-actions': { opacity: 1 },
                                      '&:focus-within .attachment-actions': { opacity: 1 }
                                    }}
                                  >
                                    <Box className="attachment-actions" sx={{ opacity: 0, position: 'absolute', top: 4, right: 4, zIndex: 10, transition: 'opacity 0.2s' }}>
                                      <IconButton 
                                        size="small" 
                                        onClick={(e) => { e.stopPropagation(); setActiveAttachment(file); setActiveAttachmentMenuAnchor(e.currentTarget); }}
                                        sx={{ ...ATTACHMENT_MENU_BUTTON_SX, width: 24, height: 24 }}
                                      >
                                        <MoreHoriz sx={{ fontSize: 16, color: 'inherit' }} />
                                      </IconButton>
                                    </Box>
                                     <Box sx={{ width: '100%', height: 100, bgcolor: COLORS.gray[50], display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                                       {isImage && fileUrl ? (
                                         <img src={fileUrl} alt={fileName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                       ) : (
                                         <Typography variant="caption" sx={{ fontSize: '32px' }}>📎</Typography>
                                       )}
                                     </Box>
                                    <Box sx={{ p: 1 }}>
                                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 500, color: COLORS.gray[800], whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {fileName}
                                      </Typography>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                                        <Typography variant="caption" sx={{ color: COLORS.gray[500], fontSize: '10px' }}>{dateStr}</Typography>
                                        <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: COLORS.ui.avatarAmber, color: COLORS.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px', fontWeight: 600 }}>
                                          {userInitials}
                                        </Box>
                                      </Box>
                                    </Box>
                                  </Box>
                                ) : (
                                  <Box key={index} 
                                    onClick={() => fileUrl && window.open(fileUrl, '_blank')}
                                    sx={{ 
                                      display: 'flex', alignItems: 'center', border: `1px solid ${COLORS.gray[200]}`, 
                                      borderRadius: 2, p: 1, bgcolor: COLORS.white, cursor: fileUrl ? 'pointer' : 'default',
                                      '&:hover': { bgcolor: COLORS.gray[50] },
                                      '&:hover .attachment-actions': { opacity: 1 },
                                      '&:focus-within .attachment-actions': { opacity: 1 }
                                    }}
                                  >
                                    <Box sx={{ width: 40, height: 40, borderRadius: 1, overflow: 'hidden', bgcolor: COLORS.gray[50], display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2, flexShrink: 0 }}>
                                      {isImage && fileUrl ? (
                                        <img src={fileUrl} alt={fileName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                      ) : (
                                        <img src={ICONS.attachFile} alt="file" style={{ width: 17, height: 17 }} />
                                      )}
                                    </Box>
                                    <Box sx={{ flexGrow: 1, minWidth: 0, mr: 2 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 500, color: COLORS.gray[800], whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {fileName}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: COLORS.gray[500] }}>{dateStr}</Typography>
                                    </Box>
                                    <Box className="attachment-actions" sx={{ opacity: 0, transition: 'opacity 0.2s', mr: 1 }}>
                                      <IconButton 
                                        size="small" 
                                        onClick={(e) => { e.stopPropagation(); setActiveAttachment(file); setActiveAttachmentMenuAnchor(e.currentTarget); }}
                                        sx={ATTACHMENT_MENU_BUTTON_SX}
                                      >
                                        <MoreHoriz sx={{ fontSize: 20, color: 'inherit' }} />
                                      </IconButton>
                                    </Box>
                                    <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: COLORS.ui.avatarAmber, color: COLORS.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 600, flexShrink: 0 }}>
                                      {userInitials}
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                            <Menu
                              anchorEl={activeAttachmentMenuAnchor}
                              open={Boolean(activeAttachmentMenuAnchor)}
                              onClose={() => closeActiveAttachmentMenu()}
                              disableRestoreFocus
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            >
                              <Box sx={{ px: 2, py: 1 }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: '10px' }}>{t('taskViewModal.attachmentTitle', 'Attachment')}</Typography>
                              </Box>
                              <MenuItem onClick={() => handleAttachmentAction('rename')}>
                                <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
                                <Typography variant="body2">{t('taskViewModal.rename', 'Rename')}</Typography>
                              </MenuItem>
                              <MenuItem onClick={() => handleAttachmentAction('copyUrl')}>
                                <ListItemIcon><ContentCopy fontSize="small" /></ListItemIcon>
                                <Typography variant="body2">{t('taskViewModal.copyUrl', 'Copy URL')}</Typography>
                              </MenuItem>
                              <MenuItem onClick={() => handleAttachmentAction('download')}>
                                <ListItemIcon><Download fontSize="small" /></ListItemIcon>
                                <Typography variant="body2">{t('taskViewModal.download', 'Download')}</Typography>
                              </MenuItem>
                              <Divider />
                              <MenuItem onClick={() => handleAttachmentAction('delete')}>
                                <ListItemIcon><Delete fontSize="small" /></ListItemIcon>
                                <Typography variant="body2">{t('taskViewModal.delete', 'Delete')}</Typography>
                              </MenuItem>
                            </Menu>
                          </Box>
                        </Box>
                      ) : (
                        <Box 
                          onClick={() => setExpandedAttachments(true)}
                          sx={{ 
                            ...COLLAPSED_HEADER_SX, 
                            cursor: isUploadingFiles ? 'not-allowed' : 'pointer',
                            opacity: isUploadingFiles ? 0.6 : 1 
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {isUploadingFiles ? <CircularProgress size={22} /> : <img src={ICONS.attachFile} alt="attachments" style={{ width: 22, height: 22 }} />}
                            <Typography variant="h6" sx={{ fontWeight: 500, color: COLORS.gray[800] }}>
                              {isUploadingFiles ? t('taskModal.uploadingFiles', 'Uploading...') : `${t('taskViewModal.attachFile', 'Attach File')} (${attachmentCount})`}
                            </Typography>
                          </Box>
                          {!isUploadingFiles && <ChevronRight sx={{ fontSize: 20, color: COLORS.gray[400] }} />}
                          <input id="description-file-upload-main" type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.svg,.webp,.zip,.mp4,.mov,.avi,.webm,.mkv" hidden multiple onChange={handleFileUpload} disabled={isUploadingFiles} />
                        </Box>
                      )}
                    </Box>

                    {/* 3. CHECKLIST */}
                    <Box sx={{ mb: expandedChecklist ? 2 : 0 }}>
                      {expandedChecklist ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {/* Floating Header */}
                          <Box sx={{ 
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
                            bgcolor: COLORS.subtask.headerBg, 
                            mx: -0.5, 
                            px: 1, 
                            py: 1, 
                            boxShadow: '0px 3px 10px rgba(0, 0, 0, 0.25)',
                            borderRadius: '6px',
                            zIndex: 1
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Close sx={{ fontSize: 16, cursor: 'pointer', color: COLORS.gray[600] }} onClick={() => setExpandedChecklist(false)} />
                              <img src={ICONS.createChecklist} alt="checklist" style={{ width: 22, height: 22 }} />
                              {(() => {
                                let total = 0;
                                let completed = 0;
                                let open = 0;
                                const rawList = ticketDetails?.checklist || taskData.checklist || [];
                                let list = Array.isArray(rawList) ? rawList : (rawList?.checklists || []);
                                if (list.length > 0 && Array.isArray(list[0]?.checklists)) list = list[0].checklists;

                                list.forEach(g => {
                                  if (g && g.items && Array.isArray(g.items)) {
                                    total += g.items.length;
                                    completed += g.items.filter(i => i.completed).length;
                                    open += g.items.filter(i => !i.completed).length;
                                  } else if (g) {
                                    total += 1;
                                    if (g.completed) completed += 1;
                                    else open += 1;
                                  }
                                });
                                const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
                                return (
                                  <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
                                      <Typography variant="h6" fontWeight="400" sx={{ color: COLORS.gray[800], lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                                        {`${t('taskViewModal.createChecklist', 'Checklist')} (${checklistItemCount})`}
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: COLORS.gray[500], fontWeight: 400, whiteSpace: 'nowrap' }}>
                                        {open} open
                                      </Typography>
                                    </Box>
                                    <Box sx={{ width: '100px', ml: 1, display: 'flex', alignItems: 'center' }}>
                                      <LinearProgress
                                        variant="determinate"
                                        value={percentage}
                                        sx={{
                                          width: '100%',
                                          height: 8,
                                          borderRadius: 4,
                                          bgcolor: COLORS.subtask.progressUnfilled,
                                          '& .MuiLinearProgress-bar': {
                                            bgcolor: COLORS.subtask.progressFilled,
                                            borderRadius: 4,
                                            transition: 'all 0.3s ease-in-out'
                                          }
                                        }}
                                      />
                                    </Box>
                                  </>
                                );
                              })()}
                            </Box>
                            <Box sx={{ bgcolor: COLORS.white, borderRadius: '4px', border: `1px solid ${COLORS.gray[300]}`, '&:hover': { bgcolor: COLORS.gray[50] } }}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setChecklistMenuAnchor(e.currentTarget);
                                }}
                                sx={{ p: 0.5 }}
                              >
                                <Add sx={{ fontSize: 18, color: COLORS.gray[700] }} />
                              </IconButton>
                            </Box>
                          </Box>
                          <Box>
                            <ChecklistManager 
                              ref={checklistRef}
                              value={currentChecklistValue}
                              hideGlobalProgress={true}
                              onChange={handleChecklistUpdate} 
                              readOnly={false} 
                              showHeader={false} 
                              projectId={activeProjectId}
                            />
                          </Box>
                        </Box>
                      ) : (
                        <Box 
                          onClick={() => setExpandedChecklist(true)}
                          sx={COLLAPSED_HEADER_SX}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <img src={ICONS.createChecklist} alt="checklist" style={{ width: 22, height: 22 }} />
                            <Typography variant="h6" sx={{ fontWeight: 500, color: COLORS.gray[800] }}>
                              {`${t('taskViewModal.createChecklist', 'Checklist')} (${checklistItemCount})`}
                            </Typography>
                          </Box>
                          <ChevronRight sx={{ fontSize: 20, color: COLORS.gray[400] }} />
                        </Box>
                      )}
                    </Box>

                    {/* Checklist Management Menu */}
                    <Menu
                      anchorEl={checklistMenuAnchor}
                      open={Boolean(checklistMenuAnchor)}
                      onClose={() => setChecklistMenuAnchor(null)}
                    >
                      <MenuItem onClick={() => {
                        if (checklistRef.current) {
                          checklistRef.current.openAddForm();
                        }
                        setChecklistMenuAnchor(null);
                      }}>
                        <ListItemIcon><Add fontSize="small" /></ListItemIcon>
                        <Typography variant="body2">{t('taskModal.checklistManager.addChecklist', 'Add Checklist')}</Typography>
                      </MenuItem>
                      <MenuItem
                        onClick={handleDeleteChecklistAction}
                        disabled={!hasChecklistItems}
                        sx={{ color: COLORS.error }}
                      >
                        <ListItemIcon><Delete fontSize="small" sx={{ color: hasChecklistItems ? COLORS.error : COLORS.gray[400] }} /></ListItemIcon>
                        <Typography variant="body2">{t('taskModal.checklistManager.deleteChecklist', 'Delete Checklist')}</Typography>
                      </MenuItem>
                    </Menu>

                    {/* 4. LINKS */}
                    <Box sx={{ mb: expandedLinks ? 2 : 0 }}>
                      {expandedLinks ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {/* Floating Header */}
                          <Box sx={EXPANDED_HEADER_SX} onClick={(e) => e.stopPropagation()}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Close sx={{ fontSize: 16, cursor: 'pointer', color: COLORS.gray[600] }} onClick={() => setExpandedLinks(false)} />
                              <Link sx={{ fontSize: 24, color: COLORS.gray[700] }} />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="h6" fontWeight="400" sx={{ color: COLORS.gray[800], lineHeight: 1.2 }}>
                                  {`${t('taskViewModal.addLinksTicks', 'Linked Tickets')} (${linkedTicketCount})`}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ bgcolor: COLORS.white, borderRadius: '4px', border: `1px solid ${COLORS.gray[200]}` }}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setIsLinkModalOpen(true);
                                }}
                                sx={{ p: 0.5 }}
                              >
                                <Add sx={{ fontSize: 18, color: COLORS.gray[700] }} />
                              </IconButton>
                            </Box>
                          </Box>
                          <Box sx={{ p: 2, bgcolor: COLORS.white, border: `1px solid ${COLORS.ui.border}`, borderRadius: '6px' }}>
                             {(!ticketDetails?.linked_tickets || Object.keys(ticketDetails.linked_tickets).length === 0) ? (
                               <Box sx={{ p: 1, textAlign: 'center', color: COLORS.gray[500] }}>
                                 <Typography variant="body2">{t('taskViewModal.noLinkedTicketsYet', 'No linked tickets yet')}</Typography>
                               </Box>
                             ) : (
                               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                 {Object.entries(ticketDetails.linked_tickets).map(([relType, relData]) => (
                                 <Box key={relType} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                   <Box 
                                      sx={{ 
                                        fontWeight: 500, 
                                        fontSize: '0.75rem',
                                        color: '#000000', 
                                        textTransform: 'uppercase', 
                                        letterSpacing: '0.05em',
                                        px: 1,
                                        mb: 0.5,
                                        display: 'block'
                                      }}
                                    >
                                      {relType}
                                    </Box>
                                   {relData.tickets?.map(lt => (
                                     <Box 
                                       key={lt.id} 
                                       sx={{ 
                                         display: 'flex', alignItems: 'center', gap: 1.5, p: 1, 
                                         bgcolor: COLORS.white, border: `1px solid ${COLORS.gray[200]}`, 
                                         borderRadius: 1, position: 'relative',
                                         '&:hover': { borderColor: COLORS.primary + '40', bgcolor: COLORS.gray[50] }
                                       }}
                                     >
                                       <Typography variant="caption" sx={{ color: COLORS.primary, fontWeight: 700, bgcolor: COLORS.primary + '10', px: 0.75, py: 0.25, borderRadius: 0.5 }}>{lt.ticket_number || 'T-000'}</Typography>
                                       <Typography 
                                         variant="body2" 
                                         onClick={() => lt.id && dispatch(fetchTicketDetails(lt.id))}
                                         sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', fontWeight: 500, '&:hover': { color: COLORS.primary } }}
                                       >
                                         {lt.title}
                                       </Typography>
                                       
                                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                         <Tooltip title={t('taskViewModal.relationshipType', 'Relationship Type')}>
                                           <IconButton 
                                             size="small" 
                                             onClick={(e) => {
                                               e.stopPropagation();
                                               setEditingLink({ relationshipType: relType, ticket: lt });
                                               setIsEditLinkModalOpen(true);
                                             }}
                                             sx={{ p: 0.5, color: COLORS.gray[400], '&:hover': { color: COLORS.primary } }}
                                           >
                                             <HubOutlined sx={{ fontSize: 18 }} />
                                           </IconButton>
                                         </Tooltip>
                                         <Box className="link-item-actions" sx={{ opacity: 1, display: 'inline-flex' }}>
                                           <Tooltip title={t('common.delete', 'Unlink')}>
                                             <IconButton 
                                               size="small" 
                                               onClick={async (e) => {
                                                 e.stopPropagation();
                                                 const rid = lt.relationship_id;
                                                 if (rid) {
                                                   dispatch(deleteLinkTickets([rid])).unwrap()
                                                     .then(() => dispatch(fetchTicketDetails(taskData.id)));
                                                 }
                                               }}
                                               sx={{ p: 0.5, color: COLORS.error }}
                                             >
                                               <Delete sx={{ fontSize: 18 }} />
                                             </IconButton>
                                           </Tooltip>
                                         </Box>
                                       </Box>
                                     </Box>
                                   ))}
                                 </Box>
                               ))}
                               </Box>
                             )}
                          </Box>
                        </Box>
                      ) : (
                        <Box 
                          onClick={() => setExpandedLinks(true)}
                          sx={COLLAPSED_HEADER_SX}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Link sx={{ fontSize: 24, color: COLORS.gray[700] }} />
                            <Typography variant="h6" sx={{ fontWeight: 500, color: COLORS.gray[800] }}>
                              {`${t('taskViewModal.addLinksTicks', 'Linked Tickets')} (${linkedTicketCount})`}
                            </Typography>
                          </Box>
                          <ChevronRight sx={{ fontSize: 20, color: COLORS.gray[400] }} />
                        </Box>
                      )}
                    </Box>
                  </Box>
                
                {fullScreen && (
                  <Box className="comments-section-wrapper" sx={{ mt: 4 }}>
                    <Typography variant="h6" fontWeight="400" sx={{ 
                      mb: 2, 
                      color: COLORS.gray[800],
                      bgcolor: window.location.pathname.startsWith('/task/') ? '#CEDFCC' : 'transparent',
                      p: window.location.pathname.startsWith('/task/') ? 1 : 0,
                      borderRadius: '4px'
                    }}>
                      {t('taskViewModal.comments')}
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                      <Box display="flex" gap={2}>
                        <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: COLORS.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.white, fontSize: '14px', fontWeight: 600, flexShrink: 0 }}>
                          {`${user?.first_name || ''} ${user?.last_name || ''}`.trim().charAt(0) || t('taskViewModal.unknownUserInitial')}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <CommentBox
                            ref={newCommentBoxRef}
                            onSubmit={handleAddComment}
                            placeholder={t('taskViewModal.writeComment')}
                            mentionUsers={mentionUsers}
                            ticketId={taskData?.id}
                            fieldName="comment"
                            enableAutoSave={true}
                            onAutoSaveActivate={handleActivateCommentDraft}
                          />
                        </Box>
                      </Box>
                    </Box>
                    {commentsLoading ? (
                      <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography>{t('taskViewModal.loadingComments')}</Typography>
                      </Box>
                    ) : localComments.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {localComments.map((comment) => (
                          <Box
                            key={comment.id}
                            id={`comment-${comment.id}`}
                            className="comment-focused-anchor"
                            sx={{ mb: 2 }}
                          >
                            <Box display="flex" gap={2}>
                              <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: stringToColor(comment.author?.name || ''), display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.white, fontSize: '12px', fontWeight: 600, flexShrink: 0 }}>
                                {comment.author?.avatar || comment.author?.name?.charAt(0) || t('taskViewModal.unknownUserInitial')}
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                  <Box>
                                    <Typography variant="body2" fontWeight="600" sx={{ color: COLORS.gray[800] }}>
                                      {comment.author?.name || t('taskViewModal.unknownUser')}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">{comment.timestamp}</Typography>
                                  </Box>
                                  <Box className="comment-action-bar">
                                    <Tooltip title={t('common.copyLink', 'Copy Link')} placement="top" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleCopyCommentLinkMemoized(comment.id)}
                                        className="comment-action-button"
                                        aria-label={t('common.copyLink', 'Copy Link')}
                                      >
                                        <ContentCopy className="comment-action-icon" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('taskViewModal.reply')} placement="top" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                                      <IconButton 
                                        size="small"
                                        onClick={() => handleStartReply(comment.id)}
                                        className="comment-action-button comment-action-button--reply"
                                        aria-label={t('taskViewModal.reply')}
                                      >
                                        <Reply className="comment-action-icon" />
                                      </IconButton>
                                    </Tooltip>
                                    {user && isCommentOwnedByUser(comment.author?.name) && (
                                      <>
                                        <Tooltip title={t('common.edit')} placement="top" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                                          <IconButton 
                                            size="small" 
                                            onClick={() => handleEditComment(comment.id)}
                                            className="comment-action-button"
                                            aria-label={t('common.edit')}
                                          >
                                            <img src={ICONS.edit} alt={t('common.edit')} style={{ width: 14, height: 14 }} />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('common.delete')} placement="top" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                                          <IconButton 
                                            size="small" 
                                            onClick={() => handleDeleteComment(comment.id)}
                                            className="comment-action-button"
                                            aria-label={t('common.delete')}
                                          >
                                            <img src={ICONS.delete} alt={t('common.delete')} style={{ width: 14, height: 14 }} />
                                          </IconButton>
                                        </Tooltip>
                                      </>
                                    )}
                                  </Box>
                                </Box>
                                <Box sx={{ mb: 2 }}>
                                  {editingCommentId === comment.id ? (
                                    <CommentBox
                                      onSubmit={async (content, attachments) => {
                                        const trimmedContent = trimEdgeHtml(content || '').trim();
                                        if (!trimmedContent && attachments.length === 0) return;
                                        try {
                                          await dispatch(updateComment({
                                            commentId: editingCommentId,
                                            comment: trimmedContent,
                                            attachments
                                          })).unwrap();

                                          setLocalComments((prev) => updateCommentInTree(prev, editingCommentId, (c) => (
                                            { ...c, content: trimmedContent, attachments }
                                          )));

                                          // Clear draft after successful update
                                          if (taskData?.id) {
                                            clearDraft(taskData.id, getCommentEditDraftFieldName(editingCommentId));
                                          }
                                          setEditingCommentId(null);
                                          setEditingCommentContent('');
                                        } catch (error) {
                                          console.error('Failed to update comment:', error);
                                        }
                                      }}
                                      onCancel={handleCancelCommentEdit}
                                      placeholder={t('taskViewModal.editYourComment')}
                                      initialContent={editingCommentContent}
                                      initialAttachments={findCommentById(localComments, editingCommentId)?.attachments || []}
                                      mentionUsers={mentionUsers}
                                      ticketId={taskData?.id}
                                      fieldName={getCommentEditDraftFieldName(editingCommentId)}
                                      enableAutoSave={true}
                                      onAutoSaveActivate={handleActivateCommentDraft}
                                    />
                                  ) : (
                                    <Box>
                                      <Box className="comment-content" sx={{
                                        color: COLORS.gray[600],
                                        fontSize: '14px',
                                        lineHeight: 1.6,
                                        '& h1, & h2, & h3': { margin: '8px 0', fontWeight: 600 },
                                        '& h1': { fontSize: '18px' },
                                        '& h2': { fontSize: '16px' },
                                        '& h3': { fontSize: '14px' },
                                        '& ul, & ol': { paddingLeft: '20px', margin: '8px 0' },
                                        '& li': { margin: '4px 0' },
                                        '& p': { margin: '8px 0' },
                                        '& strong': { fontWeight: 600 }
                                      }}>
                                        <SafeCommentRenderer content={comment.content || ''} mentionUsers={mentionUsers} />
                                      </Box>
                                      {comment.attachments && comment.attachments.length > 0 && (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                          {comment.attachments.map((file, index) => {
                                            const fileName = file.name || file.key || 'Attachment';
                                            const fileUrl = file.url;
                                            const isImage = fileName.match(/\.(jpg|jpeg|png|gif|webp)$/i);

                                            return isImage && fileUrl ? (
                                              <Box
                                                key={index}
                                                onClick={() => window.open(fileUrl, '_blank')}
                                                sx={{
                                                  width: '60px',
                                                  height: '60px',
                                                  borderRadius: 1,
                                                  overflow: 'hidden',
                                                  cursor: 'pointer',
                                                  '&:hover': { opacity: 0.8 }
                                                }}
                                              >
                                                <img
                                                  src={fileUrl}
                                                  alt="attachment"
                                                  style={{
                                                    width: '60px',
                                                    height: '60px',
                                                    objectFit: 'cover',
                                                    display: 'block'
                                                  }}
                                                />
                                              </Box>
                                            ) : (
                                              <Box
                                                key={index}
                                                onClick={() => {
                                                  if (fileUrl) {
                                                    window.open(fileUrl, '_blank');
                                                  }
                                                }}
                                                sx={{
                                                  width: '60px',
                                                  height: '60px',
                                                  border: `1px solid ${COLORS.gray[200]}`,
                                                  borderRadius: 1,
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  cursor: fileUrl ? 'pointer' : 'default',
                                                  bgcolor: COLORS.gray[50],
                                                  '&:hover': {
                                                    boxShadow: fileUrl ? `0 2px 8px ${COLORS.ui.shadow10}` : 'none',
                                                    bgcolor: fileUrl ? COLORS.blue[50] : COLORS.gray[50]
                                                  }
                                                }}
                                              >
                                                <Box sx={{ textAlign: 'center' }}>
                                                  <Box sx={{ fontSize: '24px', mb: 0.5 }}>
                                                    {fileName.match(/\.pdf$/i) ? '📄' :
                                                     fileName.match(/\.(doc|docx)$/i) ? '📝' :
                                                     fileName.match(/\.(mp4|avi|mov|wmv|flv|webm)$/i) ? '🎥' :
                                                     '📎'}
                                                  </Box>
                                                  <Box sx={{
                                                    width: '8px',
                                                    height: '2px',
                                                    bgcolor: fileName.match(/\.pdf$/i) ? COLORS.red[600] :
                                                             fileName.match(/\.(doc|docx)$/i) ? COLORS.blue[600] :
                                                             fileName.match(/\.(mp4|avi|mov|wmv|flv|webm)$/i) ? COLORS.purple[600] :
                                                             COLORS.gray[500],
                                                    borderRadius: '1px',
                                                    mx: 'auto'
                                                  }} />
                                                </Box>
                                              </Box>
                                            );
                                          })}
                                        </Box>
                                      )}
                                    </Box>
                                  )}
                                </Box>
                                {comment.replies?.length > 0 && (
                                  <div className="comment-replies-toggle">
                                    <div className="comment-replies-summary">
                                      <AssigneeAvatarStack
                                        assignees={getReplyAuthors(comment.replies)}
                                        size={COMMENT_REPLY.AVATAR_SIZE}
                                        max={COMMENT_REPLY.AVATAR_STACK_MAX}
                                      />
                                      <button
                                        type="button"
                                        className="comment-replies-toggle-button"
                                        onClick={() => toggleReplyThread(comment.id)}
                                      >
                                        {getReplyCountLabel(comment.replies.length)}
                                      </button>
                                    </div>
                                    {isReplyThreadOpen(comment.id) && (
                                      null
                                    )}
                                  </div>
                                )}
                                {replyingTarget?.commentId === comment.id && !replyingTarget?.parentReplyId && (
                                  <div className="comment-reply-editor">
                                    <CommentBox
                                      onSubmit={(content) => handleSubmitReply(comment.id, null, content)}
                                      onCancel={handleCancelReply}
                                      hideAttachments
                                      mentionUsers={mentionUsers}
                                      ticketId={taskData?.id}
                                      fieldName={getCommentReplyDraftFieldName(comment.id, null)}
                                      enableAutoSave={true}
                                      onAutoSaveActivate={handleActivateCommentDraft}
                                    />
                                  </div>
                                )}
                                {renderReplies(comment)}
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Box sx={{ p: 2, textAlign: 'center', color: COLORS.gray[500] }}>
                        <Comment sx={{ fontSize: 40, color: COLORS.gray[300], mb: 1 }} />
                        <Typography variant="body2">{t('taskViewModal.noCommentsYet')}</Typography>
                      </Box>
                    )}
                  </Box>
                )}
                
              </Box>
            )}
            
            {currentActiveTab === commentsTabIndex && !fullScreen && (
            <Box className="comments-section-wrapper" sx={{ p: 3, pr: 8 }}>
              <Box sx={{ mb: 3 }}>
                <Box display="flex" gap={2}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: COLORS.primary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: COLORS.white,
                    fontSize: '14px',
                    fontWeight: 600,
                    flexShrink: 0
                  }}>
                    {`${user?.first_name || ''} ${user?.last_name || ''}`.trim().charAt(0) || t('taskViewModal.unknownUserInitial')}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <CommentBox
                      ref={newCommentBoxRef}
                      onSubmit={handleAddComment}
                      placeholder={t('taskViewModal.writeComment')}
                      mentionUsers={mentionUsers}
                            ticketId={taskData?.id}
                            fieldName="comment"
                            enableAutoSave={true}
                            onAutoSaveActivate={handleActivateCommentDraft}
                    />
                  </Box>
                </Box>
              </Box>

              {commentsLoading ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography>{t('taskViewModal.loadingComments')}</Typography>
                </Box>
              ) : localComments.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {localComments.map((comment) => (
                    <Box
                      key={comment.id}
                      id={`comment-${comment.id}`}
                      className="comment-focused-anchor"
                      sx={{ mb: 2 }}
                    >
                      <Box display="flex" gap={2}>
                        <Box sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: stringToColor(comment.author?.name || ''),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: COLORS.white,
                          fontSize: '12px',
                          fontWeight: 600,
                          flexShrink: 0
                        }}>
                          {comment.author?.avatar || comment.author?.name?.charAt(0) || t('taskViewModal.unknownUserInitial')}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Box>
                              <Typography variant="body2" fontWeight="600" sx={{ color: COLORS.gray[800] }}>
                                {comment.author?.name || t('taskViewModal.unknownUser')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {comment.timestamp}
                              </Typography>
                            </Box>
                             <Box className="comment-action-bar">
                               <Tooltip title={t('common.copyLink', 'Copy Link')} placement="top" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                                 <IconButton
                                   size="small"
                                   onClick={() => handleCopyCommentLinkMemoized(comment.id)}
                                   className="comment-action-button"
                                   aria-label={t('common.copyLink', 'Copy Link')}
                                 >
                                   <ContentCopy className="comment-action-icon" />
                                 </IconButton>
                               </Tooltip>
                               <Tooltip title={t('taskViewModal.reply')} placement="top" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                                 <IconButton 
                                   size="small"
                                   onClick={() => handleStartReply(comment.id)}
                                   className="comment-action-button comment-action-button--reply"
                                   aria-label={t('taskViewModal.reply')}
                                 >
                                   <Reply className="comment-action-icon" />
                                 </IconButton>
                               </Tooltip>
                               {user && isCommentOwnedByUser(comment.author?.name) && (
                                 <>
                                   <Tooltip title={t('common.edit')} placement="top" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                                     <IconButton 
                                       size="small" 
                                       onClick={() => handleEditComment(comment.id)}
                                       className="comment-action-button"
                                       aria-label={t('common.edit')}
                                     >
                                       <img src={ICONS.edit} alt={t('common.edit')} style={{ width: 14, height: 14 }} />
                                     </IconButton>
                                   </Tooltip>
                                   <Tooltip title={t('common.delete')} placement="top" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                                     <IconButton 
                                       size="small" 
                                       onClick={() => handleDeleteComment(comment.id)}
                                       className="comment-action-button"
                                       aria-label={t('common.delete')}
                                     >
                                       <img src={ICONS.delete} alt={t('common.delete')} style={{ width: 14, height: 14 }} />
                                     </IconButton>
                                   </Tooltip>
                                 </>
                               )}
                             </Box>
                          </Box>
                          <Box sx={{ mb: 2 }}>
                            {editingCommentId === comment.id ? (
                              <CommentBox
                                onSubmit={async (content, attachments) => {
                                  const trimmedContent = trimEdgeHtml(content || '').trim();
                                  if (!trimmedContent && attachments.length === 0) return;
                                  try {
                                    await dispatch(updateComment({ 
                                      commentId: editingCommentId, 
                                      comment: trimmedContent,
                                      attachments
                                    })).unwrap();
                                    
                                    setLocalComments((prev) => updateCommentInTree(prev, editingCommentId, (c) => (
                                      { ...c, content: trimmedContent, attachments }
                                    )));
                                    
                                    // Clear draft after successful update
                                    if (taskData?.id) {
                                      clearDraft(taskData.id, getCommentEditDraftFieldName(editingCommentId));
                                    }
                                    setEditingCommentId(null);
                                    setEditingCommentContent('');
                                  } catch (error) {
                                    console.error('Failed to update comment:', error);
                                  }
                                }}
                                onCancel={handleCancelCommentEdit}
                                placeholder={t('taskViewModal.editYourComment')}
                                initialContent={editingCommentContent}
                                initialAttachments={findCommentById(localComments, editingCommentId)?.attachments || []}
                                mentionUsers={mentionUsers}
                                ticketId={taskData?.id}
                                fieldName={getCommentEditDraftFieldName(editingCommentId)}
                                enableAutoSave={true}
                                onAutoSaveActivate={handleActivateCommentDraft}
                              />
                            ) : (
                              <Box>
                                <Box className="description-container">
                                  <Box className="comment-content" sx={{ 
                                    color: COLORS.gray[600], 
                                    fontSize: '14px', 
                                    lineHeight: 1.6,
                                    '& h1, & h2, & h3': { margin: '8px 0', fontWeight: 600 },
                                    '& h1': { fontSize: '18px' },
                                    '& h2': { fontSize: '16px' },
                                    '& h3': { fontSize: '14px' },
                                    '& ul, & ol': { paddingLeft: '20px', margin: '8px 0' },
                                    '& li': { margin: '4px 0' },
                                    '& p': { margin: '8px 0' },
                                    '& strong': { fontWeight: 600 }
                                  }}>
                                    <div dangerouslySetInnerHTML={{ __html: renderCommentContent(comment.content || '', mentionUsers) }} />
                                  </Box>
                                </Box>
                                {comment.attachments && comment.attachments.length > 0 && (
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                    {comment.attachments.map((file, index) => {
                                      const fileName = file.name || file.key || 'Attachment';
                                      const fileUrl = file.url;
                                      const isImage = fileName.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                                      
                                      return isImage && fileUrl ? (
                                        <Box
                                          key={index}
                                          onClick={() => window.open(fileUrl, '_blank')}
                                          sx={{
                                            width: '60px',
                                            height: '60px',
                                            borderRadius: 1,
                                            overflow: 'hidden',
                                            cursor: 'pointer',
                                            '&:hover': { opacity: 0.8 }
                                          }}
                                        >
                                          <img
                                            src={fileUrl}
                                            alt="attachment"
                                            style={{
                                              width: '60px',
                                              height: '60px',
                                              objectFit: 'cover',
                                              display: 'block'
                                            }}
                                          />
                                        </Box>
                                      ) : (
                                        <Box
                                          key={index}
                                          onClick={() => {
                                            if (fileUrl) {
                                              window.open(fileUrl, '_blank');
                                            }
                                          }}
                                          sx={{
                                            width: '60px',
                                            height: '60px',
                                            border: `1px solid ${COLORS.gray[200]}`,
                                            borderRadius: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: fileUrl ? 'pointer' : 'default',
                                            bgcolor: COLORS.gray[50],
                                            '&:hover': {
                                              boxShadow: fileUrl ? `0 2px 8px ${COLORS.ui.shadow10}` : 'none',
                                              bgcolor: fileUrl ? COLORS.blue[50] : COLORS.gray[50]
                                            }
                                          }}
                                        >
                                          <Box sx={{ textAlign: 'center' }}>
                                            <Box sx={{ fontSize: '24px', mb: 0.5 }}>
                                              {fileName.match(/\.pdf$/i) ? '📄' : 
                                               fileName.match(/\.(doc|docx)$/i) ? '📝' :
                                               fileName.match(/\.(mp4|avi|mov|wmv|flv|webm)$/i) ? '🎥' :
                                               '📎'}
                                            </Box>
                                            <Box sx={{ 
                                              width: '8px', 
                                              height: '2px', 
                                              bgcolor: fileName.match(/\.pdf$/i) ? COLORS.red[600] :
                                                       fileName.match(/\.(doc|docx)$/i) ? COLORS.blue[600] :
                                                       fileName.match(/\.(mp4|avi|mov|wmv|flv|webm)$/i) ? COLORS.purple[600] :
                                                       COLORS.gray[500],
                                              borderRadius: '1px',
                                              mx: 'auto'
                                            }} />
                                          </Box>
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                )}
                                {comment.replies?.length > 0 && (
                                  <div className="comment-replies-toggle">
                                    <div className="comment-replies-summary">
                                      <AssigneeAvatarStack
                                        assignees={getReplyAuthors(comment.replies)}
                                        size={COMMENT_REPLY.AVATAR_SIZE}
                                        max={COMMENT_REPLY.AVATAR_STACK_MAX}
                                      />
                                      <button
                                        type="button"
                                        className="comment-replies-toggle-button"
                                        onClick={() => toggleReplyThread(comment.id)}
                                      >
                                        {getReplyCountLabel(comment.replies.length)}
                                      </button>
                                    </div>
                                    {isReplyThreadOpen(comment.id) && (
                                      null
                                    )}
                                  </div>
                                )}
                                {replyingTarget?.commentId === comment.id && !replyingTarget?.parentReplyId && (
                                  <div className="comment-reply-editor">
                                    <CommentBox
                                      onSubmit={(content) => handleSubmitReply(comment.id, null, content)}
                                      onCancel={handleCancelReply}
                                      hideAttachments
                                      mentionUsers={mentionUsers}
                                      ticketId={taskData?.id}
                                      fieldName={getCommentReplyDraftFieldName(comment.id, null)}
                                      enableAutoSave={true}
                                      onAutoSaveActivate={handleActivateCommentDraft}
                                    />
                                  </div>
                                )}
                                {renderReplies(comment)}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ p: 2, textAlign: 'center', color: COLORS.gray[500] }}>
                  <Comment sx={{ fontSize: 40, color: COLORS.gray[300], mb: 1 }} />
                  <Typography variant="body2">{t('taskViewModal.noCommentsYet')}</Typography>
                </Box>
              )}
            </Box>
          )}

            {/* TAB 2: WORKLOG */}
            {currentActiveTab === workLogTabIndex && (
              <Box sx={{ p: fullScreen ? 2 : 3, pr: fullScreen ? 2 : 8 }}>
                <Box sx={fullScreen ? { bgcolor: COLORS.white, border: `1px solid ${COLORS.gray[200]}`, borderRadius: 2, p: 2 } : {}}>
                  <WorkLog ticketId={taskData?.id} worklogSummary={worklogSummary} worklogSummaryLoading={worklogSummaryLoading} user={user} onCreateWorklog={handleCreateWorklog} onUpdateWorklog={handleUpdateWorklog} onDeleteWorklog={handleDeleteWorklog} />
                </Box>
              </Box>
            )}

            {/* TAB 3: HISTORY */}
            {currentActiveTab === historyTabIndex && (
              <Box sx={{ p: fullScreen ? 2 : 3, pr: fullScreen ? 2 : 8 }}>
                <Box sx={fullScreen ? { bgcolor: COLORS.white, border: `1px solid ${COLORS.gray[200]}`, borderRadius: 2, p: 2 } : {}}>
                  <TaskHistory ticketHistory={ticketHistory} ticketHistoryLoading={ticketHistoryLoading} />
                </Box>
              </Box>
            )}

            {/* TAB 4: AI DEVELOPER (FULLSCREEN/MODAL) */}
            {currentActiveTab === aiTabIndex && shouldShowAIDeveloperTab() && (
              <AITaskPlanner ticketId={taskData?.id} ticketUuid={taskData?.id} taskData={ticketDetails || taskData} ref={() => { window.ticketUuid = taskData?.id; }} />
            )}
          </Box>
        </Box>

        {/* Right Floating Rail - Only in modal mode */}
        {!fullScreen && (
          <Box sx={{ 
            width: 55, 
            bgcolor: 'transparent', 
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
            pointerEvents: 'none'
          }}>
            
            <Box sx={{
              position: 'absolute',
              right: 9.5,
              width: 36,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              pointerEvents: 'auto',
              overflowX: 'hidden',
              overflowY: 'hidden',
              bgcolor: !showFloatingRail ? COLORS.white : 'rgba(255, 255, 255, 0)',
              border: '1px solid',
              borderColor: !showFloatingRail ? COLORS.gray[200] : 'rgba(226, 232, 240, 0)',
              boxShadow: !showFloatingRail ? '0 4px 16px rgba(0, 0, 0, 0.08)' : 'none',
              borderRadius: '18px',
              height: !showFloatingRail 
                ? (isRailExpanded ? (shouldShowAIDeveloperTab() ? 260 : 220) : 36)
                : (shouldShowAIDeveloperTab() ? 260 : 220),
              top: !showFloatingRail ? 16 : 0,
              pt: showFloatingRail ? 3 : 0,
              pb: !showFloatingRail && isRailExpanded ? 1.5 : 0,
              transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1), top 0.3s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.3s, border-color 0.3s, box-shadow 0.3s, padding 0.3s',
            }}>
              
              {/* Toggle Button (MoreHoriz) */}
              <Box sx={{
                width: '100%',
                height: !showFloatingRail ? 36 : 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                opacity: !showFloatingRail ? 1 : 0,
                visibility: !showFloatingRail ? 'visible' : 'hidden',
                overflow: 'hidden',
                transition: 'opacity 0.2s ease, visibility 0.2s ease, height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}>
                <Tooltip title={isRailExpanded ? t('taskViewModal.collapse', 'Collapse') : t('taskViewModal.expand', 'Expand')} placement="left" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                  <IconButton
                    size="small"
                    onClick={() => setIsRailExpanded(prev => !prev)}
                    sx={{
                      width: 28,
                      height: 28,
                      color: COLORS.gray[500],
                      bgcolor: isRailExpanded ? COLORS.gray[100] : 'transparent',
                      '&:hover': {
                        bgcolor: COLORS.gray[200],
                        color: COLORS.gray[800],
                      },
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <MoreHoriz sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Separator / Spacer when expanded */}
              {!showFloatingRail && isRailExpanded && (
                <Divider sx={{ width: '60%', my: 0.5, borderColor: COLORS.gray[100] }} />
              )}

              {/* Tab buttons */}
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2.5,
                mt: !showFloatingRail ? 0.5 : 0,
                opacity: (showFloatingRail || isRailExpanded) ? 1 : 0,
                visibility: (showFloatingRail || isRailExpanded) ? 'visible' : 'hidden',
                transform: (showFloatingRail || isRailExpanded) ? 'translateY(0) scale(1)' : 'translateY(-10px) scale(0.9)',
                transition: 'opacity 0.2s ease, visibility 0.2s ease, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}>
                {[
                  { tabIndex: detailsTabIndex, tooltip: t('taskViewModal.details'), icon: 'file', iconDisabled: 'fileDisabled' },
                  ...(!fullScreen ? [{ tabIndex: commentsTabIndex, tooltip: t('taskViewModal.comments'), icon: 'comment', iconDisabled: 'commentDisabled' }] : []),
                  { tabIndex: workLogTabIndex, tooltip: t('taskViewModal.workLog'), icon: 'worklog', iconDisabled: 'worklogDisabled' },
                  { tabIndex: historyTabIndex, tooltip: t('taskViewModal.history'), icon: 'history', iconDisabled: 'historyDisabled' }
                ].map((item) => {
                  const isActive = currentActiveTab === item.tabIndex;
                  const iconName = isActive ? item.icon : item.iconDisabled;
                  
                  return (
                    <Tooltip key={item.tabIndex} title={item.tooltip} placement="left" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          handleTabChange(null, item.tabIndex);
                          if (isRailExpanded) setIsRailExpanded(false);
                        }}
                        sx={{
                          p: 0,
                          bgcolor: 'transparent',
                          '&:hover': {
                            bgcolor: 'transparent',
                            transform: 'scale(1.1)',
                          },
                          transition: 'transform 0.2s'
                        }}
                      >
                        <img 
                          src={`/icons/${iconName}.svg`} 
                          alt={item.tooltip} 
                          style={{ width: 22, height: 22 }} 
                        />
                      </IconButton>
                    </Tooltip>
                  );
                })}
                {shouldShowAIDeveloperTab() && (
                  <Tooltip title="AI Developer" placement="left" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        handleTabChange(null, aiTabIndex);
                        if (isRailExpanded) setIsRailExpanded(false);
                      }}
                      sx={{
                        color: currentActiveTab === aiTabIndex ? COLORS.brand.primaryDark : COLORS.gray[500],
                        bgcolor: currentActiveTab === aiTabIndex ? COLORS.brand.primaryDark + '11' : 'transparent',
                        '&:hover': {
                          bgcolor: currentActiveTab === aiTabIndex ? COLORS.brand.primaryDark + '22' : COLORS.gray[100],
                          color: currentActiveTab === aiTabIndex ? COLORS.brand.primaryDark : COLORS.gray[800],
                        }
                      }}
                    >
                      <SmartToy sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      {fullScreen ? (
        content
      ) : ticketViewMode === 'modal' ? (
        <Dialog
          open={open}
          onClose={handleClose}
          className="task-view-modal"
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              width: '80vw',
              maxWidth: 950,
              height: '90vh',
              maxHeight: '90vh',
              borderRadius: '12px',
              overflow: 'hidden'
            }
          }}
        >
          {content}
        </Dialog>
      ) : (
        <Drawer
          anchor="right"
          open={open}
          onClose={handleClose}
          className="task-view-modal"
          PaperProps={{
            className: 'task-view-drawer-paper'
          }}
        >
          {content}
        </Drawer>
      )}
      <AddTaskModal
        open={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setAddingSubtaskParentId(null);
        }}
        onSubmit={handleCreateSubtask}
        parentTicketId={addingSubtaskParentId || taskData?.id}
        selectedProjectId={
          ticketDetails?.project?.id ||
          taskData?.project_id ||
          (typeof taskData?.project === 'object' ? taskData?.project?.id : null)
        }
      />
      <AddTaskModal
        open={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setSelectedSubtask(null); }}
        onSubmit={handleUpdateSubtask}
        taskData={selectedSubtask || {}}
        isEditMode={true}
        selectedProjectId={ticketDetails?.project?.id || taskData?.project_id || (typeof taskData?.project === 'object' ? taskData?.project?.id : null)}
      />
      <CommonDeleteConfirmDialog
        open={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setSelectedSubtask(null); }}
        onConfirm={() => handleDeleteSubtask(selectedSubtask)}
        title={t('taskModal.deleteTask')}
        question={t('taskModal.confirmDeleteTask')}
        itemName={selectedSubtask?.title}
        warning={t('taskModal.actionCannotBeUndone')}
        confirmText={t('taskModal.deleteTask')}
        cancelText={t('common.cancel')}
      />
      <CommonDeleteConfirmDialog
        open={deleteOptionDialogOpen}
        onClose={handleCancelDeleteOption}
        onConfirm={handleConfirmDeleteOption}
        title={getDeleteOptionTitle()}
        question={getDeleteOptionQuestion()}
        itemName={optionToDelete?.name || ''}
        warning={t('taskModal.actionCannotBeUndone')}
        confirmText={getDeleteOptionTitle()}
        cancelText={t('common.cancel')}
      />
      <LinkTicketModal
        open={isLinkModalOpen}
        onClose={() => {
          setIsLinkModalOpen(false);
        }}
        primaryTicket={{
          number: ticketDetails?.ticket_number || taskData?.code,
          title: ticketDetails?.title || taskData?.title
        }}
        relationshipTypes={dropdowns.relationship_types?.map(rt => ({ value: rt.id, label: rt.name, colour: rt.colour })) || []}
        currentTicketId={taskData?.id}
        existingLinkedTickets={(() => {
          try {
            return ticketDetails?.linked_tickets ? 
              Object.entries(ticketDetails.linked_tickets).flatMap(([relationshipType, relationshipData]) => 
                relationshipData?.tickets?.map(ticket => ({
                  ...ticket,
                  relationshipType
                })) || []
              ) : [];
          } catch (error) {
            console.error('Error processing linked tickets:', error);
            return [];
          }
        })()}
        searchTickets={async (searchTerm) => {
          try {
            const projectId = ticketDetails?.project?.id || taskData?.project_id || (typeof taskData?.project === 'object' ? taskData?.project?.id : null);
            const result = await dispatch(searchTickets({ projectId, searchTerm })).unwrap();
            return result.map(ticket => ({
              id: ticket.id,
              number: ticket.ticket_number,
              title: ticket.title,
              status: ticket.status?.name || 'Unknown'
            }));
          } catch (error) {
            console.error('Search tickets failed:', error);
            return [];
          }
        }}
        onLinkTickets={async (linkData) => {
          try {
            const projectId = ticketDetails?.project?.id || taskData?.project_id || (typeof taskData?.project === 'object' ? taskData?.project?.id : null);
            if (!projectId) {
              toast.error('Project ID is required for linking tickets');
              return;
            }
            
            await dispatch(linkTickets({
              projectId,
              parentTicketId: taskData.id,
              relationshipType: linkData.relationshipType,
              targetTicketIds: linkData.targetTickets.map(t => t.id).filter(id => id !== taskData.id)
            })).unwrap();
            
            // Modal will close automatically, then refresh data
            requestAnimationFrame(() => {
              if (taskData?.id) {
                dispatch(fetchTicketDetails(taskData.id));
              }
              if (projectId) {
                if (!isGanttContext) {
                  dispatch(fetchTicketsByProject({ 
                    projectId: projectId,
                    filters: cleanFilters(getCurrentFilters())
                  }));
                }
              }
            });
          } catch (error) {
            console.error('Failed to link tickets:', error);
          }
        }}
      />
      <LinkTicketModal
        open={isEditLinkModalOpen}
        onClose={() => {
          setIsEditLinkModalOpen(false);
          setEditingLink(null);
        }}
        primaryTicket={{
          number: ticketDetails?.ticket_number || taskData?.code,
          title: ticketDetails?.title || taskData?.title
        }}
        relationshipTypes={dropdowns.relationship_types?.map(rt => ({ value: rt.id, label: rt.name, colour: rt.colour })) || []}
        currentTicketId={taskData?.id}
        existingLinkedTickets={(() => {
          try {
            return ticketDetails?.linked_tickets ? 
              Object.entries(ticketDetails.linked_tickets).flatMap(([relationshipType, relationshipData]) => 
                relationshipData?.tickets?.map(ticket => ({
                  ...ticket,
                  relationshipType
                })) || []
              ) : [];
          } catch (error) {
            console.error('Error processing linked tickets:', error);
            return [];
          }
        })()}
        searchTickets={async (searchTerm) => {
          try {
            const projectId = ticketDetails?.project?.id || taskData?.project_id || (typeof taskData?.project === 'object' ? taskData?.project?.id : null);
            const result = await dispatch(searchTickets({ projectId, searchTerm })).unwrap();
            return result.map(ticket => ({
              id: ticket.id,
              number: ticket.ticket_number,
              title: ticket.title,
              status: ticket.status?.name || 'Unknown'
            }));
          } catch (error) {
            console.error('Search tickets failed:', error);
            return [];
          }
        }}
        editMode={true}
        initialData={editingLink ? {
          relationshipType: editingLink.relationshipType,
          targetTicket: {
            id: editingLink.ticket.id,
            title: editingLink.ticket.title,
            number: editingLink.ticket.ticket_number
          }
        } : null}
        onLinkTickets={async (linkData) => {
          try {
            // For edit mode, we need a relationship ID
            const relationshipId = editingLink?.ticket?.relationship_id;
            if (!relationshipId) {
              toast.error('Relationship ID is required for updating link');
              return;
            }
            
            await dispatch(updateLinkTickets({
              relationshipId,
              relationshipType: linkData.relationshipType,
              related_ticket_id: linkData.targetTicketId,
              target_ticket: linkData.target_ticket
            })).unwrap();
            
            // Modal will close automatically, then refresh data
            requestAnimationFrame(() => {
              if (taskData?.id) {
                dispatch(fetchTicketDetails(taskData.id));
              }
              const dashboardProjectId = ticketDetails?.project?.id || taskData?.project_id || (typeof taskData?.project === 'object' ? taskData?.project?.id : null);
              if (dashboardProjectId) {
                if (!isGanttContext) {
                  dispatch(fetchTicketsByProject({ 
                    projectId: dashboardProjectId,
                    filters: cleanFilters(getCurrentFilters())
                  }));
                }
              }
            });
            
            setEditingLink(null);
          } catch (error) {
            console.error('Failed to update link:', error);
          }
        }}
      />

      {/* Modern Attachment Rename Dialog */}
      <Dialog 
        open={renameDialogOpen} 
        onClose={() => setRenameDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 1.5,
            width: '100%',
            maxWidth: '400px',
            boxShadow: `0 24px 48px ${COLORS.ui.shadow12}`
          }
        }}
      >
        <Box sx={{ p: 1.5 }}>
          <Typography variant="h6" fontWeight="700" sx={{ mb: 1, color: COLORS.text.primary }}>
            {t('taskViewModal.renameFile', 'Rename Assignment')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2.5, color: COLORS.gray[500] }}>
            {t('taskViewModal.renameInfo', 'Please enter a professional and descriptive name for this file.')}
          </Typography>
          
          <TextField
            autoFocus
            fullWidth
            variant="outlined"
            placeholder={t('taskViewModal.enterFileName', 'File name')}
            value={newAttachmentName}
            onChange={(e) => setNewAttachmentName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleConfirmRename()}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                bgcolor: COLORS.gray[50],
                '& fieldset': { borderColor: COLORS.gray[200] },
                '&:hover fieldset': { borderColor: COLORS.primary },
                '&.Mui-focused fieldset': { borderColor: COLORS.primary, borderWidth: 1.5 },
              }
            }}
          />
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button 
              onClick={() => setRenameDialogOpen(false)}
              sx={{ 
                color: COLORS.gray[600], 
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '8px',
                px: 2.5
              }}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button 
              onClick={handleConfirmRename}
              variant="contained"
              disableElevation
              sx={{ 
                bgcolor: COLORS.primary, 
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '8px',
                px: 3,
                '&:hover': { bgcolor: COLORS.primaryDark }
              }}
            >
              {t('common.rename', 'Rename')}
            </Button>
          </Box>
        </Box>
      </Dialog>
      <Menu
        anchorEl={commentMenuAnchorEl}
        open={Boolean(commentMenuAnchorEl)}
        onClose={handleCloseCommentMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 150, mt: 0.5, boxShadow: `0 4px 20px ${COLORS.ui.shadow10}`, borderRadius: '8px' } }}
      >
        <MenuItem onClick={handleEditComment}>
          <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
          <Typography variant="body2">{t('common.edit')}</Typography>
        </MenuItem>
        <MenuItem onClick={handleDeleteComment} sx={{ color: COLORS.error }}>
          <ListItemIcon><Delete fontSize="small" sx={{ color: COLORS.error }} /></ListItemIcon>
          <Typography variant="body2">{t('common.delete')}</Typography>
        </MenuItem>
      </Menu>
      <Dialog 
        open={isDeleteCommentDialogOpen} 
        onClose={() => {
          if (isDeletingComment) return;
          setIsDeleteCommentDialogOpen(false);
        }}
        disableEscapeKeyDown={isDeletingComment}
        PaperProps={{ sx: { borderRadius: '16px', p: 1, maxWidth: '400px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: COLORS.error, width: 44, height: 44 }}>
              <Delete />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="700">{t('taskViewModal.deleteComment', 'Delete Comment')}</Typography>
              <Typography variant="body2" color="text.secondary">{t('taskViewModal.actionCannotBeUndone', 'This action cannot be undone.')}</Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ py: 2 }}>
          <Typography variant="body1" sx={{ color: COLORS.gray[600] }}>
            {t('taskViewModal.confirmDeleteComment', 'Are you sure you want to delete this comment forever?')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5 }}>
          <Button 
            onClick={() => {
              if (isDeletingComment) return;
              setIsDeleteCommentDialogOpen(false);
            }}
            sx={{ color: COLORS.gray[600], fontWeight: 600, textTransform: 'none', borderRadius: '8px', px: 2.5 }}
            disabled={isDeletingComment}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button 
            onClick={handleConfirmDeleteComment}
            variant="contained"
            disableElevation
            disabled={isDeletingComment}
            sx={{ bgcolor: COLORS.error, fontWeight: 600, textTransform: 'none', borderRadius: '8px', px: 3, '&:hover': { bgcolor: COLORS.priority.critical } }}
          >
            {isDeletingComment ? t('common.deleting', 'Deleting...') : t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Checklist Global Delete Dialog */}
      <Dialog 
        open={isDeleteChecklistDialogOpen && hasChecklistItems}
        onClose={() => setIsDeleteChecklistDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: '16px', p: 1, maxWidth: '400px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: COLORS.error, width: 44, height: 44 }}>
              <Delete />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="700">{t('taskViewModal.deleteChecklist', 'Delete Checklist')}</Typography>
              <Typography variant="body2" color="text.secondary">{t('taskViewModal.actionCannotBeUndone', 'This action will clear all sub-tasks in this section.')}</Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ py: 2 }}>
          <Typography variant="body1" sx={{ color: COLORS.gray[600] }}>
            {t('taskViewModal.confirmDeleteChecklist', 'Are you sure you want to delete the entire checklist? All items will be permanently removed.')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5 }}>
          <Button 
            onClick={() => setIsDeleteChecklistDialogOpen(false)}
            sx={{ color: COLORS.gray[600], fontWeight: 600, textTransform: 'none', borderRadius: '8px', px: 2.5 }}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button 
            onClick={handleConfirmDeleteChecklist}
            variant="contained"
            disableElevation
            sx={{ bgcolor: COLORS.error, fontWeight: 600, textTransform: 'none', borderRadius: '8px', px: 3, '&:hover': { bgcolor: COLORS.priority.critical } }}
          >
            {t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Project On Hold Modal */}
      <ProjectStatusModal
        open={isProjectStatusModalOpen}
        onClose={closeProjectStatusModal}
        onActivate={handleActivateProject}
        loading={activatingProject}
        projectStatus={projectStatusForModal}
        contentOverride={projectStatusModalContent}
      />
    </>
  );
};

export default TaskViewModal;
